export type Locale = "en" | "es" | "zh" | "ar" | "fr" | "pt" | "ja" | "de" | "th" | "hi" | "vi" | "id" | "tr" | "ru" | "ko" | "it" | "pl" | "nl" | "sv" | "fil";

export interface Translation {
  nav: {
    trackOrder: string;
    track: string;
  };
  hero: {
    title: string;
    titleHighlight: string;
    subtitle: string;
    placeholder: string;
    cta: string;
    securePayments: string;
    fastProcessing: string;
    noPassword: string;
    noUsersFound: string;
    tryDifferent: string;
  };
  valueProp: {
    title: string;
    titleHighlight: string;
    subtitle: string;
    audienceGrowth: string;
    audienceGrowthDesc: string;
    fastProcessing: string;
    fastProcessingDesc: string;
    privacyFirst: string;
    privacyFirstDesc: string;
    securePayments: string;
    securePaymentsDesc: string;
    orderTracking: string;
    orderTrackingDesc: string;
    dedicatedSupport: string;
    dedicatedSupportDesc: string;
  };
  howItWorks: {
    title: string;
    subtitle: string;
    step1Title: string;
    step1Desc: string;
    step2Title: string;
    step2Desc: string;
    step3Title: string;
    step3Desc: string;
    step4Title: string;
    step4Desc: string;
    stat247: string;
    statSSL: string;
    statNo: string;
    statDelivery: string;
    statSupportLabel: string;
    statEncryptedLabel: string;
    statPasswordLabel: string;
    statDeliveryLabel: string;
  };
  footer: {
    faqTitle: string;
    faq1Q: string;
    faq1A: string;
    faq2Q: string;
    faq2A: string;
    faq3Q: string;
    faq3A: string;
    faq4Q: string;
    faq4A: string;
    privacy: string;
    terms: string;
    refundPolicy: string;
    contact: string;
    copyright: string;
    visaMastercard: string;
    stripeSecure: string;
    crypto: string;
    sslEncrypted: string;
  };
  currency: {
    disclaimer: string;
  };
  createOrder: {
    followers: string;
    likes: string;
    views: string;
    shares: string;
    comments: string;
    selectPostsNextStep: string;
    total: string;
    discount: string;
    continue: string;
    contactUs: string;
    selected: string;
    posts: string;
  };
  order: {
    totalDueToday: string;
    totalDue: string;
    selectVideos: string;
    selectVideosDesc: string;
    noVideosFound: string;
    continueWithVideos: string;
    selectAtLeastOne: string;
    oneTimeOffer: string;
    addFollowers: string;
    applied: string;
    apply: string;
    paymentFailed: string;
    paymentDeclined: string;
    tryCard: string;
    tryStripe: string;
    tryCrypto: string;
    nameOnCard: string;
    cardNumber: string;
    expiry: string;
    cvv: string;
    processing: string;
    pay: string;
    stripeDesc: string;
    payViaStripe: string;
    redirectingStripe: string;
    stripePowered: string;
    cryptoDesc: string;
    payWithCrypto: string;
    secureCryptomus: string;
    agreeTerms: string;
    terms: string;
    refundPolicy: string;
    safeCheckout: string;
    sslEncrypted: string;
    guaranteed: string;
    backToVideos: string;
    videos: string;
    bonus: string;
    selected: string;
    // Optional checkout/payment-panel strings (fallbacks live in the components)
    orderSummary?: string;
    minimumOrder?: string;
    invalidCardNumber?: string;
    invalidExpiry?: string;
    invalidCvv?: string;
    cardPaymentFailed?: string;
    cardPaymentDeclined?: string;
    stripeError?: string;
    stripePaymentFailed?: string;
    paypalFailed?: string;
    cryptoPaymentFailed?: string;
    tryPayPal?: string;
    cardLabel?: string;
    stripeLabel?: string;
    paypalLabel?: string;
    cryptoLabel?: string;
    stripeDescShort?: string;
    paypalDesc?: string;
    payWithStripe?: string;
    payWithPayPal?: string;
  };
  thankYou: {
    awaitingPayment: string;
    awaitingPaymentDesc: string;
    thankYou: string;
    orderConfirmed: string;
    referenceNumber: string;
    copy: string;
    copied: string;
    payment: string;
    delivery: string;
    services: string;
    total: string;
    whatsNext: string;
    step1: string;
    step2: string;
    step3: string;
    bookmark: string;
    placeAnother: string;
    pending: string;
    paid: string;
    completed: string;
    processingStatus: string;
    delivered: string;
    failed: string;
  };
  dashboard: {
    trackYourOrder: string;
    trackDesc: string;
    searchPlaceholder: string;
    noOrderFound: string;
    yourOrderHistory: string;
    refresh: string;
    noHistory: string;
    placeAnother: string;
    bookmark: string;
    whatsNext: string;
    step1: string;
    step2: string;
    step3: string;
  };
  // Per-platform hero copy (optional — falls back to English defaults)
  heroByPlatform?: Partial<Record<
    "tiktok" | "youtube" | "spotify" | "instagram" | "twitch" | "twitter" | "facebook",
    { title: string; highlight: string; subtitle: string }
  >>;
}

const en: Translation = {
  nav: { trackOrder: "Track Order", track: "Track" },
  hero: {
    title: "Grow Your TikTok",
    titleHighlight: "Audience",
    subtitle: "Professional social media promotion services to help your content reach more people.",
    placeholder: "Enter your TikTok username",
    cta: "Get Started",
    securePayments: "Secure Payments",
    fastProcessing: "Fast Processing",
    noPassword: "No Password Required",
    noUsersFound: "No users found",
    tryDifferent: "Try a different username",
  },
  valueProp: {
    title: "Why Choose",
    titleHighlight: "TokDadddy",
    subtitle: "A trusted platform for TikTok content promotion and audience development.",
    audienceGrowth: "Audience Growth",
    audienceGrowthDesc: "Professional promotion services designed to help your content reach more viewers.",
    fastProcessing: "Fast Processing",
    fastProcessingDesc: "Orders are processed promptly. Results typically visible within 24–72 hours.",
    privacyFirst: "Privacy First",
    privacyFirstDesc: "We never request your login credentials. Your account security is our priority.",
    securePayments: "Secure Payments",
    securePaymentsDesc: "Multiple payment options available. All transactions are encrypted and PCI-compliant.",
    orderTracking: "Order Tracking",
    orderTrackingDesc: "Monitor your order status in real time from placement to completion.",
    dedicatedSupport: "Dedicated Support",
    dedicatedSupportDesc: "Our support team is available to assist you with any questions.",
  },
  howItWorks: {
    title: "How It Works",
    subtitle: "Get started in under 2 minutes. No password needed — ever.",
    step1Title: "Find Your Profile",
    step1Desc: "Enter your TikTok username and we'll locate your profile instantly.",
    step2Title: "Select Services",
    step2Desc: "Choose from our range of promotion packages and customise your order.",
    step3Title: "Secure Checkout",
    step3Desc: "Complete your purchase with our encrypted payment system.",
    step4Title: "Track Your Order",
    step4Desc: "Monitor your order progress in real time from your confirmation page.",
    stat247: "24/7",
    statSSL: "SSL",
    statNo: "No",
    statDelivery: "24–72h",
    statSupportLabel: "Customer Support",
    statEncryptedLabel: "Encrypted Payments",
    statPasswordLabel: "Password Required",
    statDeliveryLabel: "Delivery Window",
  },
  footer: {
    faqTitle: "Frequently Asked Questions",
    faq1Q: "Is my account safe?",
    faq1A: "Absolutely. We never request your password or login credentials. Only your public username is needed to process orders.",
    faq2Q: "How long does processing take?",
    faq2A: "Orders begin processing promptly. Results are typically visible within 24–72 hours depending on the service selected.",
    faq3Q: "What payment methods do you accept?",
    faq3A: "We accept major credit/debit cards, PayPal, and cryptocurrency. All transactions are encrypted and PCI-compliant.",
    faq4Q: "What is your refund policy?",
    faq4A: "We offer refunds for undelivered orders. Please visit our Refund Policy page or contact hello@tokdadddy.com for details.",
    privacy: "Privacy",
    terms: "Terms",
    refundPolicy: "Refund Policy",
    contact: "Contact",
    copyright: "tokdadddy.com. All rights reserved. We are not affiliated with TikTok or ByteDance.",
    visaMastercard: "Visa / Mastercard",
    stripeSecure: "Stripe Secure",
    crypto: "Crypto",
    sslEncrypted: "SSL Encrypted",
  },
  currency: { disclaimer: "Charged in USD. Local price is for reference only." },
  createOrder: {
    followers: "Followers",
    likes: "Likes",
    views: "Views",
    shares: "Shares",
    comments: "Comments",
    selectPostsNextStep: "(select posts in next step)",
    total: "Total",
    discount: "Discount",
    continue: "Continue",
    contactUs: "If you have any questions for our team, please send us a message any time at",
    selected: "Selected",
    posts: "Posts",
  },
  order: {
    totalDueToday: "Total Due Today",
    totalDue: "Total Due",
    selectVideos: "Select videos to promote",
    selectVideosDesc: "Choose which videos you'd like to boost",
    noVideosFound: "No videos found for this user.",
    continueWithVideos: "Continue with {count} video{s}",
    selectAtLeastOne: "Select at least one video",
    oneTimeOffer: "One Time Offer 🔥",
    addFollowers: "Add 5,000 TikTok Followers for only",
    applied: "Applied",
    apply: "Apply",
    paymentFailed: "Payment failed",
    paymentDeclined: "Your payment was declined. Please try one of the other payment methods below.",
    tryCard: "💳 Try Card",
    tryStripe: "💳 Try Stripe",
    tryCrypto: "₿ Try Crypto",
    nameOnCard: "Name on Card",
    cardNumber: "Card Number",
    expiry: "Expiry",
    cvv: "CVV",
    processing: "Processing...",
    pay: "Pay",
    stripeDesc: "Pay securely via Stripe Checkout. Supports cards, Apple Pay, Google Pay, and more.",
    payViaStripe: "Pay via Stripe",
    redirectingStripe: "Redirecting to Stripe...",
    stripePowered: "🔒 Powered by Stripe — PCI Level 1 Certified",
    cryptoDesc: "Pay with Bitcoin, USDT, ETH, and 20+ other cryptocurrencies via Cryptomus.",
    payWithCrypto: "Pay with Crypto",
    secureCryptomus: "🔒 Secure payment via Cryptomus",
    agreeTerms: "You agree to our",
    terms: "Terms",
    refundPolicy: "Refund Policy",
    safeCheckout: "Safe Checkout",
    sslEncrypted: "SSL Encrypted",
    guaranteed: "Tracked Delivery",
    backToVideos: "← Back to video selection",
    videos: "videos",
    bonus: "Bonus",
    selected: "selected",
    orderSummary: "Order Summary",
    minimumOrder: "Minimum order is $0.50",
    invalidCardNumber: "Please enter a valid card number",
    invalidExpiry: "Invalid expiry (MM/YY)",
    invalidCvv: "Invalid CVV",
    cardPaymentFailed: "Card payment failed.",
    cardPaymentDeclined: "Card payment was declined.",
    stripeError: "Stripe error",
    stripePaymentFailed: "Stripe payment failed.",
    paypalFailed: "PayPal failed.",
    cryptoPaymentFailed: "Crypto payment failed.",
    tryPayPal: "🅿️ Try PayPal",
    cardLabel: "Card",
    stripeLabel: "Stripe",
    paypalLabel: "PayPal",
    cryptoLabel: "Crypto",
    stripeDescShort: "Pay securely via Stripe — supports all major credit & debit cards.",
    paypalDesc: "Pay securely with your PayPal account — fast and easy checkout.",
    payWithStripe: "Pay with Stripe",
    payWithPayPal: "Pay with PayPal",
  },
  thankYou: {
    awaitingPayment: "Awaiting Payment",
    awaitingPaymentDesc: "Your order has been created. We're waiting for payment confirmation.",
    thankYou: "Thank You!",
    orderConfirmed: "Your order has been confirmed and is being processed.",
    referenceNumber: "Reference Number",
    copy: "Copy",
    copied: "Copied!",
    payment: "Payment",
    delivery: "Delivery",
    services: "Services",
    total: "Total",
    whatsNext: "What happens next?",
    step1: "Your order is being reviewed and processed",
    step2: "Results will start appearing on your account gradually",
    step3: "Full delivery completes within 24-72 hours",
    bookmark: "Bookmark this page to check your order status anytime — no login required.",
    placeAnother: "Place Another Order",
    pending: "Pending",
    paid: "Paid",
    completed: "Completed",
    processingStatus: "Processing",
    delivered: "Delivered",
    failed: "Failed",
  },
  dashboard: {
    trackYourOrder: "Track Your Order",
    trackDesc: "Enter your reference number or view your recent orders below.",
    searchPlaceholder: "e.g. TD-ABC12345",
    noOrderFound: "No order found for that reference number.",
    yourOrderHistory: "Your Order History",
    refresh: "Refresh",
    noHistory: "No order history yet. Place an order to see it here!",
    placeAnother: "Place Another Order",
    bookmark: "Bookmark this page to check your order status anytime — no login required.",
    whatsNext: "What happens next?",
    step1: "Your order is being reviewed and processed",
    step2: "Results will start appearing on your account gradually",
    step3: "Full delivery completes within 24-72 hours",
  },
  heroByPlatform: {
    tiktok: { title: "Promote your", highlight: "TikTok content", subtitle: "Grow your reach on TikTok with marketing services that help your videos get discovered." },
    youtube: { title: "Grow your", highlight: "YouTube channel", subtitle: "Promote your videos and channel with marketing services designed to expand your audience." },
    spotify: { title: "Promote your", highlight: "Spotify music", subtitle: "Get your tracks in front of new listeners with promotion services for artists and labels." },
    instagram: { title: "Boost your", highlight: "Instagram presence", subtitle: "Promote your profile, posts and reels with marketing services to expand your audience." },
    twitch: { title: "Grow your", highlight: "Twitch stream", subtitle: "Promote your channel and streams with marketing services that help you reach new viewers." },
    twitter: { title: "Promote your", highlight: "X profile", subtitle: "Expand your reach on X with promotion services for tweets, profiles and content campaigns." },
    facebook: { title: "Promote your", highlight: "Facebook page", subtitle: "Grow your Facebook page, posts and videos with marketing services to reach a wider audience." },
  },
};

const es: Translation = {
  ...en,
  nav: { trackOrder: "Rastrear Pedido", track: "Rastrear" },
  hero: {
    title: "Haz Crecer Tu",
    titleHighlight: "Audiencia de TikTok",
    subtitle: "Servicios profesionales de promoción en redes sociales para llegar a más personas.",
    placeholder: "Ingresa tu usuario de TikTok",
    cta: "Comenzar",
    securePayments: "Pagos Seguros",
    fastProcessing: "Procesamiento Rápido",
    noPassword: "Sin Contraseña",
    noUsersFound: "No se encontraron usuarios",
    tryDifferent: "Prueba con otro nombre",
  },
  valueProp: {
    title: "¿Por Qué Elegir",
    titleHighlight: "TokDadddy",
    subtitle: "Una plataforma confiable para la promoción de contenido en TikTok.",
    audienceGrowth: "Crecimiento de Audiencia",
    audienceGrowthDesc: "Servicios profesionales diseñados para que tu contenido llegue a más espectadores.",
    fastProcessing: "Procesamiento Rápido",
    fastProcessingDesc: "Los pedidos se procesan de inmediato. Resultados visibles en 24–72 horas.",
    privacyFirst: "Privacidad Primero",
    privacyFirstDesc: "Nunca solicitamos tus credenciales. Tu seguridad es nuestra prioridad.",
    securePayments: "Pagos Seguros",
    securePaymentsDesc: "Múltiples opciones de pago. Transacciones encriptadas y compatibles con PCI.",
    orderTracking: "Seguimiento de Pedido",
    orderTrackingDesc: "Monitorea el estado de tu pedido en tiempo real.",
    dedicatedSupport: "Soporte Dedicado",
    dedicatedSupportDesc: "Nuestro equipo está disponible para ayudarte con cualquier pregunta.",
  },
  howItWorks: {
    title: "Cómo Funciona",
    subtitle: "Comienza en menos de 2 minutos. Sin contraseña — nunca.",
    step1Title: "Encuentra Tu Perfil",
    step1Desc: "Ingresa tu usuario de TikTok y lo encontraremos al instante.",
    step2Title: "Selecciona Servicios",
    step2Desc: "Elige entre nuestra variedad de paquetes de promoción.",
    step3Title: "Pago Seguro",
    step3Desc: "Completa tu compra con nuestro sistema de pago encriptado.",
    step4Title: "Rastrea Tu Pedido",
    step4Desc: "Monitorea el progreso de tu pedido en tiempo real.",
    stat247: "24/7", statSSL: "SSL", statNo: "No", statDelivery: "24–72h",
    statSupportLabel: "Soporte al Cliente",
    statEncryptedLabel: "Pagos Encriptados",
    statPasswordLabel: "Contraseña Requerida",
    statDeliveryLabel: "Tiempo de Entrega",
  },
  footer: {
    faqTitle: "Preguntas Frecuentes",
    faq1Q: "¿Mi cuenta está segura?",
    faq1A: "Absolutamente. Nunca solicitamos tu contraseña. Solo necesitamos tu nombre de usuario público.",
    faq2Q: "¿Cuánto tarda el procesamiento?",
    faq2A: "Los pedidos comienzan a procesarse de inmediato. Los resultados son visibles en 24–72 horas.",
    faq3Q: "¿Qué métodos de pago aceptan?",
    faq3A: "Aceptamos tarjetas de crédito/débito, PayPal y criptomonedas. Todas las transacciones son encriptadas.",
    faq4Q: "¿Cuál es su política de reembolso?",
    faq4A: "Ofrecemos reembolsos para pedidos no entregados. Contacta hello@tokdadddy.com para más detalles.",
    privacy: "Privacidad", terms: "Términos", refundPolicy: "Política de Reembolso", contact: "Contacto",
    copyright: "tokdadddy.com. Todos los derechos reservados. No estamos afiliados con TikTok o ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe Secure", crypto: "Crypto", sslEncrypted: "SSL Encriptado",
  },
  currency: { disclaimer: "Cobro en USD. El precio local es solo referencia." },
  createOrder: {
    followers: "Seguidores",
    likes: "Me gusta",
    views: "Vistas",
    shares: "Compartidos",
    comments: "Comentarios",
    selectPostsNextStep: "(seleccionar publicaciones en el siguiente paso)",
    total: "Total",
    discount: "Descuento",
    continue: "Continuar",
    contactUs: "Si tienes alguna pregunta, envíanos un mensaje en cualquier momento a",
    selected: "Seleccionado",
    posts: "Publicaciones",
  },
  order: {
    totalDueToday: "Total a Pagar Hoy",
    totalDue: "Total a Pagar",
    selectVideos: "Selecciona videos para promocionar",
    selectVideosDesc: "Elige qué videos quieres impulsar",
    noVideosFound: "No se encontraron videos para este usuario.",
    continueWithVideos: "Continuar con {count} video{s}",
    selectAtLeastOne: "Selecciona al menos un video",
    oneTimeOffer: "Oferta Única 🔥",
    addFollowers: "Agrega 5,000 Seguidores de TikTok por solo",
    applied: "Aplicado",
    apply: "Aplicar",
    paymentFailed: "Pago fallido",
    paymentDeclined: "Tu pago fue rechazado. Prueba otro método de pago abajo.",
    tryCard: "💳 Probar Tarjeta",
    tryStripe: "💳 Probar Stripe",
    tryCrypto: "₿ Probar Crypto",
    nameOnCard: "Nombre en la Tarjeta",
    cardNumber: "Número de Tarjeta",
    expiry: "Vencimiento",
    cvv: "CVV",
    processing: "Procesando...",
    pay: "Pagar",
    stripeDesc: "Paga de forma segura via Stripe Checkout. Admite tarjetas, Apple Pay, Google Pay y más.",
    payViaStripe: "Pagar via Stripe",
    redirectingStripe: "Redirigiendo a Stripe...",
    stripePowered: "🔒 Powered by Stripe — PCI Level 1 Certified",
    cryptoDesc: "Paga con Bitcoin, USDT, ETH y más de 20 criptomonedas via Cryptomus.",
    payWithCrypto: "Pagar con Crypto",
    secureCryptomus: "🔒 Pago seguro vía Cryptomus",
    agreeTerms: "Aceptas nuestros",
    terms: "Términos",
    refundPolicy: "Política de Reembolso",
    safeCheckout: "Pago Seguro",
    sslEncrypted: "SSL Encriptado",
    guaranteed: "Entrega Rastreada",
    backToVideos: "← Volver a selección de videos",
    videos: "videos",
    bonus: "Bono",
    selected: "seleccionados",
    orderSummary: "Resumen del pedido",
    minimumOrder: "El pedido mínimo es $0.50",
    invalidCardNumber: "Ingresa un número de tarjeta válido",
    invalidExpiry: "Fecha de expiración inválida (MM/AA)",
    invalidCvv: "CVV inválido",
    cardPaymentFailed: "Pago con tarjeta fallido.",
    cardPaymentDeclined: "Pago con tarjeta rechazado.",
    stripeError: "Error de Stripe",
    stripePaymentFailed: "Pago con Stripe fallido.",
    paypalFailed: "PayPal falló.",
    cryptoPaymentFailed: "Pago con cripto fallido.",
    tryPayPal: "🅿️ Probar PayPal",
    cardLabel: "Tarjeta", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Cripto",
    stripeDescShort: "Paga de forma segura con Stripe — admite todas las principales tarjetas de crédito y débito.",
    paypalDesc: "Paga de forma segura con tu cuenta PayPal — checkout rápido y fácil.",
    payWithStripe: "Pagar con Stripe",
    payWithPayPal: "Pagar con PayPal",
  },
  thankYou: {
    awaitingPayment: "Esperando Pago",
    awaitingPaymentDesc: "Tu pedido ha sido creado. Estamos esperando confirmación de pago.",
    thankYou: "¡Gracias!",
    orderConfirmed: "Tu pedido ha sido confirmado y está siendo procesado.",
    referenceNumber: "Número de Referencia",
    copy: "Copiar",
    copied: "¡Copiado!",
    payment: "Pago",
    delivery: "Entrega",
    services: "Servicios",
    total: "Total",
    whatsNext: "¿Qué sigue?",
    step1: "Tu pedido está siendo revisado y procesado",
    step2: "Los resultados comenzarán a aparecer gradualmente",
    step3: "La entrega completa se realiza en 24-72 horas",
    bookmark: "Guarda esta página para verificar el estado de tu pedido — no necesitas iniciar sesión.",
    placeAnother: "Realizar Otro Pedido",
    pending: "Pendiente",
    paid: "Pagado",
    completed: "Completado",
    processingStatus: "Procesando",
    delivered: "Entregado",
    failed: "Fallido",
  },
  dashboard: {
    trackYourOrder: "Rastrea Tu Pedido",
    trackDesc: "Ingresa tu número de referencia o mira tus pedidos recientes.",
    searchPlaceholder: "ej. TD-ABC12345",
    noOrderFound: "No se encontró un pedido con ese número de referencia.",
    yourOrderHistory: "Historial de Pedidos",
    refresh: "Actualizar",
    noHistory: "No hay historial de pedidos. ¡Haz un pedido para verlo aquí!",
    placeAnother: "Realizar Otro Pedido",
    bookmark: "Guarda esta página para verificar el estado de tu pedido — no necesitas iniciar sesión.",
    whatsNext: "¿Qué sigue?",
    step1: "Tu pedido está siendo revisado y procesado",
    step2: "Los resultados comenzarán a aparecer gradualmente",
    step3: "La entrega completa se realiza en 24-72 horas",
  },
  heroByPlatform: {
    tiktok: { title: "Promociona tu", highlight: "contenido de TikTok", subtitle: "Aumenta tu alcance en TikTok con servicios de marketing que ayudan a que descubran tus videos." },
    youtube: { title: "Haz crecer tu", highlight: "canal de YouTube", subtitle: "Promociona tus videos y canal con servicios de marketing diseñados para ampliar tu audiencia." },
    spotify: { title: "Promociona tu", highlight: "música en Spotify", subtitle: "Lleva tus canciones a nuevos oyentes con servicios de promoción para artistas y sellos." },
    instagram: { title: "Impulsa tu", highlight: "presencia en Instagram", subtitle: "Promociona tu perfil, publicaciones y reels con servicios de marketing para ampliar tu audiencia." },
    twitch: { title: "Haz crecer tu", highlight: "transmisión de Twitch", subtitle: "Promociona tu canal y transmisiones con servicios de marketing para llegar a nuevos espectadores." },
    twitter: { title: "Promociona tu", highlight: "perfil de X", subtitle: "Amplía tu alcance en X con servicios de promoción para tweets, perfiles y campañas." },
    facebook: { title: "Promociona tu", highlight: "página de Facebook", subtitle: "Haz crecer tu página, publicaciones y videos de Facebook con servicios de marketing." },
  },
};

const zh: Translation = {
  ...en,
  nav: { trackOrder: "追踪订单", track: "追踪" },
  hero: {
    title: "增长你的TikTok",
    titleHighlight: "粉丝群",
    subtitle: "专业的社交媒体推广服务，帮助你的内容触达更多人。",
    placeholder: "输入你的TikTok用户名",
    cta: "开始使用",
    securePayments: "安全支付",
    fastProcessing: "快速处理",
    noPassword: "无需密码",
    noUsersFound: "未找到用户",
    tryDifferent: "尝试其他用户名",
  },
  valueProp: {
    title: "为什么选择",
    titleHighlight: "TokDadddy",
    subtitle: "值得信赖的TikTok内容推广和粉丝增长平台。",
    audienceGrowth: "粉丝增长",
    audienceGrowthDesc: "专业推广服务，帮助你的内容触达更多观众。",
    fastProcessing: "快速处理",
    fastProcessingDesc: "订单立即处理。结果通常在24-72小时内可见。",
    privacyFirst: "隐私优先",
    privacyFirstDesc: "我们绝不要求你的登录凭据。你的账户安全是我们的首要任务。",
    securePayments: "安全支付",
    securePaymentsDesc: "多种支付方式。所有交易均经过加密和PCI合规处理。",
    orderTracking: "订单追踪",
    orderTrackingDesc: "实时监控你的订单状态。",
    dedicatedSupport: "专属客服",
    dedicatedSupportDesc: "我们的客服团队随时为你解答任何问题。",
  },
  howItWorks: {
    title: "使用流程",
    subtitle: "2分钟内即可开始。永远不需要密码。",
    step1Title: "找到你的账号",
    step1Desc: "输入你的TikTok用户名，我们会立即找到你的资料。",
    step2Title: "选择服务",
    step2Desc: "从我们的推广套餐中选择，定制你的订单。",
    step3Title: "安全结账",
    step3Desc: "使用我们的加密支付系统完成购买。",
    step4Title: "追踪订单",
    step4Desc: "从确认页面实时监控你的订单进度。",
    stat247: "24/7", statSSL: "SSL", statNo: "无需", statDelivery: "24–72小时",
    statSupportLabel: "客户支持",
    statEncryptedLabel: "加密支付",
    statPasswordLabel: "密码要求",
    statDeliveryLabel: "交付时间",
  },
  footer: {
    faqTitle: "常见问题",
    faq1Q: "我的账户安全吗？",
    faq1A: "绝对安全。我们绝不要求你的密码或登录凭据。只需要你的公开用户名来处理订单。",
    faq2Q: "处理需要多长时间？",
    faq2A: "订单立即开始处理。结果通常在24-72小时内可见。",
    faq3Q: "你们接受哪些支付方式？",
    faq3A: "我们接受主要信用卡/借记卡、PayPal和加密货币。所有交易均经过加密。",
    faq4Q: "退款政策是什么？",
    faq4A: "对于未交付的订单，我们提供退款。详情请联系hello@tokdadddy.com。",
    privacy: "隐私", terms: "条款", refundPolicy: "退款政策", contact: "联系我们",
    copyright: "tokdadddy.com。保留所有权利。我们与TikTok或字节跳动无关。",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe 安全", crypto: "加密货币", sslEncrypted: "SSL 加密",
  },
  currency: { disclaimer: "以美元结算。本地价格仅供参考。" },
  createOrder: {
    followers: "粉丝",
    likes: "点赞",
    views: "浏览量",
    shares: "分享",
    comments: "评论",
    selectPostsNextStep: "（在下一步选择帖子）",
    total: "总计",
    discount: "折扣",
    continue: "继续",
    contactUs: "如有任何问题，请随时发送邮件至",
    selected: "已选择",
    posts: "帖子",
  },
  order: {
    totalDueToday: "今日应付总额",
    totalDue: "应付总额",
    selectVideos: "选择要推广的视频",
    selectVideosDesc: "选择你想要提升的视频",
    noVideosFound: "未找到该用户的视频。",
    continueWithVideos: "继续选择 {count} 个视频",
    selectAtLeastOne: "请至少选择一个视频",
    oneTimeOffer: "限时优惠 🔥",
    addFollowers: "仅需以下价格即可添加5,000个TikTok粉丝",
    applied: "已应用",
    apply: "应用",
    paymentFailed: "支付失败",
    paymentDeclined: "你的支付被拒绝。请尝试下方其他支付方式。",
    tryCard: "💳 尝试银行卡",
    tryStripe: "💳 尝试 Stripe",
    tryCrypto: "₿ 尝试加密货币",
    nameOnCard: "持卡人姓名",
    cardNumber: "卡号",
    expiry: "有效期",
    cvv: "CVV",
    processing: "处理中...",
    pay: "支付",
    stripeDesc: "通过 Stripe Checkout 安全支付。支持银行卡、Apple Pay、Google Pay 等。",
    payViaStripe: "通过 Stripe 支付",
    redirectingStripe: "正在跳转到 Stripe...",
    stripePowered: "🔒 由 Stripe 提供支持 — PCI 一级认证",
    cryptoDesc: "通过 Cryptomus 使用比特币、USDT、ETH 等20多种加密货币支付。",
    payWithCrypto: "使用加密货币支付",
    secureCryptomus: "🔒 通过 Cryptomus 安全支付",
    agreeTerms: "你同意我们的",
    terms: "条款",
    refundPolicy: "退款政策",
    safeCheckout: "安全结账",
    sslEncrypted: "SSL 加密",
    guaranteed: "可追踪交付",
    backToVideos: "← 返回视频选择",
    videos: "个视频",
    bonus: "奖励",
    selected: "已选",
    orderSummary: "订单摘要",
    minimumOrder: "最低订单 $0.50",
    invalidCardNumber: "请输入有效的卡号",
    invalidExpiry: "有效期无效 (MM/YY)",
    invalidCvv: "CVV 无效",
    cardPaymentFailed: "信用卡付款失败。",
    cardPaymentDeclined: "信用卡付款被拒绝。",
    stripeError: "Stripe 错误",
    stripePaymentFailed: "Stripe 付款失败。",
    paypalFailed: "PayPal 失败。",
    cryptoPaymentFailed: "加密货币付款失败。",
    tryPayPal: "🅿️ 尝试 PayPal",
    cardLabel: "卡", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "加密货币",
    stripeDescShort: "通过 Stripe 安全付款 — 支持所有主要信用卡和借记卡。",
    paypalDesc: "使用您的 PayPal 账户安全付款 — 快速便捷。",
    payWithStripe: "使用 Stripe 付款",
    payWithPayPal: "使用 PayPal 付款",
  },
  thankYou: {
    awaitingPayment: "等待付款",
    awaitingPaymentDesc: "你的订单已创建。我们正在等待付款确认。",
    thankYou: "谢谢！",
    orderConfirmed: "你的订单已确认，正在处理中。",
    referenceNumber: "参考编号",
    copy: "复制",
    copied: "已复制！",
    payment: "付款",
    delivery: "交付",
    services: "服务",
    total: "总计",
    whatsNext: "接下来会发生什么？",
    step1: "你的订单正在审核和处理中",
    step2: "结果将逐渐出现在你的账户上",
    step3: "完整交付在24-72小时内完成",
    bookmark: "收藏此页面随时查看订单状态 — 无需登录。",
    placeAnother: "再下一单",
    pending: "待处理",
    paid: "已付款",
    completed: "已完成",
    processingStatus: "处理中",
    delivered: "已交付",
    failed: "失败",
  },
  dashboard: {
    trackYourOrder: "追踪你的订单",
    trackDesc: "输入参考编号或查看下方的最近订单。",
    searchPlaceholder: "例如 TD-ABC12345",
    noOrderFound: "未找到该参考编号的订单。",
    yourOrderHistory: "你的订单历史",
    refresh: "刷新",
    noHistory: "暂无订单历史。下单后即可在此查看！",
    placeAnother: "再下一单",
    bookmark: "收藏此页面随时查看订单状态 — 无需登录。",
    whatsNext: "接下来会发生什么？",
    step1: "你的订单正在审核和处理中",
    step2: "结果将逐渐出现在你的账户上",
    step3: "完整交付在24-72小时内完成",
  },
  heroByPlatform: {
    tiktok: { title: "推广你的", highlight: "TikTok 内容", subtitle: "通过营销服务提升你在 TikTok 的影响力,让更多人发现你的视频。" },
    youtube: { title: "发展你的", highlight: "YouTube 频道", subtitle: "通过专业营销服务推广你的视频和频道,扩大受众群体。" },
    spotify: { title: "推广你的", highlight: "Spotify 音乐", subtitle: "通过艺人和厂牌的推广服务,让你的曲目触达新的听众。" },
    instagram: { title: "提升你的", highlight: "Instagram 形象", subtitle: "通过营销服务推广你的个人主页、帖子和 Reels,扩大受众。" },
    twitch: { title: "发展你的", highlight: "Twitch 直播", subtitle: "通过营销服务推广你的频道和直播,触达新的观众。" },
    twitter: { title: "推广你的", highlight: "X 主页", subtitle: "通过推文、主页和内容活动的推广服务,扩大你在 X 的影响力。" },
    facebook: { title: "推广你的", highlight: "Facebook 页面", subtitle: "通过营销服务推广你的 Facebook 页面、帖子和视频,触达更多受众。" },
  },
};

const ar: Translation = {
  ...en,
  nav: { trackOrder: "تتبع الطلب", track: "تتبع" },
  hero: {
    title: "نمّي جمهورك على",
    titleHighlight: "تيك توك",
    subtitle: "خدمات ترويج احترافية لمساعدة محتواك في الوصول لأشخاص أكثر.",
    placeholder: "أدخل اسم مستخدم TikTok",
    cta: "ابدأ الآن",
    securePayments: "دفع آمن",
    fastProcessing: "معالجة سريعة",
    noPassword: "لا حاجة لكلمة مرور",
    noUsersFound: "لم يتم العثور على مستخدمين",
    tryDifferent: "جرّب اسم مستخدم آخر",
  },
  valueProp: {
    title: "لماذا تختار",
    titleHighlight: "TokDadddy",
    subtitle: "منصة موثوقة لترويج محتوى TikTok وتطوير الجمهور.",
    audienceGrowth: "نمو الجمهور",
    audienceGrowthDesc: "خدمات ترويج احترافية مصممة لمساعدة محتواك في الوصول لمزيد من المشاهدين.",
    fastProcessing: "معالجة سريعة",
    fastProcessingDesc: "تتم معالجة الطلبات فوراً. النتائج مرئية خلال 24-72 ساعة.",
    privacyFirst: "الخصوصية أولاً",
    privacyFirstDesc: "لا نطلب أبداً بيانات تسجيل الدخول الخاصة بك.",
    securePayments: "مدفوعات آمنة",
    securePaymentsDesc: "خيارات دفع متعددة. جميع المعاملات مشفرة ومتوافقة مع PCI.",
    orderTracking: "تتبع الطلب",
    orderTrackingDesc: "راقب حالة طلبك في الوقت الفعلي.",
    dedicatedSupport: "دعم مخصص",
    dedicatedSupportDesc: "فريق الدعم لدينا متاح لمساعدتك في أي أسئلة.",
  },
  howItWorks: {
    title: "كيف يعمل",
    subtitle: "ابدأ في أقل من دقيقتين. لا حاجة لكلمة مرور — أبداً.",
    step1Title: "ابحث عن ملفك",
    step1Desc: "أدخل اسم مستخدم TikTok وسنجد ملفك الشخصي فوراً.",
    step2Title: "اختر الخدمات",
    step2Desc: "اختر من مجموعة باقات الترويج وخصّص طلبك.",
    step3Title: "دفع آمن",
    step3Desc: "أكمل عملية الشراء بنظام الدفع المشفر.",
    step4Title: "تتبع طلبك",
    step4Desc: "راقب تقدم طلبك في الوقت الفعلي.",
    stat247: "24/7", statSSL: "SSL", statNo: "لا", statDelivery: "24–72 ساعة",
    statSupportLabel: "دعم العملاء",
    statEncryptedLabel: "مدفوعات مشفرة",
    statPasswordLabel: "كلمة مرور مطلوبة",
    statDeliveryLabel: "نافذة التسليم",
  },
  footer: {
    faqTitle: "الأسئلة الشائعة",
    faq1Q: "هل حسابي آمن؟",
    faq1A: "بالتأكيد. لا نطلب أبداً كلمة المرور الخاصة بك. نحتاج فقط اسم المستخدم العام.",
    faq2Q: "كم تستغرق المعالجة؟",
    faq2A: "تبدأ معالجة الطلبات فوراً. النتائج مرئية خلال 24-72 ساعة.",
    faq3Q: "ما طرق الدفع المقبولة؟",
    faq3A: "نقبل بطاقات الائتمان/الخصم الرئيسية وPayPal والعملات المشفرة.",
    faq4Q: "ما هي سياسة الاسترداد؟",
    faq4A: "نقدم استرداداً للطلبات غير المسلّمة. تواصل معنا على hello@tokdadddy.com.",
    privacy: "الخصوصية", terms: "الشروط", refundPolicy: "سياسة الاسترداد", contact: "اتصل بنا",
    copyright: "tokdadddy.com. جميع الحقوق محفوظة. لسنا تابعين لـ TikTok أو ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe آمن", crypto: "عملات رقمية", sslEncrypted: "تشفير SSL",
  },
  currency: { disclaimer: "الدفع بالدولار الأمريكي. السعر المحلي للإشارة فقط." },
  createOrder: {
    followers: "متابعين",
    likes: "إعجابات",
    views: "مشاهدات",
    shares: "مشاركات",
    comments: "تعليقات",
    selectPostsNextStep: "(اختر المنشورات في الخطوة التالية)",
    total: "الإجمالي",
    discount: "خصم",
    continue: "متابعة",
    contactUs: "إذا كان لديك أي أسئلة، أرسل لنا رسالة في أي وقت على",
    selected: "محدد",
    posts: "منشورات",
  },
  order: {
    totalDueToday: "إجمالي المستحق اليوم",
    totalDue: "إجمالي المستحق",
    selectVideos: "اختر الفيديوهات للترويج",
    selectVideosDesc: "اختر الفيديوهات التي تريد تعزيزها",
    noVideosFound: "لم يتم العثور على فيديوهات لهذا المستخدم.",
    continueWithVideos: "متابعة مع {count} فيديو",
    selectAtLeastOne: "اختر فيديو واحد على الأقل",
    oneTimeOffer: "عرض لمرة واحدة 🔥",
    addFollowers: "أضف 5,000 متابع TikTok بسعر",
    applied: "تم التطبيق",
    apply: "تطبيق",
    paymentFailed: "فشل الدفع",
    paymentDeclined: "تم رفض الدفع. جرّب طريقة دفع أخرى أدناه.",
    tryCard: "💳 جرّب البطاقة",
    tryStripe: "💳 جرّب Stripe",
    tryCrypto: "₿ جرّب العملات الرقمية",
    nameOnCard: "الاسم على البطاقة",
    cardNumber: "رقم البطاقة",
    expiry: "تاريخ الانتهاء",
    cvv: "CVV",
    processing: "جارٍ المعالجة...",
    pay: "ادفع",
    stripeDesc: "ادفع بأمان عبر Stripe Checkout. يدعم البطاقات و Apple Pay و Google Pay والمزيد.",
    payViaStripe: "ادفع عبر Stripe",
    redirectingStripe: "جارٍ التحويل إلى Stripe...",
    stripePowered: "🔒 مدعوم من Stripe — PCI Level 1",
    cryptoDesc: "ادفع بالبيتكوين و USDT و ETH وأكثر من 20 عملة رقمية عبر Cryptomus.",
    payWithCrypto: "ادفع بالعملات الرقمية",
    secureCryptomus: "🔒 دفع آمن عبر Cryptomus",
    agreeTerms: "أنت توافق على",
    terms: "الشروط",
    refundPolicy: "سياسة الاسترداد",
    safeCheckout: "دفع آمن",
    sslEncrypted: "تشفير SSL",
    guaranteed: "تسليم متتبع",
    backToVideos: "← العودة لاختيار الفيديو",
    videos: "فيديوهات",
    bonus: "مكافأة",
    selected: "محدد",
    orderSummary: "ملخص الطلب",
    minimumOrder: "الحد الأدنى للطلب $0.50",
    invalidCardNumber: "يرجى إدخال رقم بطاقة صحيح",
    invalidExpiry: "تاريخ انتهاء غير صالح (MM/YY)",
    invalidCvv: "CVV غير صالح",
    cardPaymentFailed: "فشل الدفع بالبطاقة.",
    cardPaymentDeclined: "تم رفض الدفع بالبطاقة.",
    stripeError: "خطأ Stripe",
    stripePaymentFailed: "فشل الدفع عبر Stripe.",
    paypalFailed: "فشل PayPal.",
    cryptoPaymentFailed: "فشل دفع العملات المشفرة.",
    tryPayPal: "🅿️ جرّب PayPal",
    cardLabel: "بطاقة", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "عملات مشفرة",
    stripeDescShort: "ادفع بأمان عبر Stripe — يدعم جميع بطاقات الائتمان والخصم الرئيسية.",
    paypalDesc: "ادفع بأمان عبر حساب PayPal — سريع وسهل.",
    payWithStripe: "ادفع عبر Stripe",
    payWithPayPal: "ادفع عبر PayPal",
  },
  thankYou: {
    awaitingPayment: "في انتظار الدفع",
    awaitingPaymentDesc: "تم إنشاء طلبك. نحن بانتظار تأكيد الدفع.",
    thankYou: "شكراً لك!",
    orderConfirmed: "تم تأكيد طلبك وهو قيد المعالجة.",
    referenceNumber: "رقم المرجع",
    copy: "نسخ",
    copied: "تم النسخ!",
    payment: "الدفع",
    delivery: "التسليم",
    services: "الخدمات",
    total: "الإجمالي",
    whatsNext: "ماذا سيحدث بعد ذلك؟",
    step1: "طلبك قيد المراجعة والمعالجة",
    step2: "ستبدأ النتائج بالظهور تدريجياً",
    step3: "التسليم الكامل خلال 24-72 ساعة",
    bookmark: "احفظ هذه الصفحة للتحقق من حالة طلبك — لا حاجة لتسجيل الدخول.",
    placeAnother: "تقديم طلب آخر",
    pending: "معلق",
    paid: "مدفوع",
    completed: "مكتمل",
    processingStatus: "قيد المعالجة",
    delivered: "تم التسليم",
    failed: "فشل",
  },
  dashboard: {
    trackYourOrder: "تتبع طلبك",
    trackDesc: "أدخل رقم المرجع أو اعرض طلباتك الأخيرة.",
    searchPlaceholder: "مثال TD-ABC12345",
    noOrderFound: "لم يتم العثور على طلب بهذا الرقم.",
    yourOrderHistory: "سجل طلباتك",
    refresh: "تحديث",
    noHistory: "لا يوجد سجل طلبات. قم بطلب لتراه هنا!",
    placeAnother: "تقديم طلب آخر",
    bookmark: "احفظ هذه الصفحة للتحقق من حالة طلبك — لا حاجة لتسجيل الدخول.",
    whatsNext: "ماذا سيحدث بعد ذلك؟",
    step1: "طلبك قيد المراجعة والمعالجة",
    step2: "ستبدأ النتائج بالظهور تدريجياً",
    step3: "التسليم الكامل خلال 24-72 ساعة",
  },
  heroByPlatform: {
    tiktok: { title: "روّج لمحتواك على", highlight: "TikTok", subtitle: "وسّع وصولك على TikTok مع خدمات تسويق تساعد فيديوهاتك على الانتشار." },
    youtube: { title: "نمِّ قناتك على", highlight: "YouTube", subtitle: "روّج لفيديوهاتك وقناتك بخدمات تسويق مصممة لتوسيع جمهورك." },
    spotify: { title: "روّج لموسيقاك على", highlight: "Spotify", subtitle: "أوصل أغانيك إلى مستمعين جدد بخدمات ترويج للفنانين وشركات الإنتاج." },
    instagram: { title: "عزّز حضورك على", highlight: "Instagram", subtitle: "روّج لحسابك ومنشوراتك ومقاطع Reels بخدمات تسويق توسّع جمهورك." },
    twitch: { title: "نمِّ بثك على", highlight: "Twitch", subtitle: "روّج لقناتك وبثوثك المباشرة بخدمات تسويق تصل بك إلى مشاهدين جدد." },
    twitter: { title: "روّج لحسابك على", highlight: "X", subtitle: "وسّع وصولك على X بخدمات ترويج للتغريدات والملفات الشخصية والحملات." },
    facebook: { title: "روّج لصفحتك على", highlight: "Facebook", subtitle: "نمِّ صفحتك ومنشوراتك وفيديوهاتك على Facebook بخدمات تسويق احترافية." },
  },
};

const fr: Translation = {
  ...en,
  nav: { trackOrder: "Suivre la Commande", track: "Suivre" },
  hero: {
    title: "Développez Votre",
    titleHighlight: "Audience TikTok",
    subtitle: "Services de promotion professionnels pour aider votre contenu à toucher plus de monde.",
    placeholder: "Entrez votre nom d'utilisateur TikTok",
    cta: "Commencer",
    securePayments: "Paiements Sécurisés",
    fastProcessing: "Traitement Rapide",
    noPassword: "Aucun Mot de Passe",
    noUsersFound: "Aucun utilisateur trouvé",
    tryDifferent: "Essayez un autre nom",
  },
  valueProp: {
    title: "Pourquoi Choisir",
    titleHighlight: "TokDadddy",
    subtitle: "Une plateforme de confiance pour la promotion de contenu TikTok.",
    audienceGrowth: "Croissance d'Audience",
    audienceGrowthDesc: "Services professionnels conçus pour que votre contenu touche plus de spectateurs.",
    fastProcessing: "Traitement Rapide",
    fastProcessingDesc: "Les commandes sont traitées rapidement. Résultats visibles sous 24–72 heures.",
    privacyFirst: "Confidentialité d'Abord",
    privacyFirstDesc: "Nous ne demandons jamais vos identifiants de connexion.",
    securePayments: "Paiements Sécurisés",
    securePaymentsDesc: "Plusieurs options de paiement. Transactions cryptées et conformes PCI.",
    orderTracking: "Suivi de Commande",
    orderTrackingDesc: "Suivez l'état de votre commande en temps réel.",
    dedicatedSupport: "Support Dédié",
    dedicatedSupportDesc: "Notre équipe est disponible pour répondre à vos questions.",
  },
  howItWorks: {
    title: "Comment Ça Marche",
    subtitle: "Commencez en moins de 2 minutes. Aucun mot de passe requis — jamais.",
    step1Title: "Trouvez Votre Profil",
    step1Desc: "Entrez votre nom TikTok et nous le trouverons instantanément.",
    step2Title: "Sélectionnez les Services",
    step2Desc: "Choisissez parmi nos forfaits de promotion et personnalisez votre commande.",
    step3Title: "Paiement Sécurisé",
    step3Desc: "Finalisez votre achat avec notre système de paiement crypté.",
    step4Title: "Suivez Votre Commande",
    step4Desc: "Surveillez la progression de votre commande en temps réel.",
    stat247: "24/7", statSSL: "SSL", statNo: "Non", statDelivery: "24–72h",
    statSupportLabel: "Support Client",
    statEncryptedLabel: "Paiements Cryptés",
    statPasswordLabel: "Mot de Passe Requis",
    statDeliveryLabel: "Délai de Livraison",
  },
  footer: {
    faqTitle: "Questions Fréquentes",
    faq1Q: "Mon compte est-il sécurisé ?",
    faq1A: "Absolument. Nous ne demandons jamais votre mot de passe. Seul votre nom d'utilisateur public est nécessaire.",
    faq2Q: "Combien de temps prend le traitement ?",
    faq2A: "Les commandes sont traitées immédiatement. Résultats visibles sous 24–72 heures.",
    faq3Q: "Quels modes de paiement acceptez-vous ?",
    faq3A: "Nous acceptons les cartes de crédit/débit, PayPal et les cryptomonnaies.",
    faq4Q: "Quelle est votre politique de remboursement ?",
    faq4A: "Nous offrons des remboursements pour les commandes non livrées. Contactez hello@tokdadddy.com.",
    privacy: "Confidentialité", terms: "Conditions", refundPolicy: "Politique de Remboursement", contact: "Contact",
    copyright: "tokdadddy.com. Tous droits réservés. Nous ne sommes pas affiliés à TikTok ou ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe Sécurisé", crypto: "Crypto", sslEncrypted: "SSL Crypté",
  },
  currency: { disclaimer: "Paiement en USD. Le prix local est indicatif uniquement." },
  createOrder: {
    followers: "Abonnés",
    likes: "J'aime",
    views: "Vues",
    shares: "Partages",
    comments: "Commentaires",
    selectPostsNextStep: "(sélectionner les publications à l'étape suivante)",
    total: "Total",
    discount: "Réduction",
    continue: "Continuer",
    contactUs: "Si vous avez des questions, envoyez-nous un message à",
    selected: "Sélectionné",
    posts: "Publications",
  },
  order: {
    totalDueToday: "Total Dû Aujourd'hui",
    totalDue: "Total Dû",
    selectVideos: "Sélectionnez les vidéos à promouvoir",
    selectVideosDesc: "Choisissez les vidéos que vous souhaitez booster",
    noVideosFound: "Aucune vidéo trouvée pour cet utilisateur.",
    continueWithVideos: "Continuer avec {count} vidéo{s}",
    selectAtLeastOne: "Sélectionnez au moins une vidéo",
    oneTimeOffer: "Offre Unique 🔥",
    addFollowers: "Ajoutez 5 000 Abonnés TikTok pour seulement",
    applied: "Appliqué",
    apply: "Appliquer",
    paymentFailed: "Paiement échoué",
    paymentDeclined: "Votre paiement a été refusé. Essayez un autre moyen de paiement ci-dessous.",
    tryCard: "💳 Essayer Carte",
    tryStripe: "💳 Essayer Stripe",
    tryCrypto: "₿ Essayer Crypto",
    nameOnCard: "Nom sur la Carte",
    cardNumber: "Numéro de Carte",
    expiry: "Expiration",
    cvv: "CVV",
    processing: "Traitement...",
    pay: "Payer",
    stripeDesc: "Payez en toute sécurité via Stripe Checkout. Cartes, Apple Pay, Google Pay et plus.",
    payViaStripe: "Payer via Stripe",
    redirectingStripe: "Redirection vers Stripe...",
    stripePowered: "🔒 Propulsé par Stripe — PCI Level 1",
    cryptoDesc: "Payez en Bitcoin, USDT, ETH et plus de 20 cryptomonnaies via Cryptomus.",
    payWithCrypto: "Payer en Crypto",
    secureCryptomus: "🔒 Paiement sécurisé via Cryptomus",
    agreeTerms: "Vous acceptez nos",
    terms: "Conditions",
    refundPolicy: "Politique de Remboursement",
    safeCheckout: "Paiement Sécurisé",
    sslEncrypted: "SSL Crypté",
    guaranteed: "Livraison Suivie",
    backToVideos: "← Retour à la sélection vidéo",
    videos: "vidéos",
    bonus: "Bonus",
    selected: "sélectionnés",
    orderSummary: "Récapitulatif de commande",
    minimumOrder: "La commande minimum est de $0.50",
    invalidCardNumber: "Entrez un numéro de carte valide",
    invalidExpiry: "Date d'expiration invalide (MM/AA)",
    invalidCvv: "CVV invalide",
    cardPaymentFailed: "Paiement par carte échoué.",
    cardPaymentDeclined: "Paiement par carte refusé.",
    stripeError: "Erreur Stripe",
    stripePaymentFailed: "Paiement Stripe échoué.",
    paypalFailed: "PayPal a échoué.",
    cryptoPaymentFailed: "Paiement crypto échoué.",
    tryPayPal: "🅿️ Essayer PayPal",
    cardLabel: "Carte", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Crypto",
    stripeDescShort: "Payez en toute sécurité via Stripe — prend en charge toutes les principales cartes de crédit et de débit.",
    paypalDesc: "Payez en toute sécurité avec votre compte PayPal — checkout rapide et facile.",
    payWithStripe: "Payer avec Stripe",
    payWithPayPal: "Payer avec PayPal",
  },
  thankYou: {
    awaitingPayment: "En Attente de Paiement",
    awaitingPaymentDesc: "Votre commande a été créée. Nous attendons la confirmation du paiement.",
    thankYou: "Merci !",
    orderConfirmed: "Votre commande a été confirmée et est en cours de traitement.",
    referenceNumber: "Numéro de Référence",
    copy: "Copier",
    copied: "Copié !",
    payment: "Paiement",
    delivery: "Livraison",
    services: "Services",
    total: "Total",
    whatsNext: "Que se passe-t-il ensuite ?",
    step1: "Votre commande est en cours de révision et de traitement",
    step2: "Les résultats commenceront à apparaître progressivement",
    step3: "La livraison complète s'effectue sous 24-72 heures",
    bookmark: "Ajoutez cette page à vos favoris pour vérifier l'état de votre commande — sans connexion.",
    placeAnother: "Passer une Autre Commande",
    pending: "En attente",
    paid: "Payé",
    completed: "Terminé",
    processingStatus: "En traitement",
    delivered: "Livré",
    failed: "Échoué",
  },
  dashboard: {
    trackYourOrder: "Suivez Votre Commande",
    trackDesc: "Entrez votre numéro de référence ou consultez vos commandes récentes.",
    searchPlaceholder: "ex. TD-ABC12345",
    noOrderFound: "Aucune commande trouvée avec ce numéro de référence.",
    yourOrderHistory: "Historique des Commandes",
    refresh: "Actualiser",
    noHistory: "Pas encore d'historique. Passez une commande pour le voir ici !",
    placeAnother: "Passer une Autre Commande",
    bookmark: "Ajoutez cette page à vos favoris pour vérifier l'état — sans connexion.",
    whatsNext: "Que se passe-t-il ensuite ?",
    step1: "Votre commande est en cours de révision et de traitement",
    step2: "Les résultats commenceront à apparaître progressivement",
    step3: "La livraison complète s'effectue sous 24-72 heures",
  },
  heroByPlatform: {
    tiktok: { title: "Faites la promotion de", highlight: "votre contenu TikTok", subtitle: "Élargissez votre portée sur TikTok grâce à des services marketing qui aident vos vidéos à être découvertes." },
    youtube: { title: "Développez votre", highlight: "chaîne YouTube", subtitle: "Faites la promotion de vos vidéos et de votre chaîne avec des services marketing conçus pour élargir votre audience." },
    spotify: { title: "Faites la promotion de", highlight: "votre musique Spotify", subtitle: "Faites découvrir vos titres à de nouveaux auditeurs grâce à des services de promotion pour artistes et labels." },
    instagram: { title: "Boostez votre", highlight: "présence Instagram", subtitle: "Faites la promotion de votre profil, de vos publications et de vos reels avec des services marketing dédiés." },
    twitch: { title: "Développez votre", highlight: "stream Twitch", subtitle: "Faites la promotion de votre chaîne et de vos streams avec des services marketing pour toucher de nouveaux spectateurs." },
    twitter: { title: "Faites la promotion de", highlight: "votre profil X", subtitle: "Élargissez votre portée sur X avec des services de promotion pour tweets, profils et campagnes." },
    facebook: { title: "Faites la promotion de", highlight: "votre page Facebook", subtitle: "Développez votre page, vos publications et vos vidéos Facebook avec des services marketing." },
  },
};

const pt: Translation = {
  ...en,
  nav: { trackOrder: "Rastrear Pedido", track: "Rastrear" },
  hero: {
    title: "Cresça Seu",
    titleHighlight: "Público no TikTok",
    subtitle: "Serviços profissionais de promoção para ajudar seu conteúdo a alcançar mais pessoas.",
    placeholder: "Digite seu usuário do TikTok",
    cta: "Começar",
    securePayments: "Pagamentos Seguros",
    fastProcessing: "Processamento Rápido",
    noPassword: "Sem Senha",
    noUsersFound: "Nenhum usuário encontrado",
    tryDifferent: "Tente outro nome de usuário",
  },
  valueProp: {
    title: "Por Que Escolher",
    titleHighlight: "TokDadddy",
    subtitle: "Uma plataforma confiável para promoção de conteúdo no TikTok.",
    audienceGrowth: "Crescimento de Público",
    audienceGrowthDesc: "Serviços profissionais projetados para que seu conteúdo alcance mais espectadores.",
    fastProcessing: "Processamento Rápido",
    fastProcessingDesc: "Pedidos processados rapidamente. Resultados visíveis em 24–72 horas.",
    privacyFirst: "Privacidade em Primeiro",
    privacyFirstDesc: "Nunca solicitamos suas credenciais de login.",
    securePayments: "Pagamentos Seguros",
    securePaymentsDesc: "Múltiplas opções de pagamento. Transações criptografadas e compatíveis com PCI.",
    orderTracking: "Rastreamento de Pedido",
    orderTrackingDesc: "Monitore o status do seu pedido em tempo real.",
    dedicatedSupport: "Suporte Dedicado",
    dedicatedSupportDesc: "Nossa equipe está disponível para ajudá-lo com qualquer dúvida.",
  },
  howItWorks: {
    title: "Como Funciona",
    subtitle: "Comece em menos de 2 minutos. Sem senha — nunca.",
    step1Title: "Encontre Seu Perfil",
    step1Desc: "Digite seu usuário do TikTok e o encontraremos instantaneamente.",
    step2Title: "Selecione Serviços",
    step2Desc: "Escolha entre nossos pacotes de promoção e personalize seu pedido.",
    step3Title: "Pagamento Seguro",
    step3Desc: "Conclua sua compra com nosso sistema de pagamento criptografado.",
    step4Title: "Rastreie Seu Pedido",
    step4Desc: "Monitore o progresso do seu pedido em tempo real.",
    stat247: "24/7", statSSL: "SSL", statNo: "Não", statDelivery: "24–72h",
    statSupportLabel: "Suporte ao Cliente",
    statEncryptedLabel: "Pagamentos Criptografados",
    statPasswordLabel: "Senha Necessária",
    statDeliveryLabel: "Prazo de Entrega",
  },
  footer: {
    faqTitle: "Perguntas Frequentes",
    faq1Q: "Minha conta está segura?",
    faq1A: "Absolutamente. Nunca solicitamos sua senha. Apenas seu nome de usuário público é necessário.",
    faq2Q: "Quanto tempo leva o processamento?",
    faq2A: "Os pedidos começam a ser processados imediatamente. Resultados visíveis em 24–72 horas.",
    faq3Q: "Quais métodos de pagamento vocês aceitam?",
    faq3A: "Aceitamos cartões de crédito/débito, PayPal e criptomoedas.",
    faq4Q: "Qual é a política de reembolso?",
    faq4A: "Oferecemos reembolsos para pedidos não entregues. Contate hello@tokdadddy.com.",
    privacy: "Privacidade", terms: "Termos", refundPolicy: "Política de Reembolso", contact: "Contato",
    copyright: "tokdadddy.com. Todos os direitos reservados. Não somos afiliados ao TikTok ou ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe Seguro", crypto: "Crypto", sslEncrypted: "SSL Criptografado",
  },
  currency: { disclaimer: "Cobrado em USD. O preço local é apenas referência." },
  createOrder: {
    followers: "Seguidores",
    likes: "Curtidas",
    views: "Visualizações",
    shares: "Compartilhamentos",
    comments: "Comentários",
    selectPostsNextStep: "(selecione publicações no próximo passo)",
    total: "Total",
    discount: "Desconto",
    continue: "Continuar",
    contactUs: "Se tiver alguma dúvida, envie-nos uma mensagem a qualquer momento para",
    selected: "Selecionado",
    posts: "Publicações",
  },
  order: {
    totalDueToday: "Total a Pagar Hoje",
    totalDue: "Total a Pagar",
    selectVideos: "Selecione vídeos para promover",
    selectVideosDesc: "Escolha quais vídeos deseja impulsionar",
    noVideosFound: "Nenhum vídeo encontrado para este usuário.",
    continueWithVideos: "Continuar com {count} vídeo{s}",
    selectAtLeastOne: "Selecione pelo menos um vídeo",
    oneTimeOffer: "Oferta Única 🔥",
    addFollowers: "Adicione 5.000 Seguidores TikTok por apenas",
    applied: "Aplicado",
    apply: "Aplicar",
    paymentFailed: "Pagamento falhou",
    paymentDeclined: "Seu pagamento foi recusado. Tente outro método de pagamento abaixo.",
    tryCard: "💳 Tentar Cartão",
    tryStripe: "💳 Tentar Stripe",
    tryCrypto: "₿ Tentar Crypto",
    nameOnCard: "Nome no Cartão",
    cardNumber: "Número do Cartão",
    expiry: "Validade",
    cvv: "CVV",
    processing: "Processando...",
    pay: "Pagar",
    stripeDesc: "Pague com segurança via Stripe Checkout. Aceita cartões, Apple Pay, Google Pay e mais.",
    payViaStripe: "Pagar via Stripe",
    redirectingStripe: "Redirecionando para Stripe...",
    stripePowered: "🔒 Powered by Stripe — PCI Level 1",
    cryptoDesc: "Pague com Bitcoin, USDT, ETH e mais de 20 criptomoedas via Cryptomus.",
    payWithCrypto: "Pagar com Crypto",
    secureCryptomus: "🔒 Pagamento seguro via Cryptomus",
    agreeTerms: "Você concorda com nossos",
    terms: "Termos",
    refundPolicy: "Política de Reembolso",
    safeCheckout: "Pagamento Seguro",
    sslEncrypted: "SSL Criptografado",
    guaranteed: "Entrega Rastreada",
    backToVideos: "← Voltar para seleção de vídeos",
    videos: "vídeos",
    bonus: "Bônus",
    selected: "selecionados",
    orderSummary: "Resumo do pedido",
    minimumOrder: "O pedido mínimo é $0.50",
    invalidCardNumber: "Insira um número de cartão válido",
    invalidExpiry: "Data de validade inválida (MM/AA)",
    invalidCvv: "CVV inválido",
    cardPaymentFailed: "Pagamento com cartão falhou.",
    cardPaymentDeclined: "Pagamento com cartão recusado.",
    stripeError: "Erro Stripe",
    stripePaymentFailed: "Pagamento Stripe falhou.",
    paypalFailed: "PayPal falhou.",
    cryptoPaymentFailed: "Pagamento cripto falhou.",
    tryPayPal: "🅿️ Tentar PayPal",
    cardLabel: "Cartão", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Cripto",
    stripeDescShort: "Pague com segurança via Stripe — suporta todos os principais cartões de crédito e débito.",
    paypalDesc: "Pague com segurança com sua conta PayPal — checkout rápido e fácil.",
    payWithStripe: "Pagar com Stripe",
    payWithPayPal: "Pagar com PayPal",
  },
  thankYou: {
    awaitingPayment: "Aguardando Pagamento",
    awaitingPaymentDesc: "Seu pedido foi criado. Estamos aguardando confirmação de pagamento.",
    thankYou: "Obrigado!",
    orderConfirmed: "Seu pedido foi confirmado e está sendo processado.",
    referenceNumber: "Número de Referência",
    copy: "Copiar",
    copied: "Copiado!",
    payment: "Pagamento",
    delivery: "Entrega",
    services: "Serviços",
    total: "Total",
    whatsNext: "O que acontece a seguir?",
    step1: "Seu pedido está sendo revisado e processado",
    step2: "Os resultados começarão a aparecer gradualmente",
    step3: "A entrega completa é realizada em 24-72 horas",
    bookmark: "Salve esta página para verificar o status — sem precisar de login.",
    placeAnother: "Fazer Outro Pedido",
    pending: "Pendente",
    paid: "Pago",
    completed: "Concluído",
    processingStatus: "Processando",
    delivered: "Entregue",
    failed: "Falhou",
  },
  dashboard: {
    trackYourOrder: "Rastreie Seu Pedido",
    trackDesc: "Insira seu número de referência ou veja seus pedidos recentes.",
    searchPlaceholder: "ex. TD-ABC12345",
    noOrderFound: "Nenhum pedido encontrado com esse número de referência.",
    yourOrderHistory: "Histórico de Pedidos",
    refresh: "Atualizar",
    noHistory: "Sem histórico de pedidos. Faça um pedido para ver aqui!",
    placeAnother: "Fazer Outro Pedido",
    bookmark: "Salve esta página para verificar o status — sem precisar de login.",
    whatsNext: "O que acontece a seguir?",
    step1: "Seu pedido está sendo revisado e processado",
    step2: "Os resultados começarão a aparecer gradualmente",
    step3: "A entrega completa é realizada em 24-72 horas",
  },
  heroByPlatform: {
    tiktok: { title: "Promova seu", highlight: "conteúdo no TikTok", subtitle: "Aumente seu alcance no TikTok com serviços de marketing que ajudam seus vídeos a serem descobertos." },
    youtube: { title: "Faça crescer seu", highlight: "canal no YouTube", subtitle: "Promova seus vídeos e canal com serviços de marketing pensados para expandir seu público." },
    spotify: { title: "Promova sua", highlight: "música no Spotify", subtitle: "Leve suas faixas a novos ouvintes com serviços de promoção para artistas e gravadoras." },
    instagram: { title: "Impulsione sua", highlight: "presença no Instagram", subtitle: "Promova seu perfil, publicações e reels com serviços de marketing para expandir seu público." },
    twitch: { title: "Faça crescer sua", highlight: "transmissão na Twitch", subtitle: "Promova seu canal e suas lives com serviços de marketing para alcançar novos espectadores." },
    twitter: { title: "Promova seu", highlight: "perfil no X", subtitle: "Amplie seu alcance no X com serviços de promoção para tweets, perfis e campanhas." },
    facebook: { title: "Promova sua", highlight: "página do Facebook", subtitle: "Faça crescer sua página, publicações e vídeos do Facebook com serviços de marketing." },
  },
};

const ja: Translation = {
  ...en,
  nav: { trackOrder: "注文を追跡", track: "追跡" },
  hero: {
    title: "TikTokの",
    titleHighlight: "オーディエンスを成長",
    subtitle: "あなたのコンテンツをより多くの人に届けるプロフェッショナルなソーシャルメディアプロモーションサービス。",
    placeholder: "TikTokユーザー名を入力",
    cta: "始める",
    securePayments: "安全な支払い",
    fastProcessing: "高速処理",
    noPassword: "パスワード不要",
    noUsersFound: "ユーザーが見つかりません",
    tryDifferent: "別のユーザー名を試してください",
  },
  valueProp: {
    title: "なぜ選ぶ？",
    titleHighlight: "TokDadddy",
    subtitle: "TikTokコンテンツのプロモーションとオーディエンス育成のための信頼できるプラットフォーム。",
    audienceGrowth: "オーディエンス成長",
    audienceGrowthDesc: "より多くの視聴者にコンテンツを届けるプロフェッショナルなプロモーションサービス。",
    fastProcessing: "高速処理",
    fastProcessingDesc: "注文は即座に処理されます。結果は通常24〜72時間以内に表示されます。",
    privacyFirst: "プライバシー優先",
    privacyFirstDesc: "ログイン情報は決して要求しません。アカウントのセキュリティが最優先です。",
    securePayments: "安全な支払い",
    securePaymentsDesc: "複数の支払いオプション。全取引は暗号化されPCI準拠です。",
    orderTracking: "注文追跡",
    orderTrackingDesc: "リアルタイムで注文状況を監視できます。",
    dedicatedSupport: "専任サポート",
    dedicatedSupportDesc: "サポートチームがあらゆるご質問にお答えします。",
  },
  howItWorks: {
    title: "使い方",
    subtitle: "2分以内で開始。パスワードは不要です。",
    step1Title: "プロフィールを検索",
    step1Desc: "TikTokユーザー名を入力すると、すぐにプロフィールが見つかります。",
    step2Title: "サービスを選択",
    step2Desc: "プロモーションパッケージから選び、注文をカスタマイズ。",
    step3Title: "安全な決済",
    step3Desc: "暗号化された支払いシステムで購入を完了。",
    step4Title: "注文を追跡",
    step4Desc: "確認ページから注文の進捗をリアルタイムで監視。",
    stat247: "24/7", statSSL: "SSL", statNo: "不要", statDelivery: "24〜72時間",
    statSupportLabel: "カスタマーサポート",
    statEncryptedLabel: "暗号化された支払い",
    statPasswordLabel: "パスワード",
    statDeliveryLabel: "配達時間",
  },
  footer: {
    faqTitle: "よくある質問",
    faq1Q: "アカウントは安全ですか？",
    faq1A: "もちろんです。パスワードやログイン情報は決して要求しません。公開ユーザー名のみが必要です。",
    faq2Q: "処理にどのくらいかかりますか？",
    faq2A: "注文はすぐに処理開始されます。結果は通常24〜72時間以内に表示されます。",
    faq3Q: "どの支払い方法を受け付けていますか？",
    faq3A: "主要なクレジット/デビットカード、PayPal、暗号通貨を受け付けています。",
    faq4Q: "返金ポリシーは？",
    faq4A: "未配達の注文に対して返金を提供しています。hello@tokdadddy.comまでお問い合わせください。",
    privacy: "プライバシー", terms: "利用規約", refundPolicy: "返金ポリシー", contact: "お問い合わせ",
    copyright: "tokdadddy.com. 全著作権所有。TikTokまたはByteDanceとは提携していません。",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe セキュア", crypto: "暗号通貨", sslEncrypted: "SSL暗号化",
  },
  currency: { disclaimer: "USD で請求されます。現地価格は参考値です。" },
  createOrder: {
    followers: "フォロワー",
    likes: "いいね",
    views: "再生回数",
    shares: "シェア",
    comments: "コメント",
    selectPostsNextStep: "（次のステップで投稿を選択）",
    total: "合計",
    discount: "割引",
    continue: "続ける",
    contactUs: "ご質問がある場合は、いつでもこちらまでメッセージをお送りください",
    selected: "選択済み",
    posts: "投稿",
  },
  order: {
    totalDueToday: "本日のお支払い合計",
    totalDue: "お支払い合計",
    selectVideos: "プロモーションする動画を選択",
    selectVideosDesc: "ブーストしたい動画を選んでください",
    noVideosFound: "このユーザーの動画が見つかりません。",
    continueWithVideos: "{count}本の動画で続行",
    selectAtLeastOne: "少なくとも1本の動画を選択してください",
    oneTimeOffer: "限定オファー 🔥",
    addFollowers: "5,000 TikTokフォロワーを追加（たった",
    applied: "適用済み",
    apply: "適用",
    paymentFailed: "支払い失敗",
    paymentDeclined: "お支払いが拒否されました。下記の別の支払い方法をお試しください。",
    tryCard: "💳 カード",
    tryStripe: "💳 Stripe",
    tryCrypto: "₿ 暗号通貨",
    nameOnCard: "カード名義人",
    cardNumber: "カード番号",
    expiry: "有効期限",
    cvv: "CVV",
    processing: "処理中...",
    pay: "支払う",
    stripeDesc: "Stripe Checkoutで安全に支払い。カード、Apple Pay、Google Pay等に対応。",
    payViaStripe: "Stripeで支払う",
    redirectingStripe: "Stripeにリダイレクト中...",
    stripePowered: "🔒 Stripe提供 — PCI Level 1認定",
    cryptoDesc: "Cryptomusを通じてBitcoin、USDT、ETHなど20以上の暗号通貨で支払い。",
    payWithCrypto: "暗号通貨で支払う",
    secureCryptomus: "🔒 Cryptomusによる安全な支払い",
    agreeTerms: "以下に同意します",
    terms: "利用規約",
    refundPolicy: "返金ポリシー",
    safeCheckout: "安全な決済",
    sslEncrypted: "SSL暗号化",
    guaranteed: "配送追跡",
    backToVideos: "← 動画選択に戻る",
    videos: "本の動画",
    bonus: "ボーナス",
    selected: "選択済み",
    orderSummary: "注文の概要",
    minimumOrder: "最低注文額は $0.50 です",
    invalidCardNumber: "有効なカード番号を入力してください",
    invalidExpiry: "有効期限が無効です (MM/YY)",
    invalidCvv: "CVV が無効です",
    cardPaymentFailed: "カード決済に失敗しました。",
    cardPaymentDeclined: "カード決済が拒否されました。",
    stripeError: "Stripe エラー",
    stripePaymentFailed: "Stripe 決済に失敗しました。",
    paypalFailed: "PayPal に失敗しました。",
    cryptoPaymentFailed: "暗号通貨決済に失敗しました。",
    tryPayPal: "🅿️ PayPal を試す",
    cardLabel: "カード", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "暗号通貨",
    stripeDescShort: "Stripe による安全な決済 — 主要なクレジットカード・デビットカードに対応。",
    paypalDesc: "PayPal アカウントで安全に決済 — 速くて簡単。",
    payWithStripe: "Stripe で支払う",
    payWithPayPal: "PayPal で支払う",
  },
  thankYou: {
    awaitingPayment: "お支払い待ち",
    awaitingPaymentDesc: "注文が作成されました。お支払いの確認をお待ちしています。",
    thankYou: "ありがとうございます！",
    orderConfirmed: "ご注文が確認され、処理中です。",
    referenceNumber: "参照番号",
    copy: "コピー",
    copied: "コピーしました！",
    payment: "お支払い",
    delivery: "配送",
    services: "サービス",
    total: "合計",
    whatsNext: "次のステップは？",
    step1: "ご注文は審査・処理中です",
    step2: "結果は徐々にアカウントに反映されます",
    step3: "完全な配信は24〜72時間で完了します",
    bookmark: "このページをブックマークしていつでも注文状況を確認 — ログイン不要。",
    placeAnother: "もう一度注文する",
    pending: "保留中",
    paid: "支払い済み",
    completed: "完了",
    processingStatus: "処理中",
    delivered: "配信済み",
    failed: "失敗",
  },
  dashboard: {
    trackYourOrder: "注文を追跡",
    trackDesc: "参照番号を入力するか、最近の注文を確認してください。",
    searchPlaceholder: "例: TD-ABC12345",
    noOrderFound: "その参照番号の注文は見つかりませんでした。",
    yourOrderHistory: "注文履歴",
    refresh: "更新",
    noHistory: "注文履歴はまだありません。注文するとここに表示されます！",
    placeAnother: "もう一度注文する",
    bookmark: "このページをブックマークしていつでも注文状況を確認 — ログイン不要。",
    whatsNext: "次のステップは？",
    step1: "ご注文は審査・処理中です",
    step2: "結果は徐々にアカウントに反映されます",
    step3: "完全な配信は24〜72時間で完了します",
  },
  heroByPlatform: {
    tiktok: { title: "あなたの", highlight: "TikTokコンテンツを宣伝", subtitle: "動画が発見されるよう支援するマーケティングサービスでTikTokのリーチを拡大します。" },
    youtube: { title: "あなたの", highlight: "YouTubeチャンネルを成長", subtitle: "視聴者を拡大するために設計されたマーケティングサービスで動画とチャンネルを宣伝します。" },
    spotify: { title: "あなたの", highlight: "Spotify楽曲を宣伝", subtitle: "アーティストとレーベル向けのプロモーションサービスで楽曲を新しいリスナーに届けます。" },
    instagram: { title: "あなたの", highlight: "Instagramの存在感を強化", subtitle: "プロフィール、投稿、リールを宣伝し、視聴者を拡大します。" },
    twitch: { title: "あなたの", highlight: "Twitch配信を成長", subtitle: "新しい視聴者にリーチするためのマーケティングサービスでチャンネルと配信を宣伝します。" },
    twitter: { title: "あなたの", highlight: "Xプロフィールを宣伝", subtitle: "ツイート、プロフィール、キャンペーンのプロモーションサービスでXのリーチを拡大します。" },
    facebook: { title: "あなたの", highlight: "Facebookページを宣伝", subtitle: "マーケティングサービスでFacebookページ、投稿、動画を成長させ、より多くの視聴者にリーチします。" },
  },
};

const de: Translation = {
  ...en,
  nav: { trackOrder: "Bestellung verfolgen", track: "Verfolgen" },
  hero: {
    title: "Vergrößere dein TikTok",
    titleHighlight: "Publikum",
    subtitle: "Professionelle Social-Media-Promotion, um deine Inhalte mehr Menschen zu zeigen.",
    placeholder: "TikTok-Benutzername eingeben",
    cta: "Jetzt starten",
    securePayments: "Sichere Zahlung",
    fastProcessing: "Schnelle Bearbeitung",
    noPassword: "Kein Passwort nötig",
    noUsersFound: "Keine Benutzer gefunden",
    tryDifferent: "Anderen Namen versuchen",
  },
  valueProp: {
    title: "Warum",
    titleHighlight: "TokDadddy",
    subtitle: "Eine vertrauenswürdige Plattform für TikTok-Content-Promotion und Publikumswachstum.",
    audienceGrowth: "Publikumswachstum",
    audienceGrowthDesc: "Professionelle Promotion-Services, um deine Inhalte mehr Zuschauern zu zeigen.",
    fastProcessing: "Schnelle Bearbeitung",
    fastProcessingDesc: "Bestellungen werden sofort bearbeitet. Ergebnisse in 24–72 Stunden sichtbar.",
    privacyFirst: "Datenschutz zuerst",
    privacyFirstDesc: "Wir fragen niemals nach deinen Anmeldedaten. Deine Kontosicherheit hat Priorität.",
    securePayments: "Sichere Zahlungen",
    securePaymentsDesc: "Mehrere Zahlungsoptionen. Alle Transaktionen sind verschlüsselt und PCI-konform.",
    orderTracking: "Bestellverfolgung",
    orderTrackingDesc: "Verfolge deinen Bestellstatus in Echtzeit.",
    dedicatedSupport: "Persönlicher Support",
    dedicatedSupportDesc: "Unser Support-Team steht dir bei allen Fragen zur Verfügung.",
  },
  howItWorks: {
    title: "So funktioniert's",
    subtitle: "Starte in unter 2 Minuten. Kein Passwort nötig — niemals.",
    step1Title: "Profil finden",
    step1Desc: "Gib deinen TikTok-Benutzernamen ein und wir finden dein Profil sofort.",
    step2Title: "Services wählen",
    step2Desc: "Wähle aus unseren Promotion-Paketen und passe deine Bestellung an.",
    step3Title: "Sicher bezahlen",
    step3Desc: "Schließe deinen Kauf mit unserem verschlüsselten Zahlungssystem ab.",
    step4Title: "Bestellung verfolgen",
    step4Desc: "Verfolge den Fortschritt deiner Bestellung in Echtzeit.",
    stat247: "24/7", statSSL: "SSL", statNo: "Nein", statDelivery: "24–72 Std.",
    statSupportLabel: "Kundensupport",
    statEncryptedLabel: "Verschlüsselte Zahlungen",
    statPasswordLabel: "Passwort erforderlich",
    statDeliveryLabel: "Lieferzeit",
  },
  footer: {
    faqTitle: "Häufig gestellte Fragen",
    faq1Q: "Ist mein Konto sicher?",
    faq1A: "Absolut. Wir fragen niemals nach deinem Passwort. Nur dein öffentlicher Benutzername wird benötigt.",
    faq2Q: "Wie lange dauert die Bearbeitung?",
    faq2A: "Bestellungen werden sofort bearbeitet. Ergebnisse sind in 24–72 Stunden sichtbar.",
    faq3Q: "Welche Zahlungsmethoden akzeptiert ihr?",
    faq3A: "Wir akzeptieren Kredit-/Debitkarten, PayPal und Kryptowährungen. Alle Transaktionen sind verschlüsselt.",
    faq4Q: "Wie ist eure Rückerstattungsrichtlinie?",
    faq4A: "Wir bieten Rückerstattungen für nicht gelieferte Bestellungen. Kontaktiere hello@tokdadddy.com.",
    privacy: "Datenschutz", terms: "AGB", refundPolicy: "Rückerstattung", contact: "Kontakt",
    copyright: "tokdadddy.com. Alle Rechte vorbehalten. Wir sind nicht mit TikTok oder ByteDance verbunden.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe Sicher", crypto: "Krypto", sslEncrypted: "SSL-verschlüsselt",
  },
  currency: { disclaimer: "Zahlung in USD. Der lokale Preis dient nur als Referenz." },
  createOrder: {
    followers: "Follower",
    likes: "Likes",
    views: "Aufrufe",
    shares: "Shares",
    comments: "Kommentare",
    selectPostsNextStep: "(Beiträge im nächsten Schritt auswählen)",
    total: "Gesamt",
    discount: "Rabatt",
    continue: "Weiter",
    contactUs: "Bei Fragen sende uns jederzeit eine Nachricht an",
    selected: "Ausgewählt",
    posts: "Beiträge",
  },
  order: {
    totalDueToday: "Heute fälliger Gesamtbetrag",
    totalDue: "Fälliger Gesamtbetrag",
    selectVideos: "Videos zur Promotion auswählen",
    selectVideosDesc: "Wähle die Videos, die du boosten möchtest",
    noVideosFound: "Keine Videos für diesen Benutzer gefunden.",
    continueWithVideos: "Weiter mit {count} Video{s}",
    selectAtLeastOne: "Wähle mindestens ein Video",
    oneTimeOffer: "Einmaliges Angebot 🔥",
    addFollowers: "5.000 TikTok Follower hinzufügen für nur",
    applied: "Angewendet",
    apply: "Anwenden",
    paymentFailed: "Zahlung fehlgeschlagen",
    paymentDeclined: "Deine Zahlung wurde abgelehnt. Versuche eine andere Zahlungsmethode.",
    tryCard: "💳 Karte versuchen",
    tryStripe: "💳 Stripe versuchen",
    tryCrypto: "₿ Krypto versuchen",
    nameOnCard: "Name auf der Karte",
    cardNumber: "Kartennummer",
    expiry: "Ablaufdatum",
    cvv: "CVV",
    processing: "Verarbeitung...",
    pay: "Bezahlen",
    stripeDesc: "Sicher bezahlen über Stripe Checkout. Unterstützt Karten, Apple Pay, Google Pay und mehr.",
    payViaStripe: "Über Stripe bezahlen",
    redirectingStripe: "Weiterleitung zu Stripe...",
    stripePowered: "🔒 Powered by Stripe — PCI Level 1",
    cryptoDesc: "Bezahle mit Bitcoin, USDT, ETH und über 20 weiteren Kryptowährungen via Cryptomus.",
    payWithCrypto: "Mit Krypto bezahlen",
    secureCryptomus: "🔒 Sichere Zahlung über Cryptomus",
    agreeTerms: "Du stimmst unseren zu",
    terms: "AGB",
    refundPolicy: "Rückerstattungsrichtlinie",
    safeCheckout: "Sicherer Checkout",
    sslEncrypted: "SSL-verschlüsselt",
    guaranteed: "Verfolgte Lieferung",
    backToVideos: "← Zurück zur Videoauswahl",
    videos: "Videos",
    bonus: "Bonus",
    selected: "ausgewählt",
    orderSummary: "Bestellübersicht",
    minimumOrder: "Mindestbestellung ist $0.50",
    invalidCardNumber: "Bitte gültige Kartennummer eingeben",
    invalidExpiry: "Ungültiges Ablaufdatum (MM/JJ)",
    invalidCvv: "Ungültiger CVV",
    cardPaymentFailed: "Kartenzahlung fehlgeschlagen.",
    cardPaymentDeclined: "Kartenzahlung abgelehnt.",
    stripeError: "Stripe-Fehler",
    stripePaymentFailed: "Stripe-Zahlung fehlgeschlagen.",
    paypalFailed: "PayPal fehlgeschlagen.",
    cryptoPaymentFailed: "Krypto-Zahlung fehlgeschlagen.",
    tryPayPal: "🅿️ PayPal versuchen",
    cardLabel: "Karte", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Krypto",
    stripeDescShort: "Sicher bezahlen über Stripe — unterstützt alle gängigen Kredit- und Debitkarten.",
    paypalDesc: "Sicher bezahlen mit Ihrem PayPal-Konto — schneller und einfacher Checkout.",
    payWithStripe: "Mit Stripe bezahlen",
    payWithPayPal: "Mit PayPal bezahlen",
  },
  thankYou: {
    awaitingPayment: "Warte auf Zahlung",
    awaitingPaymentDesc: "Deine Bestellung wurde erstellt. Wir warten auf die Zahlungsbestätigung.",
    thankYou: "Danke!",
    orderConfirmed: "Deine Bestellung wurde bestätigt und wird bearbeitet.",
    referenceNumber: "Referenznummer",
    copy: "Kopieren",
    copied: "Kopiert!",
    payment: "Zahlung",
    delivery: "Lieferung",
    services: "Services",
    total: "Gesamt",
    whatsNext: "Was passiert als nächstes?",
    step1: "Deine Bestellung wird geprüft und bearbeitet",
    step2: "Ergebnisse erscheinen schrittweise auf deinem Konto",
    step3: "Vollständige Lieferung innerhalb von 24-72 Stunden",
    bookmark: "Speichere diese Seite, um deinen Bestellstatus jederzeit zu prüfen — kein Login nötig.",
    placeAnother: "Weitere Bestellung aufgeben",
    pending: "Ausstehend",
    paid: "Bezahlt",
    completed: "Abgeschlossen",
    processingStatus: "In Bearbeitung",
    delivered: "Geliefert",
    failed: "Fehlgeschlagen",
  },
  dashboard: {
    trackYourOrder: "Bestellung verfolgen",
    trackDesc: "Gib deine Referenznummer ein oder sieh dir deine letzten Bestellungen an.",
    searchPlaceholder: "z.B. TD-ABC12345",
    noOrderFound: "Keine Bestellung mit dieser Referenznummer gefunden.",
    yourOrderHistory: "Deine Bestellhistorie",
    refresh: "Aktualisieren",
    noHistory: "Noch keine Bestellhistorie. Gib eine Bestellung auf, um sie hier zu sehen!",
    placeAnother: "Weitere Bestellung aufgeben",
    bookmark: "Speichere diese Seite, um deinen Bestellstatus jederzeit zu prüfen — kein Login nötig.",
    whatsNext: "Was passiert als nächstes?",
    step1: "Deine Bestellung wird geprüft und bearbeitet",
    step2: "Ergebnisse erscheinen schrittweise auf deinem Konto",
    step3: "Vollständige Lieferung innerhalb von 24-72 Stunden",
  },
  heroByPlatform: {
    tiktok: { title: "Bewirb deine", highlight: "TikTok-Inhalte", subtitle: "Erhöhe deine Reichweite auf TikTok mit Marketing-Services, die deinen Videos zu mehr Sichtbarkeit verhelfen." },
    youtube: { title: "Lass deinen", highlight: "YouTube-Kanal wachsen", subtitle: "Bewirb deine Videos und deinen Kanal mit Marketing-Services, die deine Zielgruppe erweitern." },
    spotify: { title: "Bewirb deine", highlight: "Spotify-Musik", subtitle: "Bring deine Tracks zu neuen Hörern mit Promotion-Services für Künstler und Labels." },
    instagram: { title: "Steigere deine", highlight: "Instagram-Präsenz", subtitle: "Bewirb dein Profil, deine Beiträge und Reels mit Marketing-Services für mehr Reichweite." },
    twitch: { title: "Lass deinen", highlight: "Twitch-Stream wachsen", subtitle: "Bewirb deinen Kanal und deine Streams mit Marketing-Services, um neue Zuschauer zu erreichen." },
    twitter: { title: "Bewirb dein", highlight: "X-Profil", subtitle: "Erweitere deine Reichweite auf X mit Promotion-Services für Tweets, Profile und Kampagnen." },
    facebook: { title: "Bewirb deine", highlight: "Facebook-Seite", subtitle: "Lass deine Facebook-Seite, Beiträge und Videos mit Marketing-Services wachsen." },
  },
};

const th: Translation = {
  ...en,
  nav: { trackOrder: "ติดตามคำสั่งซื้อ", track: "ติดตาม" },
  hero: {
    title: "เพิ่มยอดผู้ติดตาม",
    titleHighlight: "TikTok ของคุณ",
    subtitle: "บริการโปรโมทโซเชียลมีเดียระดับมืออาชีพ ช่วยให้คอนเทนต์ของคุณเข้าถึงผู้คนมากขึ้น",
    placeholder: "ใส่ชื่อผู้ใช้ TikTok ของคุณ",
    cta: "เริ่มต้นเลย",
    securePayments: "ชำระเงินปลอดภัย",
    fastProcessing: "ดำเนินการรวดเร็ว",
    noPassword: "ไม่ต้องใช้รหัสผ่าน",
    noUsersFound: "ไม่พบผู้ใช้",
    tryDifferent: "ลองใช้ชื่อผู้ใช้อื่น",
  },
  valueProp: {
    title: "ทำไมต้องเลือก",
    titleHighlight: "TokDadddy",
    subtitle: "แพลตฟอร์มที่เชื่อถือได้สำหรับโปรโมทคอนเทนต์ TikTok และเพิ่มผู้ติดตาม",
    audienceGrowth: "เพิ่มผู้ติดตาม",
    audienceGrowthDesc: "บริการโปรโมทระดับมืออาชีพ ช่วยให้คอนเทนต์ของคุณเข้าถึงผู้ชมมากขึ้น",
    fastProcessing: "ดำเนินการรวดเร็ว",
    fastProcessingDesc: "คำสั่งซื้อถูกดำเนินการทันที ผลลัพธ์จะเห็นภายใน 24–72 ชั่วโมง",
    privacyFirst: "ความเป็นส่วนตัวมาก่อน",
    privacyFirstDesc: "เราไม่เคยขอข้อมูลการเข้าสู่ระบบของคุณ ความปลอดภัยของบัญชีเป็นสิ่งสำคัญ",
    securePayments: "ชำระเงินปลอดภัย",
    securePaymentsDesc: "หลายช่องทางการชำระเงิน ทุกธุรกรรมเข้ารหัสและเป็นไปตามมาตรฐาน PCI",
    orderTracking: "ติดตามคำสั่งซื้อ",
    orderTrackingDesc: "ติดตามสถานะคำสั่งซื้อของคุณแบบเรียลไทม์",
    dedicatedSupport: "ทีมซัพพอร์ตเฉพาะ",
    dedicatedSupportDesc: "ทีมซัพพอร์ตของเราพร้อมช่วยเหลือคุณทุกคำถาม",
  },
  howItWorks: {
    title: "วิธีใช้งาน",
    subtitle: "เริ่มต้นภายใน 2 นาที ไม่ต้องใช้รหัสผ่าน — ตลอดไป",
    step1Title: "ค้นหาโปรไฟล์",
    step1Desc: "ใส่ชื่อผู้ใช้ TikTok ของคุณ เราจะค้นหาโปรไฟล์ให้ทันที",
    step2Title: "เลือกบริการ",
    step2Desc: "เลือกจากแพ็กเกจโปรโมทของเรา และปรับแต่งคำสั่งซื้อ",
    step3Title: "ชำระเงินอย่างปลอดภัย",
    step3Desc: "ดำเนินการซื้อด้วยระบบชำระเงินที่เข้ารหัส",
    step4Title: "ติดตามคำสั่งซื้อ",
    step4Desc: "ติดตามความคืบหน้าคำสั่งซื้อแบบเรียลไทม์",
    stat247: "24/7", statSSL: "SSL", statNo: "ไม่", statDelivery: "24–72 ชม.",
    statSupportLabel: "บริการลูกค้า",
    statEncryptedLabel: "การชำระเงินเข้ารหัส",
    statPasswordLabel: "ต้องใช้รหัสผ่าน",
    statDeliveryLabel: "ระยะเวลาส่งมอบ",
  },
  footer: {
    faqTitle: "คำถามที่พบบ่อย",
    faq1Q: "บัญชีของฉันปลอดภัยไหม?",
    faq1A: "ปลอดภัยอย่างแน่นอน เราไม่เคยขอรหัสผ่านหรือข้อมูลเข้าสู่ระบบ ต้องการเพียงชื่อผู้ใช้สาธารณะเท่านั้น",
    faq2Q: "ใช้เวลาดำเนินการนานแค่ไหน?",
    faq2A: "คำสั่งซื้อเริ่มดำเนินการทันที ผลลัพธ์จะเห็นภายใน 24–72 ชั่วโมง",
    faq3Q: "รับชำระเงินช่องทางไหนบ้าง?",
    faq3A: "เรารับบัตรเครดิต/เดบิต, PayPal และคริปโต ทุกธุรกรรมเข้ารหัสอย่างปลอดภัย",
    faq4Q: "นโยบายการคืนเงินเป็นอย่างไร?",
    faq4A: "เราคืนเงินสำหรับคำสั่งซื้อที่ไม่ได้รับการส่งมอบ ติดต่อ hello@tokdadddy.com",
    privacy: "ความเป็นส่วนตัว", terms: "ข้อกำหนด", refundPolicy: "นโยบายคืนเงิน", contact: "ติดต่อเรา",
    copyright: "tokdadddy.com สงวนลิขสิทธิ์ เราไม่ได้เป็นพันธมิตรกับ TikTok หรือ ByteDance",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe ปลอดภัย", crypto: "คริปโต", sslEncrypted: "เข้ารหัส SSL",
  },
  currency: { disclaimer: "ชำระเงินเป็น USD ราคาท้องถิ่นเป็นราคาประมาณเท่านั้น" },
  createOrder: {
    followers: "ผู้ติดตาม",
    likes: "ไลค์",
    views: "ยอดวิว",
    shares: "แชร์",
    comments: "คอมเมนต์",
    selectPostsNextStep: "(เลือกโพสต์ในขั้นตอนถัดไป)",
    total: "รวม",
    discount: "ส่วนลด",
    continue: "ดำเนินการต่อ",
    contactUs: "หากมีคำถาม ส่งข้อความถึงเราได้ที่",
    selected: "เลือกแล้ว",
    posts: "โพสต์",
  },
  order: {
    totalDueToday: "ยอดชำระวันนี้",
    totalDue: "ยอดชำระ",
    selectVideos: "เลือกวิดีโอที่ต้องการโปรโมท",
    selectVideosDesc: "เลือกวิดีโอที่คุณต้องการบูสต์",
    noVideosFound: "ไม่พบวิดีโอสำหรับผู้ใช้นี้",
    continueWithVideos: "ดำเนินการต่อกับ {count} วิดีโอ",
    selectAtLeastOne: "เลือกอย่างน้อย 1 วิดีโอ",
    oneTimeOffer: "ข้อเสนอพิเศษ 🔥",
    addFollowers: "เพิ่ม 5,000 ผู้ติดตาม TikTok ในราคาเพียง",
    applied: "ใช้แล้ว",
    apply: "ใช้",
    paymentFailed: "การชำระเงินล้มเหลว",
    paymentDeclined: "การชำระเงินถูกปฏิเสธ ลองวิธีชำระเงินอื่นด้านล่าง",
    tryCard: "💳 ลองบัตร",
    tryStripe: "💳 ลอง Stripe",
    tryCrypto: "₿ ลองคริปโต",
    nameOnCard: "ชื่อบนบัตร",
    cardNumber: "หมายเลขบัตร",
    expiry: "วันหมดอายุ",
    cvv: "CVV",
    processing: "กำลังดำเนินการ...",
    pay: "ชำระเงิน",
    stripeDesc: "ชำระเงินอย่างปลอดภัยผ่าน Stripe Checkout รองรับบัตร, Apple Pay, Google Pay และอื่นๆ",
    payViaStripe: "ชำระผ่าน Stripe",
    redirectingStripe: "กำลังไปยัง Stripe...",
    stripePowered: "🔒 ขับเคลื่อนโดย Stripe — PCI Level 1",
    cryptoDesc: "ชำระด้วย Bitcoin, USDT, ETH และคริปโตอีกกว่า 20 สกุลผ่าน Cryptomus",
    payWithCrypto: "ชำระด้วยคริปโต",
    secureCryptomus: "🔒 ชำระเงินปลอดภัยผ่าน Cryptomus",
    agreeTerms: "คุณยอมรับ",
    terms: "ข้อกำหนด",
    refundPolicy: "นโยบายคืนเงิน",
    safeCheckout: "ชำระเงินปลอดภัย",
    sslEncrypted: "เข้ารหัส SSL",
    guaranteed: "ติดตามการจัดส่ง",
    backToVideos: "← กลับไปเลือกวิดีโอ",
    videos: "วิดีโอ",
    bonus: "โบนัส",
    selected: "เลือกแล้ว",
    orderSummary: "สรุปคำสั่งซื้อ",
    minimumOrder: "ขั้นต่ำการสั่งซื้อ $0.50",
    invalidCardNumber: "กรุณากรอกเลขบัตรที่ถูกต้อง",
    invalidExpiry: "วันหมดอายุไม่ถูกต้อง (MM/YY)",
    invalidCvv: "CVV ไม่ถูกต้อง",
    cardPaymentFailed: "การชำระด้วยบัตรล้มเหลว",
    cardPaymentDeclined: "การชำระด้วยบัตรถูกปฏิเสธ",
    stripeError: "เกิดข้อผิดพลาด Stripe",
    stripePaymentFailed: "การชำระผ่าน Stripe ล้มเหลว",
    paypalFailed: "PayPal ล้มเหลว",
    cryptoPaymentFailed: "การชำระด้วยคริปโตล้มเหลว",
    tryPayPal: "🅿️ ลอง PayPal",
    cardLabel: "บัตร", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "คริปโต",
    stripeDescShort: "ชำระเงินอย่างปลอดภัยผ่าน Stripe — รองรับบัตรเครดิตและเดบิตหลักทุกประเภท",
    paypalDesc: "ชำระเงินอย่างปลอดภัยด้วยบัญชี PayPal ของคุณ — รวดเร็วและง่ายดาย",
    payWithStripe: "ชำระด้วย Stripe",
    payWithPayPal: "ชำระด้วย PayPal",
  },
  thankYou: {
    awaitingPayment: "รอการชำระเงิน",
    awaitingPaymentDesc: "คำสั่งซื้อของคุณถูกสร้างแล้ว เรากำลังรอการยืนยันการชำระเงิน",
    thankYou: "ขอบคุณ!",
    orderConfirmed: "คำสั่งซื้อของคุณได้รับการยืนยันและกำลังดำเนินการ",
    referenceNumber: "หมายเลขอ้างอิง",
    copy: "คัดลอก",
    copied: "คัดลอกแล้ว!",
    payment: "การชำระเงิน",
    delivery: "การจัดส่ง",
    services: "บริการ",
    total: "รวม",
    whatsNext: "ขั้นตอนถัดไปคืออะไร?",
    step1: "คำสั่งซื้อกำลังได้รับการตรวจสอบและดำเนินการ",
    step2: "ผลลัพธ์จะเริ่มปรากฏในบัญชีของคุณทีละน้อย",
    step3: "ส่งมอบเสร็จสมบูรณ์ภายใน 24-72 ชั่วโมง",
    bookmark: "บุ๊คมาร์คหน้านี้เพื่อตรวจสอบสถานะคำสั่งซื้อ — ไม่ต้องเข้าสู่ระบบ",
    placeAnother: "สั่งซื้ออีกครั้ง",
    pending: "รอดำเนินการ",
    paid: "ชำระแล้ว",
    completed: "เสร็จสมบูรณ์",
    processingStatus: "กำลังดำเนินการ",
    delivered: "ส่งมอบแล้ว",
    failed: "ล้มเหลว",
  },
  dashboard: {
    trackYourOrder: "ติดตามคำสั่งซื้อ",
    trackDesc: "ใส่หมายเลขอ้างอิงหรือดูคำสั่งซื้อล่าสุดด้านล่าง",
    searchPlaceholder: "เช่น TD-ABC12345",
    noOrderFound: "ไม่พบคำสั่งซื้อสำหรับหมายเลขอ้างอิงนี้",
    yourOrderHistory: "ประวัติคำสั่งซื้อ",
    refresh: "รีเฟรช",
    noHistory: "ยังไม่มีประวัติคำสั่งซื้อ สั่งซื้อเพื่อดูที่นี่!",
    placeAnother: "สั่งซื้ออีกครั้ง",
    bookmark: "บุ๊คมาร์คหน้านี้เพื่อตรวจสอบสถานะ — ไม่ต้องเข้าสู่ระบบ",
    whatsNext: "ขั้นตอนถัดไปคืออะไร?",
    step1: "คำสั่งซื้อกำลังได้รับการตรวจสอบและดำเนินการ",
    step2: "ผลลัพธ์จะเริ่มปรากฏในบัญชีของคุณทีละน้อย",
    step3: "ส่งมอบเสร็จสมบูรณ์ภายใน 24-72 ชั่วโมง",
  },
  heroByPlatform: {
    tiktok: { title: "โปรโมตคอนเทนต์", highlight: "TikTok ของคุณ", subtitle: "เพิ่มการเข้าถึงบน TikTok ด้วยบริการการตลาดที่ช่วยให้วิดีโอของคุณถูกค้นพบมากขึ้น" },
    youtube: { title: "เติบโตช่อง", highlight: "YouTube ของคุณ", subtitle: "โปรโมตวิดีโอและช่องของคุณด้วยบริการการตลาดที่ออกแบบมาเพื่อขยายฐานผู้ชม" },
    spotify: { title: "โปรโมตเพลง", highlight: "Spotify ของคุณ", subtitle: "นำเพลงของคุณไปสู่ผู้ฟังใหม่ๆ ด้วยบริการโปรโมตสำหรับศิลปินและค่ายเพลง" },
    instagram: { title: "เพิ่มการมีตัวตนบน", highlight: "Instagram ของคุณ", subtitle: "โปรโมตโปรไฟล์ โพสต์ และรีลของคุณด้วยบริการการตลาดเพื่อขยายฐานผู้ติดตาม" },
    twitch: { title: "เติบโตสตรีม", highlight: "Twitch ของคุณ", subtitle: "โปรโมตช่องและการสตรีมของคุณด้วยบริการการตลาดเพื่อเข้าถึงผู้ชมใหม่ๆ" },
    twitter: { title: "โปรโมตโปรไฟล์", highlight: "X ของคุณ", subtitle: "ขยายการเข้าถึงบน X ด้วยบริการโปรโมตสำหรับทวีต โปรไฟล์ และแคมเปญต่างๆ" },
    facebook: { title: "โปรโมตเพจ", highlight: "Facebook ของคุณ", subtitle: "เติบโตเพจ โพสต์ และวิดีโอ Facebook ของคุณด้วยบริการการตลาดเพื่อเข้าถึงผู้ชมที่กว้างขึ้น" },
  },
};

const hi: Translation = {
  ...en,
  nav: { trackOrder: "ऑर्डर ट्रैक करें", track: "ट्रैक" },
  hero: {
    title: "अपने TikTok को",
    titleHighlight: "बढ़ाएँ",
    subtitle: "पेशेवर सोशल मीडिया प्रमोशन सेवाएँ जो आपकी कंटेंट को अधिक लोगों तक पहुँचाती हैं।",
    placeholder: "अपना TikTok उपयोगकर्ता नाम दर्ज करें",
    cta: "शुरू करें",
    securePayments: "सुरक्षित भुगतान",
    fastProcessing: "तेज़ प्रोसेसिंग",
    noPassword: "पासवर्ड की आवश्यकता नहीं",
    noUsersFound: "कोई उपयोगकर्ता नहीं मिला",
    tryDifferent: "कोई दूसरा उपयोगकर्ता नाम आज़माएँ",
  },
  valueProp: {
    title: "क्यों चुनें",
    titleHighlight: "TokDadddy",
    subtitle: "TikTok कंटेंट प्रमोशन के लिए एक भरोसेमंद प्लेटफ़ॉर्म।",
    audienceGrowth: "ऑडियंस ग्रोथ",
    audienceGrowthDesc: "पेशेवर प्रमोशन सेवाएँ जो आपकी कंटेंट को अधिक दर्शकों तक पहुँचाती हैं।",
    fastProcessing: "तेज़ प्रोसेसिंग",
    fastProcessingDesc: "ऑर्डर तुरंत प्रोसेस होते हैं। परिणाम 24–72 घंटों में दिखाई देते हैं।",
    privacyFirst: "गोपनीयता पहले",
    privacyFirstDesc: "हम कभी आपके लॉगिन क्रेडेंशियल्स नहीं माँगते। आपकी सुरक्षा हमारी प्राथमिकता है।",
    securePayments: "सुरक्षित भुगतान",
    securePaymentsDesc: "कई भुगतान विकल्प। सभी लेनदेन एन्क्रिप्टेड और PCI-अनुपालक हैं।",
    orderTracking: "ऑर्डर ट्रैकिंग",
    orderTrackingDesc: "अपने ऑर्डर की स्थिति को रीयल-टाइम में मॉनिटर करें।",
    dedicatedSupport: "समर्पित सहायता",
    dedicatedSupportDesc: "हमारी टीम आपके किसी भी प्रश्न में मदद के लिए उपलब्ध है।",
  },
  howItWorks: {
    title: "यह कैसे काम करता है",
    subtitle: "2 मिनट से कम में शुरू करें। पासवर्ड की आवश्यकता नहीं — कभी नहीं।",
    step1Title: "अपनी प्रोफ़ाइल खोजें",
    step1Desc: "अपना TikTok उपयोगकर्ता नाम दर्ज करें और हम तुरंत आपकी प्रोफ़ाइल ढूँढेंगे।",
    step2Title: "सेवाएँ चुनें",
    step2Desc: "हमारे प्रमोशन पैकेजों में से चुनें और अपना ऑर्डर अनुकूलित करें।",
    step3Title: "सुरक्षित चेकआउट",
    step3Desc: "हमारे एन्क्रिप्टेड भुगतान सिस्टम से अपनी खरीदारी पूरी करें।",
    step4Title: "अपना ऑर्डर ट्रैक करें",
    step4Desc: "अपने ऑर्डर की प्रगति को रीयल-टाइम में मॉनिटर करें।",
    stat247: "24/7", statSSL: "SSL", statNo: "नहीं", statDelivery: "24–72घं",
    statSupportLabel: "ग्राहक सहायता",
    statEncryptedLabel: "एन्क्रिप्टेड भुगतान",
    statPasswordLabel: "पासवर्ड आवश्यक",
    statDeliveryLabel: "डिलीवरी समय",
  },
  footer: {
    faqTitle: "अक्सर पूछे जाने वाले प्रश्न",
    faq1Q: "क्या मेरा खाता सुरक्षित है?",
    faq1A: "बिल्कुल। हम कभी आपका पासवर्ड या लॉगिन क्रेडेंशियल नहीं माँगते। केवल सार्वजनिक उपयोगकर्ता नाम चाहिए।",
    faq2Q: "प्रोसेसिंग में कितना समय लगता है?",
    faq2A: "ऑर्डर तुरंत प्रोसेस होने लगते हैं। परिणाम सेवा के अनुसार 24–72 घंटों में दिखाई देते हैं।",
    faq3Q: "आप कौन से भुगतान तरीके स्वीकार करते हैं?",
    faq3A: "हम क्रेडिट/डेबिट कार्ड, PayPal और क्रिप्टोकरेंसी स्वीकार करते हैं। सभी लेनदेन एन्क्रिप्टेड हैं।",
    faq4Q: "आपकी रिफंड पॉलिसी क्या है?",
    faq4A: "हम न डिलीवर हुए ऑर्डर के लिए रिफंड देते हैं। विवरण के लिए hello@tokdadddy.com से संपर्क करें।",
    privacy: "गोपनीयता", terms: "शर्तें", refundPolicy: "रिफंड पॉलिसी", contact: "संपर्क",
    copyright: "tokdadddy.com सर्वाधिकार सुरक्षित। हम TikTok या ByteDance से संबद्ध नहीं हैं।",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe सुरक्षित", crypto: "क्रिप्टो", sslEncrypted: "SSL एन्क्रिप्टेड",
  },
  currency: { disclaimer: "USD में चार्ज किया गया। स्थानीय मूल्य केवल संदर्भ के लिए है।" },
  createOrder: {
    followers: "फ़ॉलोअर्स", likes: "लाइक्स", views: "व्यूज़", shares: "शेयर", comments: "टिप्पणियाँ",
    selectPostsNextStep: "(अगले चरण में पोस्ट चुनें)",
    total: "कुल", discount: "छूट", continue: "जारी रखें",
    contactUs: "किसी भी प्रश्न के लिए हमें संदेश भेजें",
    selected: "चयनित", posts: "पोस्ट",
  },
  order: {
    totalDueToday: "आज देय कुल", totalDue: "देय कुल",
    selectVideos: "प्रमोट करने के लिए वीडियो चुनें",
    selectVideosDesc: "वे वीडियो चुनें जिन्हें आप बूस्ट करना चाहते हैं",
    noVideosFound: "इस उपयोगकर्ता के लिए कोई वीडियो नहीं मिला।",
    continueWithVideos: "{count} वीडियो के साथ जारी रखें",
    selectAtLeastOne: "कम से कम एक वीडियो चुनें",
    oneTimeOffer: "एक बार का ऑफ़र 🔥",
    addFollowers: "केवल इतने में 5,000 TikTok फ़ॉलोअर्स जोड़ें",
    applied: "लागू", apply: "लागू करें",
    paymentFailed: "भुगतान असफल",
    paymentDeclined: "आपका भुगतान अस्वीकृत हुआ। नीचे दिए गए अन्य तरीके आज़माएँ।",
    tryCard: "💳 कार्ड आज़माएँ", tryStripe: "💳 Stripe आज़माएँ", tryCrypto: "₿ क्रिप्टो आज़माएँ",
    nameOnCard: "कार्ड पर नाम", cardNumber: "कार्ड नंबर", expiry: "समाप्ति", cvv: "CVV",
    processing: "प्रोसेसिंग...", pay: "भुगतान करें",
    stripeDesc: "Stripe Checkout के माध्यम से सुरक्षित भुगतान करें। कार्ड, Apple Pay, Google Pay आदि का समर्थन।",
    payViaStripe: "Stripe से भुगतान करें", redirectingStripe: "Stripe पर रीडायरेक्ट हो रहा है...",
    stripePowered: "🔒 Stripe द्वारा संचालित — PCI Level 1",
    cryptoDesc: "Bitcoin, USDT, ETH और 20+ अन्य क्रिप्टोकरेंसी से Cryptomus के माध्यम से भुगतान करें।",
    payWithCrypto: "क्रिप्टो से भुगतान करें", secureCryptomus: "🔒 Cryptomus के माध्यम से सुरक्षित भुगतान",
    agreeTerms: "आप सहमत हैं हमारी", terms: "शर्तें", refundPolicy: "रिफंड पॉलिसी",
    safeCheckout: "सुरक्षित चेकआउट", sslEncrypted: "SSL एन्क्रिप्टेड", guaranteed: "ट्रैक्ड डिलीवरी",
    backToVideos: "← वीडियो चयन पर वापस", videos: "वीडियो", bonus: "बोनस", selected: "चयनित",
    orderSummary: "ऑर्डर सारांश",
    minimumOrder: "न्यूनतम ऑर्डर $0.50 है",
    invalidCardNumber: "कृपया मान्य कार्ड नंबर दर्ज करें",
    invalidExpiry: "अमान्य समाप्ति तिथि (MM/YY)",
    invalidCvv: "अमान्य CVV",
    cardPaymentFailed: "कार्ड भुगतान विफल।",
    cardPaymentDeclined: "कार्ड भुगतान अस्वीकृत हुआ।",
    stripeError: "Stripe त्रुटि",
    stripePaymentFailed: "Stripe भुगतान विफल।",
    paypalFailed: "PayPal विफल।",
    cryptoPaymentFailed: "क्रिप्टो भुगतान विफल।",
    tryPayPal: "🅿️ PayPal आज़माएँ",
    cardLabel: "कार्ड", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "क्रिप्टो",
    stripeDescShort: "Stripe के माध्यम से सुरक्षित भुगतान — सभी प्रमुख क्रेडिट और डेबिट कार्ड समर्थित।",
    paypalDesc: "अपने PayPal खाते से सुरक्षित भुगतान — तेज़ और आसान चेकआउट।",
    payWithStripe: "Stripe से भुगतान करें",
    payWithPayPal: "PayPal से भुगतान करें",
  },
  thankYou: {
    awaitingPayment: "भुगतान की प्रतीक्षा",
    awaitingPaymentDesc: "आपका ऑर्डर बन गया है। हम भुगतान की पुष्टि की प्रतीक्षा कर रहे हैं।",
    thankYou: "धन्यवाद!", orderConfirmed: "आपका ऑर्डर पुष्टि हो गया है और प्रोसेस हो रहा है।",
    referenceNumber: "संदर्भ संख्या", copy: "कॉपी", copied: "कॉपी हो गया!",
    payment: "भुगतान", delivery: "डिलीवरी", services: "सेवाएँ", total: "कुल",
    whatsNext: "आगे क्या?",
    step1: "आपका ऑर्डर समीक्षा और प्रक्रिया के अधीन है",
    step2: "परिणाम धीरे-धीरे आपके खाते पर दिखने लगेंगे",
    step3: "पूर्ण डिलीवरी 24-72 घंटों में पूरी होती है",
    bookmark: "अपने ऑर्डर की स्थिति जाँचने के लिए इस पेज को बुकमार्क करें — लॉगिन की आवश्यकता नहीं।",
    placeAnother: "एक और ऑर्डर करें",
    pending: "लंबित", paid: "भुगतान किया", completed: "पूर्ण", processingStatus: "प्रोसेसिंग", delivered: "डिलीवर", failed: "असफल",
  },
  dashboard: {
    trackYourOrder: "अपना ऑर्डर ट्रैक करें",
    trackDesc: "अपनी संदर्भ संख्या दर्ज करें या नीचे अपने हाल के ऑर्डर देखें।",
    searchPlaceholder: "जैसे TD-ABC12345",
    noOrderFound: "उस संदर्भ संख्या के लिए कोई ऑर्डर नहीं मिला।",
    yourOrderHistory: "आपका ऑर्डर इतिहास", refresh: "रीफ़्रेश",
    noHistory: "अभी तक कोई ऑर्डर इतिहास नहीं। यहाँ देखने के लिए ऑर्डर करें!",
    placeAnother: "एक और ऑर्डर करें",
    bookmark: "स्थिति जाँचने के लिए इस पेज को बुकमार्क करें — लॉगिन की आवश्यकता नहीं।",
    whatsNext: "आगे क्या?",
    step1: "आपका ऑर्डर समीक्षा और प्रक्रिया के अधीन है",
    step2: "परिणाम धीरे-धीरे आपके खाते पर दिखने लगेंगे",
    step3: "पूर्ण डिलीवरी 24-72 घंटों में पूरी होती है",
  },
  heroByPlatform: {
    tiktok: { title: "अपनी", highlight: "TikTok कंटेंट प्रमोट करें", subtitle: "मार्केटिंग सेवाओं के साथ TikTok पर अपनी पहुँच बढ़ाएँ जो आपके वीडियो को खोजे जाने में मदद करती हैं।" },
    youtube: { title: "अपना", highlight: "YouTube चैनल बढ़ाएँ", subtitle: "अपने वीडियो और चैनल को मार्केटिंग सेवाओं से प्रमोट करें जो आपकी ऑडियंस का विस्तार करती हैं।" },
    spotify: { title: "अपना", highlight: "Spotify संगीत प्रमोट करें", subtitle: "कलाकारों और लेबलों के लिए प्रमोशन सेवाओं के साथ अपने ट्रैक को नए श्रोताओं तक पहुँचाएँ।" },
    instagram: { title: "अपनी", highlight: "Instagram उपस्थिति बढ़ाएँ", subtitle: "अपनी प्रोफ़ाइल, पोस्ट और रील्स को मार्केटिंग सेवाओं से प्रमोट करें।" },
    twitch: { title: "अपनी", highlight: "Twitch स्ट्रीम बढ़ाएँ", subtitle: "मार्केटिंग सेवाओं से अपने चैनल और स्ट्रीम को प्रमोट करें जो नए दर्शकों तक पहुँचने में मदद करती हैं।" },
    twitter: { title: "अपनी", highlight: "X प्रोफ़ाइल प्रमोट करें", subtitle: "ट्वीट, प्रोफ़ाइल और कंटेंट कैम्पेन के लिए प्रमोशन सेवाओं के साथ X पर अपनी पहुँच बढ़ाएँ।" },
    facebook: { title: "अपना", highlight: "Facebook पेज प्रमोट करें", subtitle: "अपना Facebook पेज, पोस्ट और वीडियो को व्यापक ऑडियंस तक पहुँचाने के लिए मार्केटिंग सेवाओं से बढ़ाएँ।" },
  },
};

const vi: Translation = {
  ...en,
  nav: { trackOrder: "Theo dõi đơn hàng", track: "Theo dõi" },
  hero: {
    title: "Phát triển",
    titleHighlight: "TikTok của bạn",
    subtitle: "Dịch vụ quảng bá mạng xã hội chuyên nghiệp giúp nội dung của bạn tiếp cận nhiều người hơn.",
    placeholder: "Nhập tên người dùng TikTok của bạn",
    cta: "Bắt đầu",
    securePayments: "Thanh toán an toàn",
    fastProcessing: "Xử lý nhanh chóng",
    noPassword: "Không cần mật khẩu",
    noUsersFound: "Không tìm thấy người dùng",
    tryDifferent: "Thử tên người dùng khác",
  },
  valueProp: {
    title: "Tại sao chọn",
    titleHighlight: "TokDadddy",
    subtitle: "Nền tảng đáng tin cậy để quảng bá nội dung TikTok và phát triển khán giả.",
    audienceGrowth: "Tăng trưởng khán giả",
    audienceGrowthDesc: "Dịch vụ quảng bá chuyên nghiệp giúp nội dung của bạn tiếp cận nhiều người xem hơn.",
    fastProcessing: "Xử lý nhanh chóng",
    fastProcessingDesc: "Đơn hàng được xử lý kịp thời. Kết quả thường thấy trong 24–72 giờ.",
    privacyFirst: "Quyền riêng tư trên hết",
    privacyFirstDesc: "Chúng tôi không bao giờ yêu cầu thông tin đăng nhập của bạn. An toàn tài khoản là ưu tiên.",
    securePayments: "Thanh toán an toàn",
    securePaymentsDesc: "Nhiều tùy chọn thanh toán. Tất cả giao dịch đều được mã hóa và tuân thủ PCI.",
    orderTracking: "Theo dõi đơn hàng",
    orderTrackingDesc: "Theo dõi trạng thái đơn hàng theo thời gian thực.",
    dedicatedSupport: "Hỗ trợ tận tâm",
    dedicatedSupportDesc: "Đội ngũ hỗ trợ luôn sẵn sàng giải đáp mọi thắc mắc.",
  },
  howItWorks: {
    title: "Cách thức hoạt động",
    subtitle: "Bắt đầu trong chưa đầy 2 phút. Không cần mật khẩu — không bao giờ.",
    step1Title: "Tìm hồ sơ của bạn",
    step1Desc: "Nhập tên người dùng TikTok và chúng tôi sẽ tìm hồ sơ ngay lập tức.",
    step2Title: "Chọn dịch vụ",
    step2Desc: "Chọn từ các gói quảng bá và tùy chỉnh đơn hàng của bạn.",
    step3Title: "Thanh toán an toàn",
    step3Desc: "Hoàn tất giao dịch với hệ thống thanh toán được mã hóa.",
    step4Title: "Theo dõi đơn hàng",
    step4Desc: "Giám sát tiến độ đơn hàng theo thời gian thực.",
    stat247: "24/7", statSSL: "SSL", statNo: "Không", statDelivery: "24–72 giờ",
    statSupportLabel: "Hỗ trợ khách hàng",
    statEncryptedLabel: "Thanh toán mã hóa",
    statPasswordLabel: "Yêu cầu mật khẩu",
    statDeliveryLabel: "Thời gian giao",
  },
  footer: {
    faqTitle: "Câu hỏi thường gặp",
    faq1Q: "Tài khoản của tôi có an toàn không?",
    faq1A: "Hoàn toàn an toàn. Chúng tôi không yêu cầu mật khẩu. Chỉ cần tên người dùng công khai.",
    faq2Q: "Quá trình xử lý mất bao lâu?",
    faq2A: "Đơn hàng được xử lý ngay. Kết quả thường thấy trong 24–72 giờ tùy dịch vụ.",
    faq3Q: "Bạn chấp nhận phương thức thanh toán nào?",
    faq3A: "Chúng tôi chấp nhận thẻ tín dụng/ghi nợ, PayPal và tiền mã hóa. Tất cả đều được mã hóa.",
    faq4Q: "Chính sách hoàn tiền của bạn là gì?",
    faq4A: "Chúng tôi hoàn tiền cho đơn hàng chưa giao. Liên hệ hello@tokdadddy.com để biết chi tiết.",
    privacy: "Quyền riêng tư", terms: "Điều khoản", refundPolicy: "Chính sách hoàn tiền", contact: "Liên hệ",
    copyright: "tokdadddy.com bảo lưu mọi quyền. Không liên kết với TikTok hoặc ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe an toàn", crypto: "Tiền mã hóa", sslEncrypted: "Mã hóa SSL",
  },
  currency: { disclaimer: "Tính phí bằng USD. Giá địa phương chỉ để tham khảo." },
  createOrder: {
    followers: "Người theo dõi", likes: "Lượt thích", views: "Lượt xem", shares: "Chia sẻ", comments: "Bình luận",
    selectPostsNextStep: "(chọn bài đăng ở bước tiếp theo)",
    total: "Tổng", discount: "Giảm giá", continue: "Tiếp tục",
    contactUs: "Nếu bạn có câu hỏi, hãy nhắn tin cho chúng tôi tại",
    selected: "Đã chọn", posts: "Bài đăng",
  },
  order: {
    totalDueToday: "Tổng phải trả hôm nay", totalDue: "Tổng phải trả",
    selectVideos: "Chọn video để quảng bá",
    selectVideosDesc: "Chọn video bạn muốn boost",
    noVideosFound: "Không tìm thấy video cho người dùng này.",
    continueWithVideos: "Tiếp tục với {count} video",
    selectAtLeastOne: "Chọn ít nhất một video",
    oneTimeOffer: "Ưu đãi một lần 🔥",
    addFollowers: "Thêm 5.000 người theo dõi TikTok chỉ với",
    applied: "Đã áp dụng", apply: "Áp dụng",
    paymentFailed: "Thanh toán thất bại",
    paymentDeclined: "Thanh toán bị từ chối. Vui lòng thử phương thức khác bên dưới.",
    tryCard: "💳 Thử thẻ", tryStripe: "💳 Thử Stripe", tryCrypto: "₿ Thử Crypto",
    nameOnCard: "Tên trên thẻ", cardNumber: "Số thẻ", expiry: "Hết hạn", cvv: "CVV",
    processing: "Đang xử lý...", pay: "Thanh toán",
    stripeDesc: "Thanh toán an toàn qua Stripe Checkout. Hỗ trợ thẻ, Apple Pay, Google Pay.",
    payViaStripe: "Thanh toán qua Stripe", redirectingStripe: "Đang chuyển đến Stripe...",
    stripePowered: "🔒 Powered by Stripe — PCI Level 1",
    cryptoDesc: "Thanh toán bằng Bitcoin, USDT, ETH và 20+ tiền mã hóa khác qua Cryptomus.",
    payWithCrypto: "Thanh toán bằng Crypto", secureCryptomus: "🔒 Thanh toán an toàn qua Cryptomus",
    agreeTerms: "Bạn đồng ý với", terms: "Điều khoản", refundPolicy: "Chính sách hoàn tiền",
    safeCheckout: "Thanh toán an toàn", sslEncrypted: "Mã hóa SSL", guaranteed: "Giao hàng có theo dõi",
    backToVideos: "← Quay lại chọn video", videos: "video", bonus: "Thưởng", selected: "đã chọn",
    orderSummary: "Tóm tắt đơn hàng",
    minimumOrder: "Đơn hàng tối thiểu là $0.50",
    invalidCardNumber: "Vui lòng nhập số thẻ hợp lệ",
    invalidExpiry: "Ngày hết hạn không hợp lệ (MM/YY)",
    invalidCvv: "CVV không hợp lệ",
    cardPaymentFailed: "Thanh toán bằng thẻ thất bại.",
    cardPaymentDeclined: "Thanh toán bằng thẻ bị từ chối.",
    stripeError: "Lỗi Stripe",
    stripePaymentFailed: "Thanh toán Stripe thất bại.",
    paypalFailed: "PayPal thất bại.",
    cryptoPaymentFailed: "Thanh toán crypto thất bại.",
    tryPayPal: "🅿️ Thử PayPal",
    cardLabel: "Thẻ", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Crypto",
    stripeDescShort: "Thanh toán an toàn qua Stripe — hỗ trợ tất cả các thẻ tín dụng và ghi nợ chính.",
    paypalDesc: "Thanh toán an toàn bằng tài khoản PayPal — checkout nhanh và dễ dàng.",
    payWithStripe: "Thanh toán bằng Stripe",
    payWithPayPal: "Thanh toán bằng PayPal",
  },
  thankYou: {
    awaitingPayment: "Chờ thanh toán",
    awaitingPaymentDesc: "Đơn hàng đã tạo. Chúng tôi đang chờ xác nhận thanh toán.",
    thankYou: "Cảm ơn bạn!", orderConfirmed: "Đơn hàng đã xác nhận và đang được xử lý.",
    referenceNumber: "Số tham chiếu", copy: "Sao chép", copied: "Đã sao chép!",
    payment: "Thanh toán", delivery: "Giao hàng", services: "Dịch vụ", total: "Tổng",
    whatsNext: "Tiếp theo là gì?",
    step1: "Đơn hàng đang được xem xét và xử lý",
    step2: "Kết quả sẽ dần xuất hiện trên tài khoản của bạn",
    step3: "Giao hàng đầy đủ trong 24-72 giờ",
    bookmark: "Đánh dấu trang này để kiểm tra trạng thái — không cần đăng nhập.",
    placeAnother: "Đặt đơn khác",
    pending: "Chờ", paid: "Đã thanh toán", completed: "Hoàn tất", processingStatus: "Đang xử lý", delivered: "Đã giao", failed: "Thất bại",
  },
  dashboard: {
    trackYourOrder: "Theo dõi đơn hàng",
    trackDesc: "Nhập số tham chiếu hoặc xem các đơn hàng gần đây bên dưới.",
    searchPlaceholder: "ví dụ TD-ABC12345",
    noOrderFound: "Không tìm thấy đơn hàng cho số tham chiếu đó.",
    yourOrderHistory: "Lịch sử đơn hàng", refresh: "Làm mới",
    noHistory: "Chưa có lịch sử đơn hàng. Đặt đơn để xem ở đây!",
    placeAnother: "Đặt đơn khác",
    bookmark: "Đánh dấu trang này để kiểm tra trạng thái — không cần đăng nhập.",
    whatsNext: "Tiếp theo là gì?",
    step1: "Đơn hàng đang được xem xét và xử lý",
    step2: "Kết quả sẽ dần xuất hiện trên tài khoản của bạn",
    step3: "Giao hàng đầy đủ trong 24-72 giờ",
  },
  heroByPlatform: {
    tiktok: { title: "Quảng bá", highlight: "nội dung TikTok của bạn", subtitle: "Mở rộng phạm vi tiếp cận trên TikTok với dịch vụ marketing giúp video của bạn được khám phá." },
    youtube: { title: "Phát triển", highlight: "kênh YouTube của bạn", subtitle: "Quảng bá video và kênh với dịch vụ marketing được thiết kế để mở rộng khán giả." },
    spotify: { title: "Quảng bá", highlight: "âm nhạc Spotify của bạn", subtitle: "Đưa các bản nhạc đến với người nghe mới với dịch vụ quảng bá cho nghệ sĩ và hãng đĩa." },
    instagram: { title: "Tăng cường", highlight: "sự hiện diện Instagram", subtitle: "Quảng bá hồ sơ, bài đăng và reels với dịch vụ marketing để mở rộng khán giả." },
    twitch: { title: "Phát triển", highlight: "stream Twitch của bạn", subtitle: "Quảng bá kênh và stream với dịch vụ marketing giúp tiếp cận người xem mới." },
    twitter: { title: "Quảng bá", highlight: "hồ sơ X của bạn", subtitle: "Mở rộng phạm vi trên X với dịch vụ quảng bá tweet, hồ sơ và chiến dịch nội dung." },
    facebook: { title: "Quảng bá", highlight: "trang Facebook của bạn", subtitle: "Phát triển trang Facebook, bài đăng và video với dịch vụ marketing để tiếp cận khán giả rộng hơn." },
  },
};

const id: Translation = {
  ...en,
  nav: { trackOrder: "Lacak Pesanan", track: "Lacak" },
  hero: {
    title: "Kembangkan",
    titleHighlight: "TikTok Anda",
    subtitle: "Layanan promosi media sosial profesional untuk membantu konten Anda menjangkau lebih banyak orang.",
    placeholder: "Masukkan nama pengguna TikTok Anda",
    cta: "Mulai",
    securePayments: "Pembayaran Aman",
    fastProcessing: "Proses Cepat",
    noPassword: "Tanpa Kata Sandi",
    noUsersFound: "Pengguna tidak ditemukan",
    tryDifferent: "Coba nama pengguna lain",
  },
  valueProp: {
    title: "Mengapa Memilih",
    titleHighlight: "TokDadddy",
    subtitle: "Platform terpercaya untuk promosi konten TikTok dan pengembangan audiens.",
    audienceGrowth: "Pertumbuhan Audiens",
    audienceGrowthDesc: "Layanan promosi profesional dirancang untuk menjangkau lebih banyak penonton.",
    fastProcessing: "Proses Cepat",
    fastProcessingDesc: "Pesanan diproses dengan cepat. Hasil terlihat dalam 24–72 jam.",
    privacyFirst: "Privasi Utama",
    privacyFirstDesc: "Kami tidak pernah meminta kredensial login Anda. Keamanan akun adalah prioritas.",
    securePayments: "Pembayaran Aman",
    securePaymentsDesc: "Banyak opsi pembayaran. Semua transaksi terenkripsi dan sesuai PCI.",
    orderTracking: "Pelacakan Pesanan",
    orderTrackingDesc: "Pantau status pesanan Anda secara real-time.",
    dedicatedSupport: "Dukungan Khusus",
    dedicatedSupportDesc: "Tim kami siap membantu setiap pertanyaan Anda.",
  },
  howItWorks: {
    title: "Cara Kerjanya",
    subtitle: "Mulai dalam kurang dari 2 menit. Tanpa kata sandi — selamanya.",
    step1Title: "Temukan Profil Anda",
    step1Desc: "Masukkan nama pengguna TikTok dan kami akan menemukan profil Anda secara instan.",
    step2Title: "Pilih Layanan",
    step2Desc: "Pilih dari paket promosi kami dan sesuaikan pesanan Anda.",
    step3Title: "Checkout Aman",
    step3Desc: "Selesaikan pembelian dengan sistem pembayaran terenkripsi.",
    step4Title: "Lacak Pesanan",
    step4Desc: "Pantau kemajuan pesanan Anda secara real-time.",
    stat247: "24/7", statSSL: "SSL", statNo: "Tanpa", statDelivery: "24–72j",
    statSupportLabel: "Dukungan Pelanggan",
    statEncryptedLabel: "Pembayaran Terenkripsi",
    statPasswordLabel: "Kata Sandi Diperlukan",
    statDeliveryLabel: "Waktu Pengiriman",
  },
  footer: {
    faqTitle: "Pertanyaan yang Sering Diajukan",
    faq1Q: "Apakah akun saya aman?",
    faq1A: "Tentu saja. Kami tidak meminta kata sandi atau kredensial login. Hanya nama pengguna publik.",
    faq2Q: "Berapa lama proses pengerjaannya?",
    faq2A: "Pesanan diproses segera. Hasil biasanya terlihat dalam 24–72 jam tergantung layanan.",
    faq3Q: "Metode pembayaran apa yang Anda terima?",
    faq3A: "Kami menerima kartu kredit/debit, PayPal, dan kripto. Semua transaksi terenkripsi.",
    faq4Q: "Apa kebijakan pengembalian dana Anda?",
    faq4A: "Kami menawarkan refund untuk pesanan yang tidak terkirim. Hubungi hello@tokdadddy.com.",
    privacy: "Privasi", terms: "Ketentuan", refundPolicy: "Kebijakan Refund", contact: "Kontak",
    copyright: "tokdadddy.com hak cipta dilindungi. Tidak berafiliasi dengan TikTok atau ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe Aman", crypto: "Kripto", sslEncrypted: "Terenkripsi SSL",
  },
  currency: { disclaimer: "Ditagih dalam USD. Harga lokal hanya untuk referensi." },
  createOrder: {
    followers: "Pengikut", likes: "Suka", views: "Tontonan", shares: "Bagikan", comments: "Komentar",
    selectPostsNextStep: "(pilih postingan di langkah berikutnya)",
    total: "Total", discount: "Diskon", continue: "Lanjutkan",
    contactUs: "Jika ada pertanyaan, kirim pesan kapan saja ke",
    selected: "Dipilih", posts: "Postingan",
  },
  order: {
    totalDueToday: "Total Hari Ini", totalDue: "Total Tagihan",
    selectVideos: "Pilih video untuk dipromosikan",
    selectVideosDesc: "Pilih video yang ingin Anda boost",
    noVideosFound: "Tidak ada video untuk pengguna ini.",
    continueWithVideos: "Lanjutkan dengan {count} video",
    selectAtLeastOne: "Pilih minimal satu video",
    oneTimeOffer: "Penawaran Sekali 🔥",
    addFollowers: "Tambah 5.000 Pengikut TikTok hanya dengan",
    applied: "Diterapkan", apply: "Terapkan",
    paymentFailed: "Pembayaran gagal",
    paymentDeclined: "Pembayaran ditolak. Silakan coba metode lain di bawah.",
    tryCard: "💳 Coba Kartu", tryStripe: "💳 Coba Stripe", tryCrypto: "₿ Coba Kripto",
    nameOnCard: "Nama di Kartu", cardNumber: "Nomor Kartu", expiry: "Kadaluarsa", cvv: "CVV",
    processing: "Memproses...", pay: "Bayar",
    stripeDesc: "Bayar aman via Stripe Checkout. Mendukung kartu, Apple Pay, Google Pay, dll.",
    payViaStripe: "Bayar via Stripe", redirectingStripe: "Mengalihkan ke Stripe...",
    stripePowered: "🔒 Didukung oleh Stripe — PCI Level 1",
    cryptoDesc: "Bayar dengan Bitcoin, USDT, ETH, dan 20+ kripto lain via Cryptomus.",
    payWithCrypto: "Bayar dengan Kripto", secureCryptomus: "🔒 Pembayaran aman via Cryptomus",
    agreeTerms: "Anda menyetujui", terms: "Ketentuan", refundPolicy: "Kebijakan Refund",
    safeCheckout: "Checkout Aman", sslEncrypted: "Terenkripsi SSL", guaranteed: "Pengiriman Terlacak",
    backToVideos: "← Kembali ke pemilihan video", videos: "video", bonus: "Bonus", selected: "dipilih",
    orderSummary: "Ringkasan Pesanan",
    minimumOrder: "Pesanan minimum adalah $0.50",
    invalidCardNumber: "Masukkan nomor kartu yang valid",
    invalidExpiry: "Tanggal kedaluwarsa tidak valid (MM/YY)",
    invalidCvv: "CVV tidak valid",
    cardPaymentFailed: "Pembayaran kartu gagal.",
    cardPaymentDeclined: "Pembayaran kartu ditolak.",
    stripeError: "Kesalahan Stripe",
    stripePaymentFailed: "Pembayaran Stripe gagal.",
    paypalFailed: "PayPal gagal.",
    cryptoPaymentFailed: "Pembayaran kripto gagal.",
    tryPayPal: "🅿️ Coba PayPal",
    cardLabel: "Kartu", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Kripto",
    stripeDescShort: "Bayar dengan aman melalui Stripe — mendukung semua kartu kredit dan debit utama.",
    paypalDesc: "Bayar dengan aman menggunakan akun PayPal Anda — checkout cepat dan mudah.",
    payWithStripe: "Bayar dengan Stripe",
    payWithPayPal: "Bayar dengan PayPal",
  },
  thankYou: {
    awaitingPayment: "Menunggu Pembayaran",
    awaitingPaymentDesc: "Pesanan Anda telah dibuat. Kami menunggu konfirmasi pembayaran.",
    thankYou: "Terima Kasih!", orderConfirmed: "Pesanan dikonfirmasi dan sedang diproses.",
    referenceNumber: "Nomor Referensi", copy: "Salin", copied: "Tersalin!",
    payment: "Pembayaran", delivery: "Pengiriman", services: "Layanan", total: "Total",
    whatsNext: "Apa selanjutnya?",
    step1: "Pesanan sedang ditinjau dan diproses",
    step2: "Hasil akan mulai muncul di akun Anda secara bertahap",
    step3: "Pengiriman lengkap dalam 24-72 jam",
    bookmark: "Bookmark halaman ini untuk cek status kapan saja — tanpa login.",
    placeAnother: "Pesan Lagi",
    pending: "Tertunda", paid: "Dibayar", completed: "Selesai", processingStatus: "Memproses", delivered: "Terkirim", failed: "Gagal",
  },
  dashboard: {
    trackYourOrder: "Lacak Pesanan Anda",
    trackDesc: "Masukkan nomor referensi atau lihat pesanan terbaru di bawah.",
    searchPlaceholder: "mis. TD-ABC12345",
    noOrderFound: "Tidak ada pesanan untuk nomor referensi tersebut.",
    yourOrderHistory: "Riwayat Pesanan", refresh: "Refresh",
    noHistory: "Belum ada riwayat pesanan. Pesan untuk melihatnya di sini!",
    placeAnother: "Pesan Lagi",
    bookmark: "Bookmark halaman ini untuk cek status — tanpa login.",
    whatsNext: "Apa selanjutnya?",
    step1: "Pesanan sedang ditinjau dan diproses",
    step2: "Hasil akan mulai muncul di akun Anda secara bertahap",
    step3: "Pengiriman lengkap dalam 24-72 jam",
  },
  heroByPlatform: {
    tiktok: { title: "Promosikan", highlight: "konten TikTok Anda", subtitle: "Tingkatkan jangkauan di TikTok dengan layanan marketing yang membantu video Anda ditemukan." },
    youtube: { title: "Kembangkan", highlight: "channel YouTube Anda", subtitle: "Promosikan video dan channel dengan layanan marketing untuk memperluas audiens." },
    spotify: { title: "Promosikan", highlight: "musik Spotify Anda", subtitle: "Sajikan track Anda kepada pendengar baru dengan layanan promosi untuk artis dan label." },
    instagram: { title: "Tingkatkan", highlight: "kehadiran Instagram", subtitle: "Promosikan profil, postingan, dan reels dengan layanan marketing untuk memperluas audiens." },
    twitch: { title: "Kembangkan", highlight: "stream Twitch Anda", subtitle: "Promosikan channel dan stream dengan layanan marketing untuk menjangkau penonton baru." },
    twitter: { title: "Promosikan", highlight: "profil X Anda", subtitle: "Perluas jangkauan di X dengan layanan promosi untuk tweet, profil, dan kampanye konten." },
    facebook: { title: "Promosikan", highlight: "halaman Facebook Anda", subtitle: "Kembangkan halaman, postingan, dan video Facebook dengan layanan marketing untuk menjangkau audiens lebih luas." },
  },
};

const tr: Translation = {
  ...en,
  nav: { trackOrder: "Sipariş Takip", track: "Takip" },
  hero: {
    title: "TikTok'unuzu",
    titleHighlight: "Büyütün",
    subtitle: "İçeriğinizin daha fazla kişiye ulaşmasını sağlayan profesyonel sosyal medya tanıtım hizmetleri.",
    placeholder: "TikTok kullanıcı adınızı girin",
    cta: "Başla",
    securePayments: "Güvenli Ödeme",
    fastProcessing: "Hızlı İşlem",
    noPassword: "Şifre Gerekmez",
    noUsersFound: "Kullanıcı bulunamadı",
    tryDifferent: "Farklı bir kullanıcı adı deneyin",
  },
  valueProp: {
    title: "Neden",
    titleHighlight: "TokDadddy",
    subtitle: "TikTok içerik tanıtımı ve kitle geliştirme için güvenilir bir platform.",
    audienceGrowth: "Kitle Büyümesi",
    audienceGrowthDesc: "İçeriğinizi daha fazla izleyiciye ulaştıran profesyonel tanıtım hizmetleri.",
    fastProcessing: "Hızlı İşlem",
    fastProcessingDesc: "Siparişler hemen işlenir. Sonuçlar genellikle 24–72 saat içinde görülür.",
    privacyFirst: "Önce Gizlilik",
    privacyFirstDesc: "Asla giriş bilgilerinizi istemiyoruz. Hesap güvenliği önceliğimizdir.",
    securePayments: "Güvenli Ödeme",
    securePaymentsDesc: "Birden fazla ödeme seçeneği. Tüm işlemler şifreli ve PCI uyumludur.",
    orderTracking: "Sipariş Takibi",
    orderTrackingDesc: "Sipariş durumunuzu gerçek zamanlı izleyin.",
    dedicatedSupport: "Özel Destek",
    dedicatedSupportDesc: "Destek ekibimiz tüm sorularınızda yardımcı olmaya hazır.",
  },
  howItWorks: {
    title: "Nasıl Çalışır",
    subtitle: "2 dakikadan kısa sürede başlayın. Şifre gerekmez — asla.",
    step1Title: "Profilinizi Bulun",
    step1Desc: "TikTok kullanıcı adınızı girin, profilinizi anında buluruz.",
    step2Title: "Hizmetleri Seçin",
    step2Desc: "Tanıtım paketlerimizden birini seçin ve siparişinizi özelleştirin.",
    step3Title: "Güvenli Ödeme",
    step3Desc: "Şifreli ödeme sistemimizle satın almanızı tamamlayın.",
    step4Title: "Siparişi Takip Edin",
    step4Desc: "Sipariş ilerlemesini gerçek zamanlı izleyin.",
    stat247: "24/7", statSSL: "SSL", statNo: "Yok", statDelivery: "24–72sa",
    statSupportLabel: "Müşteri Desteği",
    statEncryptedLabel: "Şifreli Ödeme",
    statPasswordLabel: "Şifre Gerekli",
    statDeliveryLabel: "Teslim Süresi",
  },
  footer: {
    faqTitle: "Sıkça Sorulan Sorular",
    faq1Q: "Hesabım güvende mi?",
    faq1A: "Kesinlikle. Şifre veya giriş bilgilerinizi asla istemiyoruz. Sadece genel kullanıcı adınız yeterli.",
    faq2Q: "İşlem ne kadar sürer?",
    faq2A: "Siparişler hemen işlenir. Sonuçlar hizmete bağlı olarak 24–72 saat içinde görülür.",
    faq3Q: "Hangi ödeme yöntemlerini kabul ediyorsunuz?",
    faq3A: "Kredi/banka kartları, PayPal ve kripto para kabul ediyoruz. Tüm işlemler şifrelidir.",
    faq4Q: "İade politikanız nedir?",
    faq4A: "Teslim edilmeyen siparişler için iade sunuyoruz. Detaylar için hello@tokdadddy.com.",
    privacy: "Gizlilik", terms: "Şartlar", refundPolicy: "İade Politikası", contact: "İletişim",
    copyright: "tokdadddy.com tüm hakları saklıdır. TikTok veya ByteDance ile bağlantılı değildir.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe Güvenli", crypto: "Kripto", sslEncrypted: "SSL Şifreli",
  },
  currency: { disclaimer: "USD olarak ücretlendirilir. Yerel fiyat sadece referans amaçlıdır." },
  createOrder: {
    followers: "Takipçi", likes: "Beğeni", views: "Görüntüleme", shares: "Paylaşım", comments: "Yorum",
    selectPostsNextStep: "(sonraki adımda gönderileri seçin)",
    total: "Toplam", discount: "İndirim", continue: "Devam",
    contactUs: "Sorularınız için bize her zaman mesaj gönderebilirsiniz",
    selected: "Seçildi", posts: "Gönderi",
  },
  order: {
    totalDueToday: "Bugün Ödenecek", totalDue: "Toplam Borç",
    selectVideos: "Tanıtılacak videoları seçin",
    selectVideosDesc: "Hangi videoları boost etmek istediğinizi seçin",
    noVideosFound: "Bu kullanıcı için video bulunamadı.",
    continueWithVideos: "{count} video ile devam et",
    selectAtLeastOne: "En az bir video seçin",
    oneTimeOffer: "Tek Seferlik Teklif 🔥",
    addFollowers: "Sadece bu fiyata 5.000 TikTok Takipçi ekleyin",
    applied: "Uygulandı", apply: "Uygula",
    paymentFailed: "Ödeme başarısız",
    paymentDeclined: "Ödemeniz reddedildi. Lütfen aşağıdaki diğer yöntemleri deneyin.",
    tryCard: "💳 Kart Dene", tryStripe: "💳 Stripe Dene", tryCrypto: "₿ Kripto Dene",
    nameOnCard: "Kart Üzerindeki İsim", cardNumber: "Kart Numarası", expiry: "Son Kullanma", cvv: "CVV",
    processing: "İşleniyor...", pay: "Öde",
    stripeDesc: "Stripe Checkout ile güvenli ödeyin. Kart, Apple Pay, Google Pay ve daha fazlasını destekler.",
    payViaStripe: "Stripe ile Öde", redirectingStripe: "Stripe'a yönlendiriliyor...",
    stripePowered: "🔒 Stripe ile güçlendirildi — PCI Level 1",
    cryptoDesc: "Cryptomus üzerinden Bitcoin, USDT, ETH ve 20+ kripto ile ödeyin.",
    payWithCrypto: "Kripto ile Öde", secureCryptomus: "🔒 Cryptomus ile güvenli ödeme",
    agreeTerms: "Şunu kabul ediyorsunuz", terms: "Şartlar", refundPolicy: "İade Politikası",
    safeCheckout: "Güvenli Ödeme", sslEncrypted: "SSL Şifreli", guaranteed: "Takipli Teslimat",
    backToVideos: "← Video seçimine geri dön", videos: "video", bonus: "Bonus", selected: "seçildi",
    orderSummary: "Sipariş Özeti",
    minimumOrder: "Minimum sipariş $0.50",
    invalidCardNumber: "Geçerli bir kart numarası girin",
    invalidExpiry: "Geçersiz son kullanma tarihi (AA/YY)",
    invalidCvv: "Geçersiz CVV",
    cardPaymentFailed: "Kart ödemesi başarısız.",
    cardPaymentDeclined: "Kart ödemesi reddedildi.",
    stripeError: "Stripe hatası",
    stripePaymentFailed: "Stripe ödemesi başarısız.",
    paypalFailed: "PayPal başarısız.",
    cryptoPaymentFailed: "Kripto ödemesi başarısız.",
    tryPayPal: "🅿️ PayPal Dene",
    cardLabel: "Kart", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Kripto",
    stripeDescShort: "Stripe ile güvenli ödeme — tüm büyük kredi ve banka kartlarını destekler.",
    paypalDesc: "PayPal hesabınızla güvenli ödeme — hızlı ve kolay checkout.",
    payWithStripe: "Stripe ile Öde",
    payWithPayPal: "PayPal ile Öde",
  },
  thankYou: {
    awaitingPayment: "Ödeme Bekleniyor",
    awaitingPaymentDesc: "Siparişiniz oluşturuldu. Ödeme onayını bekliyoruz.",
    thankYou: "Teşekkürler!", orderConfirmed: "Siparişiniz onaylandı ve işleniyor.",
    referenceNumber: "Referans Numarası", copy: "Kopyala", copied: "Kopyalandı!",
    payment: "Ödeme", delivery: "Teslimat", services: "Hizmetler", total: "Toplam",
    whatsNext: "Sırada ne var?",
    step1: "Siparişiniz inceleniyor ve işleniyor",
    step2: "Sonuçlar hesabınızda kademeli olarak görünmeye başlayacak",
    step3: "Tam teslimat 24-72 saat içinde tamamlanır",
    bookmark: "Sipariş durumunu kontrol etmek için bu sayfayı yer imlerine ekleyin — giriş gerekmez.",
    placeAnother: "Başka Sipariş Ver",
    pending: "Beklemede", paid: "Ödendi", completed: "Tamamlandı", processingStatus: "İşleniyor", delivered: "Teslim Edildi", failed: "Başarısız",
  },
  dashboard: {
    trackYourOrder: "Siparişinizi Takip Edin",
    trackDesc: "Referans numaranızı girin veya son siparişlerinizi aşağıda görüntüleyin.",
    searchPlaceholder: "örn. TD-ABC12345",
    noOrderFound: "Bu referans numarası için sipariş bulunamadı.",
    yourOrderHistory: "Sipariş Geçmişiniz", refresh: "Yenile",
    noHistory: "Henüz sipariş geçmişi yok. Burada görmek için sipariş verin!",
    placeAnother: "Başka Sipariş Ver",
    bookmark: "Durumu kontrol etmek için bu sayfayı yer imlerine ekleyin — giriş gerekmez.",
    whatsNext: "Sırada ne var?",
    step1: "Siparişiniz inceleniyor ve işleniyor",
    step2: "Sonuçlar hesabınızda kademeli olarak görünmeye başlayacak",
    step3: "Tam teslimat 24-72 saat içinde tamamlanır",
  },
  heroByPlatform: {
    tiktok: { title: "Tanıtın", highlight: "TikTok içeriğinizi", subtitle: "Videolarınızın keşfedilmesini sağlayan pazarlama hizmetleriyle TikTok'taki erişiminizi artırın." },
    youtube: { title: "Büyütün", highlight: "YouTube kanalınızı", subtitle: "Kitlenizi genişletmek için tasarlanmış pazarlama hizmetleriyle videolarınızı ve kanalınızı tanıtın." },
    spotify: { title: "Tanıtın", highlight: "Spotify müziğinizi", subtitle: "Sanatçılar ve plak şirketleri için tanıtım hizmetleriyle parçalarınızı yeni dinleyicilere ulaştırın." },
    instagram: { title: "Güçlendirin", highlight: "Instagram varlığınızı", subtitle: "Kitlenizi genişletmek için profilinizi, gönderilerinizi ve reels'lerinizi pazarlama hizmetleriyle tanıtın." },
    twitch: { title: "Büyütün", highlight: "Twitch yayınınızı", subtitle: "Yeni izleyicilere ulaşmanıza yardımcı olan pazarlama hizmetleriyle kanalınızı ve yayınlarınızı tanıtın." },
    twitter: { title: "Tanıtın", highlight: "X profilinizi", subtitle: "Tweet, profil ve içerik kampanyaları için tanıtım hizmetleriyle X'teki erişiminizi genişletin." },
    facebook: { title: "Tanıtın", highlight: "Facebook sayfanızı", subtitle: "Daha geniş bir kitleye ulaşmak için pazarlama hizmetleriyle Facebook sayfanızı, gönderilerinizi ve videolarınızı büyütün." },
  },
};

const ru: Translation = {
  ...en,
  nav: { trackOrder: "Отследить заказ", track: "Отследить" },
  hero: {
    title: "Развивайте свой",
    titleHighlight: "TikTok",
    subtitle: "Профессиональные услуги продвижения в социальных сетях, помогающие вашему контенту охватить больше людей.",
    placeholder: "Введите ваше имя пользователя TikTok",
    cta: "Начать",
    securePayments: "Безопасные платежи",
    fastProcessing: "Быстрая обработка",
    noPassword: "Без пароля",
    noUsersFound: "Пользователи не найдены",
    tryDifferent: "Попробуйте другое имя пользователя",
  },
  valueProp: {
    title: "Почему выбирают",
    titleHighlight: "TokDadddy",
    subtitle: "Надёжная платформа для продвижения контента TikTok и развития аудитории.",
    audienceGrowth: "Рост аудитории",
    audienceGrowthDesc: "Профессиональные услуги продвижения, помогающие достичь большего количества зрителей.",
    fastProcessing: "Быстрая обработка",
    fastProcessingDesc: "Заказы обрабатываются оперативно. Результаты обычно видны в течение 24–72 часов.",
    privacyFirst: "Конфиденциальность прежде всего",
    privacyFirstDesc: "Мы никогда не запрашиваем ваши учётные данные. Безопасность аккаунта — наш приоритет.",
    securePayments: "Безопасные платежи",
    securePaymentsDesc: "Множество способов оплаты. Все транзакции зашифрованы и соответствуют PCI.",
    orderTracking: "Отслеживание заказа",
    orderTrackingDesc: "Контролируйте статус заказа в реальном времени.",
    dedicatedSupport: "Персональная поддержка",
    dedicatedSupportDesc: "Наша команда поддержки готова помочь с любыми вопросами.",
  },
  howItWorks: {
    title: "Как это работает",
    subtitle: "Начните менее чем за 2 минуты. Без пароля — никогда.",
    step1Title: "Найдите свой профиль",
    step1Desc: "Введите имя пользователя TikTok, и мы мгновенно найдём ваш профиль.",
    step2Title: "Выберите услуги",
    step2Desc: "Выберите из наших пакетов продвижения и настройте свой заказ.",
    step3Title: "Безопасная оплата",
    step3Desc: "Завершите покупку с помощью нашей зашифрованной платёжной системы.",
    step4Title: "Отслеживайте заказ",
    step4Desc: "Контролируйте ход выполнения заказа в реальном времени.",
    stat247: "24/7", statSSL: "SSL", statNo: "Нет", statDelivery: "24–72ч",
    statSupportLabel: "Поддержка клиентов",
    statEncryptedLabel: "Зашифрованные платежи",
    statPasswordLabel: "Требуется пароль",
    statDeliveryLabel: "Время доставки",
  },
  footer: {
    faqTitle: "Часто задаваемые вопросы",
    faq1Q: "Безопасен ли мой аккаунт?",
    faq1A: "Абсолютно. Мы никогда не запрашиваем пароль или учётные данные. Нужно только публичное имя пользователя.",
    faq2Q: "Сколько времени занимает обработка?",
    faq2A: "Заказы начинают обрабатываться сразу. Результаты обычно видны в течение 24–72 часов.",
    faq3Q: "Какие способы оплаты вы принимаете?",
    faq3A: "Мы принимаем кредитные/дебетовые карты, PayPal и криптовалюту. Все транзакции зашифрованы.",
    faq4Q: "Какова ваша политика возврата?",
    faq4A: "Мы предлагаем возврат за невыполненные заказы. Свяжитесь с hello@tokdadddy.com для деталей.",
    privacy: "Конфиденциальность", terms: "Условия", refundPolicy: "Политика возврата", contact: "Контакты",
    copyright: "tokdadddy.com все права защищены. Не связано с TikTok или ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe безопасно", crypto: "Крипто", sslEncrypted: "SSL шифрование",
  },
  currency: { disclaimer: "Оплата в USD. Местная цена только для справки." },
  createOrder: {
    followers: "Подписчики", likes: "Лайки", views: "Просмотры", shares: "Репосты", comments: "Комментарии",
    selectPostsNextStep: "(выберите посты на следующем шаге)",
    total: "Итого", discount: "Скидка", continue: "Продолжить",
    contactUs: "Если у вас есть вопросы, напишите нам в любое время на",
    selected: "Выбрано", posts: "Посты",
  },
  order: {
    totalDueToday: "К оплате сегодня", totalDue: "К оплате",
    selectVideos: "Выберите видео для продвижения",
    selectVideosDesc: "Выберите, какие видео вы хотите продвинуть",
    noVideosFound: "Видео для этого пользователя не найдены.",
    continueWithVideos: "Продолжить с {count} видео",
    selectAtLeastOne: "Выберите хотя бы одно видео",
    oneTimeOffer: "Разовое предложение 🔥",
    addFollowers: "Добавьте 5 000 подписчиков TikTok всего за",
    applied: "Применено", apply: "Применить",
    paymentFailed: "Платёж не выполнен",
    paymentDeclined: "Ваш платёж отклонён. Пожалуйста, попробуйте другие способы оплаты ниже.",
    tryCard: "💳 Попробовать карту", tryStripe: "💳 Попробовать Stripe", tryCrypto: "₿ Попробовать крипто",
    nameOnCard: "Имя на карте", cardNumber: "Номер карты", expiry: "Срок действия", cvv: "CVV",
    processing: "Обработка...", pay: "Оплатить",
    stripeDesc: "Безопасная оплата через Stripe Checkout. Поддерживает карты, Apple Pay, Google Pay и др.",
    payViaStripe: "Оплатить через Stripe", redirectingStripe: "Перенаправление в Stripe...",
    stripePowered: "🔒 Работает на Stripe — PCI Level 1",
    cryptoDesc: "Платите Bitcoin, USDT, ETH и 20+ другими криптовалютами через Cryptomus.",
    payWithCrypto: "Оплатить криптой", secureCryptomus: "🔒 Безопасный платёж через Cryptomus",
    agreeTerms: "Вы соглашаетесь с нашими", terms: "Условиями", refundPolicy: "Политикой возврата",
    safeCheckout: "Безопасная оплата", sslEncrypted: "SSL шифрование", guaranteed: "Отслеживаемая доставка",
    backToVideos: "← Назад к выбору видео", videos: "видео", bonus: "Бонус", selected: "выбрано",
    orderSummary: "Сводка заказа",
    minimumOrder: "Минимальный заказ $0.50",
    invalidCardNumber: "Введите корректный номер карты",
    invalidExpiry: "Неверный срок действия (ММ/ГГ)",
    invalidCvv: "Неверный CVV",
    cardPaymentFailed: "Оплата картой не удалась.",
    cardPaymentDeclined: "Оплата картой отклонена.",
    stripeError: "Ошибка Stripe",
    stripePaymentFailed: "Оплата через Stripe не удалась.",
    paypalFailed: "Ошибка PayPal.",
    cryptoPaymentFailed: "Оплата криптой не удалась.",
    tryPayPal: "🅿️ Попробовать PayPal",
    cardLabel: "Карта", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Крипто",
    stripeDescShort: "Безопасная оплата через Stripe — поддерживаются все основные кредитные и дебетовые карты.",
    paypalDesc: "Безопасная оплата через ваш аккаунт PayPal — быстрая и удобная.",
    payWithStripe: "Оплатить через Stripe",
    payWithPayPal: "Оплатить через PayPal",
  },
  thankYou: {
    awaitingPayment: "Ожидание оплаты",
    awaitingPaymentDesc: "Ваш заказ создан. Мы ожидаем подтверждения платежа.",
    thankYou: "Спасибо!", orderConfirmed: "Ваш заказ подтверждён и обрабатывается.",
    referenceNumber: "Номер заказа", copy: "Копировать", copied: "Скопировано!",
    payment: "Платёж", delivery: "Доставка", services: "Услуги", total: "Итого",
    whatsNext: "Что дальше?",
    step1: "Ваш заказ проверяется и обрабатывается",
    step2: "Результаты будут постепенно появляться в вашем аккаунте",
    step3: "Полная доставка завершается в течение 24-72 часов",
    bookmark: "Добавьте эту страницу в закладки для проверки статуса — без входа в систему.",
    placeAnother: "Сделать новый заказ",
    pending: "Ожидание", paid: "Оплачено", completed: "Завершено", processingStatus: "Обработка", delivered: "Доставлено", failed: "Ошибка",
  },
  dashboard: {
    trackYourOrder: "Отследить заказ",
    trackDesc: "Введите номер заказа или просмотрите последние заказы ниже.",
    searchPlaceholder: "напр. TD-ABC12345",
    noOrderFound: "Заказ с таким номером не найден.",
    yourOrderHistory: "История заказов", refresh: "Обновить",
    noHistory: "История заказов пуста. Сделайте заказ, чтобы увидеть его здесь!",
    placeAnother: "Сделать новый заказ",
    bookmark: "Добавьте страницу в закладки для проверки статуса — без входа.",
    whatsNext: "Что дальше?",
    step1: "Ваш заказ проверяется и обрабатывается",
    step2: "Результаты будут постепенно появляться в вашем аккаунте",
    step3: "Полная доставка завершается в течение 24-72 часов",
  },
  heroByPlatform: {
    tiktok: { title: "Продвигайте", highlight: "контент TikTok", subtitle: "Расширьте охват в TikTok с помощью маркетинговых услуг, помогающих находить ваши видео." },
    youtube: { title: "Развивайте", highlight: "канал YouTube", subtitle: "Продвигайте свои видео и канал с помощью маркетинговых услуг для расширения аудитории." },
    spotify: { title: "Продвигайте", highlight: "музыку Spotify", subtitle: "Представьте свои треки новым слушателям с помощью услуг продвижения для артистов и лейблов." },
    instagram: { title: "Усильте", highlight: "присутствие в Instagram", subtitle: "Продвигайте профиль, посты и reels с помощью маркетинговых услуг для расширения аудитории." },
    twitch: { title: "Развивайте", highlight: "стрим Twitch", subtitle: "Продвигайте канал и стримы с помощью маркетинговых услуг для привлечения новых зрителей." },
    twitter: { title: "Продвигайте", highlight: "профиль X", subtitle: "Расширьте охват в X с помощью услуг продвижения для твитов, профилей и контент-кампаний." },
    facebook: { title: "Продвигайте", highlight: "страницу Facebook", subtitle: "Развивайте страницу, посты и видео Facebook с помощью маркетинговых услуг для широкой аудитории." },
  },
};

const ko: Translation = {
  ...en,
  nav: { trackOrder: "주문 조회", track: "조회" },
  hero: {
    title: "당신의",
    titleHighlight: "TikTok 성장",
    subtitle: "콘텐츠가 더 많은 사람에게 도달하도록 돕는 전문 소셜 미디어 프로모션 서비스.",
    placeholder: "TikTok 사용자 이름 입력",
    cta: "시작하기",
    securePayments: "안전한 결제",
    fastProcessing: "빠른 처리",
    noPassword: "비밀번호 불필요",
    noUsersFound: "사용자를 찾을 수 없음",
    tryDifferent: "다른 사용자 이름으로 시도하세요",
  },
  valueProp: {
    title: "왜",
    titleHighlight: "TokDadddy",
    subtitle: "TikTok 콘텐츠 프로모션과 오디언스 성장을 위한 신뢰할 수 있는 플랫폼.",
    audienceGrowth: "오디언스 성장",
    audienceGrowthDesc: "더 많은 시청자에게 도달하도록 돕는 전문 프로모션 서비스.",
    fastProcessing: "빠른 처리",
    fastProcessingDesc: "주문은 즉시 처리됩니다. 결과는 보통 24~72시간 이내에 나타납니다.",
    privacyFirst: "개인정보 우선",
    privacyFirstDesc: "로그인 정보를 절대 요청하지 않습니다. 계정 보안이 최우선입니다.",
    securePayments: "안전한 결제",
    securePaymentsDesc: "다양한 결제 옵션. 모든 거래는 암호화되고 PCI를 준수합니다.",
    orderTracking: "주문 추적",
    orderTrackingDesc: "주문 상태를 실시간으로 모니터링하세요.",
    dedicatedSupport: "전담 지원",
    dedicatedSupportDesc: "지원팀이 모든 질문에 답변할 준비가 되어 있습니다.",
  },
  howItWorks: {
    title: "이용 방법",
    subtitle: "2분 안에 시작. 비밀번호는 절대 필요하지 않습니다.",
    step1Title: "프로필 찾기",
    step1Desc: "TikTok 사용자 이름을 입력하면 즉시 프로필을 찾아드립니다.",
    step2Title: "서비스 선택",
    step2Desc: "프로모션 패키지에서 선택하고 주문을 맞춤 설정하세요.",
    step3Title: "안전한 결제",
    step3Desc: "암호화된 결제 시스템으로 구매를 완료하세요.",
    step4Title: "주문 추적",
    step4Desc: "주문 진행 상황을 실시간으로 모니터링하세요.",
    stat247: "24/7", statSSL: "SSL", statNo: "없음", statDelivery: "24~72시간",
    statSupportLabel: "고객 지원",
    statEncryptedLabel: "암호화 결제",
    statPasswordLabel: "비밀번호 요구",
    statDeliveryLabel: "배송 시간",
  },
  footer: {
    faqTitle: "자주 묻는 질문",
    faq1Q: "내 계정은 안전한가요?",
    faq1A: "물론입니다. 비밀번호나 로그인 정보를 요청하지 않습니다. 공개 사용자 이름만 필요합니다.",
    faq2Q: "처리에 얼마나 걸리나요?",
    faq2A: "주문은 즉시 처리됩니다. 결과는 서비스에 따라 보통 24~72시간 이내에 나타납니다.",
    faq3Q: "어떤 결제 방법을 받나요?",
    faq3A: "신용/직불 카드, PayPal, 암호화폐를 받습니다. 모든 거래는 암호화됩니다.",
    faq4Q: "환불 정책은 어떻게 되나요?",
    faq4A: "미배송 주문에 대해 환불을 제공합니다. 자세한 내용은 hello@tokdadddy.com으로 문의하세요.",
    privacy: "개인정보처리방침", terms: "이용약관", refundPolicy: "환불 정책", contact: "문의",
    copyright: "tokdadddy.com 모든 권리 보유. TikTok 또는 ByteDance와 무관합니다.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe 안전", crypto: "암호화폐", sslEncrypted: "SSL 암호화",
  },
  currency: { disclaimer: "USD로 청구됩니다. 현지 가격은 참고용입니다." },
  createOrder: {
    followers: "팔로워", likes: "좋아요", views: "조회수", shares: "공유", comments: "댓글",
    selectPostsNextStep: "(다음 단계에서 게시물 선택)",
    total: "합계", discount: "할인", continue: "계속",
    contactUs: "문의 사항이 있으시면 언제든지 메시지를 보내주세요",
    selected: "선택됨", posts: "게시물",
  },
  order: {
    totalDueToday: "오늘 결제할 금액", totalDue: "결제 금액",
    selectVideos: "프로모션할 동영상 선택",
    selectVideosDesc: "부스트하고 싶은 동영상을 선택하세요",
    noVideosFound: "이 사용자의 동영상을 찾을 수 없습니다.",
    continueWithVideos: "{count}개 동영상으로 계속",
    selectAtLeastOne: "최소 하나의 동영상을 선택하세요",
    oneTimeOffer: "일회성 특가 🔥",
    addFollowers: "단돈",
    applied: "적용됨", apply: "적용",
    paymentFailed: "결제 실패",
    paymentDeclined: "결제가 거부되었습니다. 아래의 다른 방법을 시도해 주세요.",
    tryCard: "💳 카드 시도", tryStripe: "💳 Stripe 시도", tryCrypto: "₿ 암호화폐 시도",
    nameOnCard: "카드 소유자명", cardNumber: "카드 번호", expiry: "만료일", cvv: "CVV",
    processing: "처리 중...", pay: "결제",
    stripeDesc: "Stripe Checkout을 통한 안전한 결제. 카드, Apple Pay, Google Pay 등을 지원합니다.",
    payViaStripe: "Stripe로 결제", redirectingStripe: "Stripe로 이동 중...",
    stripePowered: "🔒 Stripe 기반 — PCI Level 1",
    cryptoDesc: "Cryptomus를 통해 Bitcoin, USDT, ETH 등 20개 이상의 암호화폐로 결제하세요.",
    payWithCrypto: "암호화폐로 결제", secureCryptomus: "🔒 Cryptomus를 통한 안전한 결제",
    agreeTerms: "당신은 다음에 동의합니다", terms: "이용약관", refundPolicy: "환불 정책",
    safeCheckout: "안전한 결제", sslEncrypted: "SSL 암호화", guaranteed: "추적 가능한 배송",
    backToVideos: "← 동영상 선택으로 돌아가기", videos: "동영상", bonus: "보너스", selected: "선택됨",
    orderSummary: "주문 요약",
    minimumOrder: "최소 주문 금액은 $0.50입니다",
    invalidCardNumber: "유효한 카드 번호를 입력하세요",
    invalidExpiry: "잘못된 만료일 (MM/YY)",
    invalidCvv: "잘못된 CVV",
    cardPaymentFailed: "카드 결제 실패.",
    cardPaymentDeclined: "카드 결제가 거부되었습니다.",
    stripeError: "Stripe 오류",
    stripePaymentFailed: "Stripe 결제 실패.",
    paypalFailed: "PayPal 실패.",
    cryptoPaymentFailed: "암호화폐 결제 실패.",
    tryPayPal: "🅿️ PayPal 시도",
    cardLabel: "카드", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "암호화폐",
    stripeDescShort: "Stripe를 통한 안전한 결제 — 모든 주요 신용 및 직불 카드 지원.",
    paypalDesc: "PayPal 계정으로 안전한 결제 — 빠르고 간편한 결제.",
    payWithStripe: "Stripe로 결제",
    payWithPayPal: "PayPal로 결제",
  },
  thankYou: {
    awaitingPayment: "결제 대기 중",
    awaitingPaymentDesc: "주문이 생성되었습니다. 결제 확인을 기다리고 있습니다.",
    thankYou: "감사합니다!", orderConfirmed: "주문이 확인되었으며 처리 중입니다.",
    referenceNumber: "주문 번호", copy: "복사", copied: "복사됨!",
    payment: "결제", delivery: "배송", services: "서비스", total: "합계",
    whatsNext: "다음 단계는?",
    step1: "주문이 검토되고 처리되고 있습니다",
    step2: "결과가 점진적으로 계정에 나타납니다",
    step3: "전체 배송은 24~72시간 내에 완료됩니다",
    bookmark: "이 페이지를 즐겨찾기에 추가하여 상태를 확인하세요 — 로그인 불필요.",
    placeAnother: "다른 주문하기",
    pending: "대기 중", paid: "결제 완료", completed: "완료", processingStatus: "처리 중", delivered: "배송 완료", failed: "실패",
  },
  dashboard: {
    trackYourOrder: "주문 조회",
    trackDesc: "주문 번호를 입력하거나 아래에서 최근 주문을 확인하세요.",
    searchPlaceholder: "예: TD-ABC12345",
    noOrderFound: "해당 주문 번호의 주문을 찾을 수 없습니다.",
    yourOrderHistory: "주문 내역", refresh: "새로고침",
    noHistory: "아직 주문 내역이 없습니다. 주문하면 여기에 표시됩니다!",
    placeAnother: "다른 주문하기",
    bookmark: "이 페이지를 즐겨찾기에 추가하여 상태를 확인하세요 — 로그인 불필요.",
    whatsNext: "다음 단계는?",
    step1: "주문이 검토되고 처리되고 있습니다",
    step2: "결과가 점진적으로 계정에 나타납니다",
    step3: "전체 배송은 24~72시간 내에 완료됩니다",
  },
  heroByPlatform: {
    tiktok: { title: "당신의", highlight: "TikTok 콘텐츠 프로모션", subtitle: "동영상이 발견되도록 돕는 마케팅 서비스로 TikTok 도달 범위를 확장하세요." },
    youtube: { title: "당신의", highlight: "YouTube 채널 성장", subtitle: "오디언스를 확장하기 위해 설계된 마케팅 서비스로 동영상과 채널을 홍보하세요." },
    spotify: { title: "당신의", highlight: "Spotify 음악 프로모션", subtitle: "아티스트와 레이블을 위한 프로모션 서비스로 새로운 청취자에게 트랙을 전달하세요." },
    instagram: { title: "당신의", highlight: "Instagram 존재감 강화", subtitle: "오디언스를 확장하는 마케팅 서비스로 프로필, 게시물, 릴을 홍보하세요." },
    twitch: { title: "당신의", highlight: "Twitch 스트림 성장", subtitle: "새로운 시청자에게 도달하도록 돕는 마케팅 서비스로 채널과 스트림을 홍보하세요." },
    twitter: { title: "당신의", highlight: "X 프로필 프로모션", subtitle: "트윗, 프로필, 콘텐츠 캠페인을 위한 프로모션 서비스로 X 도달 범위를 확장하세요." },
    facebook: { title: "당신의", highlight: "Facebook 페이지 부스트", subtitle: "더 넓은 오디언스에 도달하기 위한 마케팅 서비스로 Facebook 페이지, 게시물, 동영상을 성장시키세요." },
  },
};

const it: Translation = {
  ...en,
  nav: { trackOrder: "Traccia ordine", track: "Traccia" },
  hero: {
    title: "Fai crescere il tuo",
    titleHighlight: "successo su TikTok",
    subtitle: "Servizi professionali di promozione sui social media per aiutare i tuoi contenuti a raggiungere più persone.",
    placeholder: "Inserisci il tuo nome utente TikTok",
    cta: "Inizia ora",
    securePayments: "Pagamenti sicuri",
    fastProcessing: "Elaborazione rapida",
    noPassword: "Nessuna password richiesta",
    noUsersFound: "Nessun utente trovato",
    tryDifferent: "Prova un altro nome utente",
  },
  valueProp: {
    title: "Perché",
    titleHighlight: "TokDadddy",
    subtitle: "La piattaforma affidabile per promuovere i tuoi contenuti TikTok e far crescere il tuo pubblico.",
    audienceGrowth: "Crescita del pubblico",
    audienceGrowthDesc: "Servizi di promozione professionale per aiutare i tuoi contenuti a raggiungere più spettatori.",
    fastProcessing: "Elaborazione rapida",
    fastProcessingDesc: "Gli ordini vengono elaborati immediatamente. I risultati appaiono solitamente entro 24-72 ore.",
    privacyFirst: "Privacy prima di tutto",
    privacyFirstDesc: "Non chiediamo mai le tue credenziali. La sicurezza del tuo account è la nostra priorità.",
    securePayments: "Pagamenti sicuri",
    securePaymentsDesc: "Più opzioni di pagamento. Tutte le transazioni sono crittografate e conformi PCI.",
    orderTracking: "Tracciamento ordini",
    orderTrackingDesc: "Monitora lo stato del tuo ordine in tempo reale.",
    dedicatedSupport: "Supporto dedicato",
    dedicatedSupportDesc: "Il nostro team è pronto a rispondere a tutte le tue domande.",
  },
  howItWorks: {
    title: "Come funziona",
    subtitle: "Inizia in 2 minuti. Nessuna password richiesta.",
    step1Title: "Trova il tuo profilo",
    step1Desc: "Inserisci il tuo nome utente TikTok e troveremo il tuo profilo all'istante.",
    step2Title: "Scegli i servizi",
    step2Desc: "Seleziona dai nostri pacchetti di promozione e personalizza il tuo ordine.",
    step3Title: "Pagamento sicuro",
    step3Desc: "Completa l'acquisto con il nostro sistema di pagamento crittografato.",
    step4Title: "Traccia il tuo ordine",
    step4Desc: "Monitora il progresso del tuo ordine in tempo reale.",
    stat247: "24/7", statSSL: "SSL", statNo: "No", statDelivery: "24-72h",
    statSupportLabel: "Supporto clienti",
    statEncryptedLabel: "Pagamenti crittografati",
    statPasswordLabel: "Password richieste",
    statDeliveryLabel: "Tempo di consegna",
  },
  footer: {
    faqTitle: "Domande frequenti",
    faq1Q: "Il mio account è al sicuro?",
    faq1A: "Assolutamente. Non chiediamo mai password o credenziali. Solo il tuo nome utente pubblico.",
    faq2Q: "Quanto tempo richiede l'elaborazione?",
    faq2A: "Gli ordini vengono elaborati immediatamente. I risultati appaiono solitamente entro 24-72 ore a seconda del servizio.",
    faq3Q: "Quali metodi di pagamento accettate?",
    faq3A: "Accettiamo carte di credito/debito, PayPal e criptovalute. Tutte le transazioni sono crittografate.",
    faq4Q: "Qual è la vostra politica di rimborso?",
    faq4A: "Offriamo rimborsi per ordini non consegnati. Contatta hello@tokdadddy.com per i dettagli.",
    privacy: "Privacy", terms: "Termini", refundPolicy: "Politica di rimborso", contact: "Contatto",
    copyright: "tokdadddy.com Tutti i diritti riservati. Non affiliato con TikTok o ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe Sicuro", crypto: "Cripto", sslEncrypted: "SSL Crittografato",
  },
  currency: { disclaimer: "Addebitato in USD. Il prezzo locale è solo di riferimento." },
  createOrder: {
    followers: "Follower", likes: "Mi piace", views: "Visualizzazioni", shares: "Condivisioni", comments: "Commenti",
    selectPostsNextStep: "(seleziona i post nel prossimo passaggio)",
    total: "Totale", discount: "Sconto", continue: "Continua",
    contactUs: "Contattaci se hai domande, siamo sempre qui",
    selected: "selezionati", posts: "post",
  },
  order: {
    totalDueToday: "Totale da pagare oggi", totalDue: "Totale da pagare",
    selectVideos: "Seleziona i video da promuovere",
    selectVideosDesc: "Scegli i video che vuoi potenziare",
    noVideosFound: "Nessun video trovato per questo utente.",
    continueWithVideos: "Continua con {count} video",
    selectAtLeastOne: "Seleziona almeno un video",
    oneTimeOffer: "Offerta unica 🔥",
    addFollowers: "per soli",
    applied: "Applicato", apply: "Applica",
    paymentFailed: "Pagamento fallito",
    paymentDeclined: "Il tuo pagamento è stato rifiutato. Prova uno degli altri metodi qui sotto.",
    tryCard: "💳 Prova Carta", tryStripe: "💳 Prova Stripe", tryCrypto: "₿ Prova Cripto",
    nameOnCard: "Nome sulla carta", cardNumber: "Numero carta", expiry: "Scadenza", cvv: "CVV",
    processing: "Elaborazione...", pay: "Paga",
    stripeDesc: "Pagamento sicuro tramite Stripe Checkout. Supporta carte, Apple Pay, Google Pay e altro.",
    payViaStripe: "Paga tramite Stripe", redirectingStripe: "Reindirizzamento a Stripe...",
    stripePowered: "🔒 Powered by Stripe — PCI Level 1",
    cryptoDesc: "Paga con Bitcoin, USDT, ETH e oltre 20 criptovalute tramite Cryptomus.",
    payWithCrypto: "Paga con Cripto", secureCryptomus: "🔒 Pagamento sicuro via Cryptomus",
    agreeTerms: "Accetti i nostri", terms: "Termini di servizio", refundPolicy: "Politica di rimborso",
    safeCheckout: "Checkout sicuro", sslEncrypted: "Crittografato SSL", guaranteed: "Consegna tracciabile",
    backToVideos: "← Torna alla selezione video", videos: "video", bonus: "Bonus", selected: "selezionato",
    orderSummary: "Riepilogo ordine",
    minimumOrder: "L'ordine minimo è $0.50",
    invalidCardNumber: "Inserisci un numero di carta valido",
    invalidExpiry: "Scadenza non valida (MM/AA)",
    invalidCvv: "CVV non valido",
    cardPaymentFailed: "Pagamento con carta fallito.",
    cardPaymentDeclined: "Pagamento con carta rifiutato.",
    stripeError: "Errore Stripe",
    stripePaymentFailed: "Pagamento Stripe fallito.",
    paypalFailed: "PayPal fallito.",
    cryptoPaymentFailed: "Pagamento cripto fallito.",
    tryPayPal: "🅿️ Prova PayPal",
    cardLabel: "Carta", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Cripto",
    stripeDescShort: "Paga in sicurezza tramite Stripe — supporta tutte le principali carte di credito e debito.",
    paypalDesc: "Paga in sicurezza con il tuo account PayPal — checkout veloce e facile.",
    payWithStripe: "Paga con Stripe",
    payWithPayPal: "Paga con PayPal",
  },
  thankYou: {
    awaitingPayment: "In attesa di pagamento",
    awaitingPaymentDesc: "Il tuo ordine è stato creato. Stiamo aspettando la conferma del pagamento.",
    thankYou: "Grazie!", orderConfirmed: "Il tuo ordine è confermato e in elaborazione.",
    referenceNumber: "Numero di riferimento", copy: "Copia", copied: "Copiato!",
    payment: "Pagamento", delivery: "Consegna", services: "Servizi", total: "Totale",
    whatsNext: "Cosa succede ora?",
    step1: "Il tuo ordine viene esaminato ed elaborato",
    step2: "I risultati appariranno gradualmente nel tuo account",
    step3: "Consegna completa entro 24-72 ore",
    bookmark: "Aggiungi questa pagina ai segnalibri per controllare lo stato — nessun login richiesto.",
    placeAnother: "Effettua un altro ordine",
    pending: "In attesa", paid: "Pagato", completed: "Completato", processingStatus: "In elaborazione", delivered: "Consegnato", failed: "Fallito",
  },
  dashboard: {
    trackYourOrder: "Traccia il tuo ordine",
    trackDesc: "Inserisci il tuo numero di riferimento o controlla i tuoi ordini recenti qui sotto.",
    searchPlaceholder: "es. TD-ABC12345",
    noOrderFound: "Nessun ordine trovato con quel numero di riferimento.",
    yourOrderHistory: "La tua cronologia ordini", refresh: "Aggiorna",
    noHistory: "Ancora nessuna cronologia ordini. Effettua un ordine e apparirà qui!",
    placeAnother: "Effettua un altro ordine",
    bookmark: "Aggiungi questa pagina ai segnalibri per controllare lo stato — nessun login richiesto.",
    whatsNext: "Cosa succede ora?",
    step1: "Il tuo ordine viene esaminato ed elaborato",
    step2: "I risultati appariranno gradualmente nel tuo account",
    step3: "Consegna completa entro 24-72 ore",
  },
  heroByPlatform: {
    tiktok: { title: "Promuovi i tuoi", highlight: "contenuti TikTok", subtitle: "Espandi la tua portata su TikTok con servizi di marketing che aiutano i tuoi video a essere scoperti." },
    youtube: { title: "Fai crescere il tuo", highlight: "canale YouTube", subtitle: "Promuovi video e canali con servizi di marketing progettati per espandere il tuo pubblico." },
    spotify: { title: "Promuovi la tua", highlight: "musica su Spotify", subtitle: "Servizi promozionali per artisti ed etichette per far arrivare i brani a nuovi ascoltatori." },
    instagram: { title: "Potenzia la tua", highlight: "presenza su Instagram", subtitle: "Promuovi profili, post e Reel con servizi di marketing che espandono il tuo pubblico." },
    twitch: { title: "Fai crescere il tuo", highlight: "stream Twitch", subtitle: "Promuovi il tuo canale e i tuoi stream con servizi di marketing che ti aiutano a raggiungere nuovi spettatori." },
    twitter: { title: "Promuovi il tuo", highlight: "profilo X", subtitle: "Espandi la tua portata su X con servizi promozionali per tweet, profili e campagne di contenuti." },
    facebook: { title: "Potenzia la tua", highlight: "pagina Facebook", subtitle: "Fai crescere pagine, post e video Facebook con servizi di marketing per raggiungere un pubblico più ampio." },
  },
};

const pl: Translation = {
  ...en,
  nav: { trackOrder: "Śledź zamówienie", track: "Śledź" },
  hero: {
    title: "Rozwijaj swój",
    titleHighlight: "sukces na TikToku",
    subtitle: "Profesjonalne usługi promocji w mediach społecznościowych, które pomogą Twoim treściom dotrzeć do większej liczby osób.",
    placeholder: "Wpisz swoją nazwę użytkownika TikTok",
    cta: "Zacznij teraz",
    securePayments: "Bezpieczne płatności",
    fastProcessing: "Szybka realizacja",
    noPassword: "Bez hasła",
    noUsersFound: "Nie znaleziono użytkowników",
    tryDifferent: "Spróbuj innej nazwy użytkownika",
  },
  valueProp: {
    title: "Dlaczego",
    titleHighlight: "TokDadddy",
    subtitle: "Zaufana platforma do promowania treści TikTok i powiększania publiczności.",
    audienceGrowth: "Wzrost publiczności",
    audienceGrowthDesc: "Profesjonalne usługi promocji, które pomagają Twoim treściom dotrzeć do większej liczby widzów.",
    fastProcessing: "Szybka realizacja",
    fastProcessingDesc: "Zamówienia są realizowane natychmiast. Wyniki zwykle pojawiają się w ciągu 24-72 godzin.",
    privacyFirst: "Prywatność przede wszystkim",
    privacyFirstDesc: "Nigdy nie prosimy o dane logowania. Bezpieczeństwo Twojego konta jest naszym priorytetem.",
    securePayments: "Bezpieczne płatności",
    securePaymentsDesc: "Wiele opcji płatności. Wszystkie transakcje są szyfrowane i zgodne z PCI.",
    orderTracking: "Śledzenie zamówień",
    orderTrackingDesc: "Monitoruj status zamówienia w czasie rzeczywistym.",
    dedicatedSupport: "Dedykowane wsparcie",
    dedicatedSupportDesc: "Nasz zespół wsparcia jest gotowy odpowiedzieć na wszystkie Twoje pytania.",
  },
  howItWorks: {
    title: "Jak to działa",
    subtitle: "Zacznij w 2 minuty. Nigdy nie potrzeba hasła.",
    step1Title: "Znajdź swój profil",
    step1Desc: "Wpisz swoją nazwę użytkownika TikTok, a my natychmiast znajdziemy Twój profil.",
    step2Title: "Wybierz usługi",
    step2Desc: "Wybierz z naszych pakietów promocyjnych i dostosuj swoje zamówienie.",
    step3Title: "Bezpieczna płatność",
    step3Desc: "Sfinalizuj zakup za pomocą naszego szyfrowanego systemu płatności.",
    step4Title: "Śledź zamówienie",
    step4Desc: "Monitoruj postępy zamówienia w czasie rzeczywistym.",
    stat247: "24/7", statSSL: "SSL", statNo: "Brak", statDelivery: "24-72h",
    statSupportLabel: "Obsługa klienta",
    statEncryptedLabel: "Szyfrowane płatności",
    statPasswordLabel: "Wymagane hasła",
    statDeliveryLabel: "Czas dostawy",
  },
  footer: {
    faqTitle: "Często zadawane pytania",
    faq1Q: "Czy moje konto jest bezpieczne?",
    faq1A: "Absolutnie. Nigdy nie prosimy o hasła ani dane logowania. Tylko Twoja publiczna nazwa użytkownika.",
    faq2Q: "Jak długo trwa realizacja?",
    faq2A: "Zamówienia są realizowane natychmiast. Wyniki zwykle pojawiają się w ciągu 24-72 godzin w zależności od usługi.",
    faq3Q: "Jakie metody płatności akceptujecie?",
    faq3A: "Akceptujemy karty kredytowe/debetowe, PayPal i kryptowaluty. Wszystkie transakcje są szyfrowane.",
    faq4Q: "Jaka jest Wasza polityka zwrotów?",
    faq4A: "Oferujemy zwroty za niezrealizowane zamówienia. Skontaktuj się z hello@tokdadddy.com w sprawie szczegółów.",
    privacy: "Prywatność", terms: "Warunki", refundPolicy: "Polityka zwrotów", contact: "Kontakt",
    copyright: "tokdadddy.com Wszelkie prawa zastrzeżone. Niepowiązany z TikTok ani ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe Bezpieczne", crypto: "Krypto", sslEncrypted: "SSL Szyfrowane",
  },
  currency: { disclaimer: "Płatność w USD. Cena lokalna jest tylko orientacyjna." },
  createOrder: {
    followers: "Obserwujący", likes: "Polubienia", views: "Wyświetlenia", shares: "Udostępnienia", comments: "Komentarze",
    selectPostsNextStep: "(wybierz posty w następnym kroku)",
    total: "Razem", discount: "Rabat", continue: "Kontynuuj",
    contactUs: "Skontaktuj się z nami w razie pytań, jesteśmy zawsze tutaj",
    selected: "wybrane", posts: "posty",
  },
  order: {
    totalDueToday: "Łącznie do zapłaty dzisiaj", totalDue: "Łącznie do zapłaty",
    selectVideos: "Wybierz filmy do promocji",
    selectVideosDesc: "Wybierz filmy, które chcesz wzmocnić",
    noVideosFound: "Nie znaleziono filmów dla tego użytkownika.",
    continueWithVideos: "Kontynuuj z {count} filmami",
    selectAtLeastOne: "Wybierz co najmniej jeden film",
    oneTimeOffer: "Oferta jednorazowa 🔥",
    addFollowers: "tylko za",
    applied: "Zastosowane", apply: "Zastosuj",
    paymentFailed: "Płatność nie powiodła się",
    paymentDeclined: "Twoja płatność została odrzucona. Spróbuj jednej z innych metod poniżej.",
    tryCard: "💳 Wypróbuj Kartę", tryStripe: "💳 Wypróbuj Stripe", tryCrypto: "₿ Wypróbuj Krypto",
    nameOnCard: "Imię na karcie", cardNumber: "Numer karty", expiry: "Data ważności", cvv: "CVV",
    processing: "Przetwarzanie...", pay: "Zapłać",
    stripeDesc: "Bezpieczna płatność przez Stripe Checkout. Obsługuje karty, Apple Pay, Google Pay i więcej.",
    payViaStripe: "Zapłać przez Stripe", redirectingStripe: "Przekierowywanie do Stripe...",
    stripePowered: "🔒 Powered by Stripe — PCI Level 1",
    cryptoDesc: "Zapłać Bitcoin, USDT, ETH i ponad 20 kryptowalutami przez Cryptomus.",
    payWithCrypto: "Zapłać Krypto", secureCryptomus: "🔒 Bezpieczna płatność przez Cryptomus",
    agreeTerms: "Zgadzasz się na nasze", terms: "Warunki świadczenia usług", refundPolicy: "Polityka zwrotów",
    safeCheckout: "Bezpieczny checkout", sslEncrypted: "Szyfrowane SSL", guaranteed: "Śledzona dostawa",
    backToVideos: "← Powrót do wyboru filmów", videos: "filmy", bonus: "Bonus", selected: "wybrane",
    orderSummary: "Podsumowanie zamówienia",
    minimumOrder: "Minimalne zamówienie to $0.50",
    invalidCardNumber: "Wprowadź prawidłowy numer karty",
    invalidExpiry: "Nieprawidłowa data ważności (MM/RR)",
    invalidCvv: "Nieprawidłowy CVV",
    cardPaymentFailed: "Płatność kartą nie powiodła się.",
    cardPaymentDeclined: "Płatność kartą została odrzucona.",
    stripeError: "Błąd Stripe",
    stripePaymentFailed: "Płatność Stripe nie powiodła się.",
    paypalFailed: "PayPal nie powiódł się.",
    cryptoPaymentFailed: "Płatność kryptowalutami nie powiodła się.",
    tryPayPal: "🅿️ Wypróbuj PayPal",
    cardLabel: "Karta", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Krypto",
    stripeDescShort: "Zapłać bezpiecznie przez Stripe — obsługuje wszystkie główne karty kredytowe i debetowe.",
    paypalDesc: "Zapłać bezpiecznie swoim kontem PayPal — szybki i łatwy checkout.",
    payWithStripe: "Zapłać przez Stripe",
    payWithPayPal: "Zapłać przez PayPal",
  },
  thankYou: {
    awaitingPayment: "Oczekiwanie na płatność",
    awaitingPaymentDesc: "Twoje zamówienie zostało utworzone. Czekamy na potwierdzenie płatności.",
    thankYou: "Dziękujemy!", orderConfirmed: "Twoje zamówienie jest potwierdzone i przetwarzane.",
    referenceNumber: "Numer referencyjny", copy: "Kopiuj", copied: "Skopiowane!",
    payment: "Płatność", delivery: "Dostawa", services: "Usługi", total: "Razem",
    whatsNext: "Co dalej?",
    step1: "Twoje zamówienie jest sprawdzane i przetwarzane",
    step2: "Wyniki będą stopniowo pojawiać się na Twoim koncie",
    step3: "Pełna dostawa w ciągu 24-72 godzin",
    bookmark: "Dodaj tę stronę do zakładek, aby sprawdzać status — bez logowania.",
    placeAnother: "Złóż kolejne zamówienie",
    pending: "Oczekujące", paid: "Opłacone", completed: "Zakończone", processingStatus: "W trakcie", delivered: "Dostarczone", failed: "Nieudane",
  },
  dashboard: {
    trackYourOrder: "Śledź swoje zamówienie",
    trackDesc: "Wprowadź numer referencyjny lub sprawdź ostatnie zamówienia poniżej.",
    searchPlaceholder: "np. TD-ABC12345",
    noOrderFound: "Nie znaleziono zamówienia z tym numerem referencyjnym.",
    yourOrderHistory: "Historia Twoich zamówień", refresh: "Odśwież",
    noHistory: "Jeszcze brak historii zamówień. Złóż zamówienie, a pojawi się tutaj!",
    placeAnother: "Złóż kolejne zamówienie",
    bookmark: "Dodaj tę stronę do zakładek, aby sprawdzać status — bez logowania.",
    whatsNext: "Co dalej?",
    step1: "Twoje zamówienie jest sprawdzane i przetwarzane",
    step2: "Wyniki będą stopniowo pojawiać się na Twoim koncie",
    step3: "Pełna dostawa w ciągu 24-72 godzin",
  },
  heroByPlatform: {
    tiktok: { title: "Promuj swoje", highlight: "treści TikTok", subtitle: "Rozszerz zasięg na TikToku dzięki usługom marketingowym, które pomagają odkryć Twoje filmy." },
    youtube: { title: "Rozwijaj swój", highlight: "kanał YouTube", subtitle: "Promuj filmy i kanały z usługami marketingowymi zaprojektowanymi do powiększenia publiczności." },
    spotify: { title: "Promuj swoją", highlight: "muzykę na Spotify", subtitle: "Usługi promocyjne dla artystów i wytwórni, aby dotrzeć do nowych słuchaczy." },
    instagram: { title: "Wzmocnij swoją", highlight: "obecność na Instagramie", subtitle: "Promuj profile, posty i Reels z usługami marketingowymi powiększającymi publiczność." },
    twitch: { title: "Rozwijaj swoje", highlight: "transmisje Twitch", subtitle: "Promuj swój kanał i transmisje z usługami marketingowymi pomagającymi dotrzeć do nowych widzów." },
    twitter: { title: "Promuj swój", highlight: "profil X", subtitle: "Rozszerz zasięg na X dzięki usługom promocyjnym dla tweetów, profili i kampanii treści." },
    facebook: { title: "Wzmocnij swoją", highlight: "stronę Facebook", subtitle: "Rozwijaj strony, posty i filmy Facebook z usługami marketingowymi, aby dotrzeć do szerszej publiczności." },
  },
};

const nl: Translation = {
  ...en,
  nav: { trackOrder: "Bestelling volgen", track: "Volgen" },
  hero: {
    title: "Laat je",
    titleHighlight: "TikTok-succes groeien",
    subtitle: "Professionele social media promotiediensten om je content meer mensen te laten bereiken.",
    placeholder: "Voer je TikTok-gebruikersnaam in",
    cta: "Begin nu",
    securePayments: "Veilige betalingen",
    fastProcessing: "Snelle verwerking",
    noPassword: "Geen wachtwoord nodig",
    noUsersFound: "Geen gebruikers gevonden",
    tryDifferent: "Probeer een andere gebruikersnaam",
  },
  valueProp: {
    title: "Waarom",
    titleHighlight: "TokDadddy",
    subtitle: "Het vertrouwde platform om je TikTok-content te promoten en je publiek te laten groeien.",
    audienceGrowth: "Publieksgroei",
    audienceGrowthDesc: "Professionele promotiediensten om je content meer kijkers te laten bereiken.",
    fastProcessing: "Snelle verwerking",
    fastProcessingDesc: "Bestellingen worden direct verwerkt. Resultaten verschijnen meestal binnen 24-72 uur.",
    privacyFirst: "Privacy voorop",
    privacyFirstDesc: "We vragen nooit om je inloggegevens. De veiligheid van je account is onze prioriteit.",
    securePayments: "Veilige betalingen",
    securePaymentsDesc: "Meerdere betaalopties. Alle transacties zijn versleuteld en PCI-conform.",
    orderTracking: "Bestelling volgen",
    orderTrackingDesc: "Volg de status van je bestelling in realtime.",
    dedicatedSupport: "Toegewijde ondersteuning",
    dedicatedSupportDesc: "Ons supportteam staat klaar om al je vragen te beantwoorden.",
  },
  howItWorks: {
    title: "Hoe het werkt",
    subtitle: "Begin binnen 2 minuten. Nooit een wachtwoord nodig.",
    step1Title: "Vind je profiel",
    step1Desc: "Voer je TikTok-gebruikersnaam in en we vinden direct je profiel.",
    step2Title: "Kies diensten",
    step2Desc: "Selecteer uit onze promotiepakketten en pas je bestelling aan.",
    step3Title: "Veilig betalen",
    step3Desc: "Voltooi je aankoop met ons versleutelde betaalsysteem.",
    step4Title: "Volg je bestelling",
    step4Desc: "Volg de voortgang van je bestelling in realtime.",
    stat247: "24/7", statSSL: "SSL", statNo: "Geen", statDelivery: "24-72u",
    statSupportLabel: "Klantenservice",
    statEncryptedLabel: "Versleutelde betalingen",
    statPasswordLabel: "Wachtwoord vereist",
    statDeliveryLabel: "Levertijd",
  },
  footer: {
    faqTitle: "Veelgestelde vragen",
    faq1Q: "Is mijn account veilig?",
    faq1A: "Absoluut. We vragen nooit om wachtwoorden of inloggegevens. Alleen je openbare gebruikersnaam.",
    faq2Q: "Hoe lang duurt de verwerking?",
    faq2A: "Bestellingen worden direct verwerkt. Resultaten verschijnen meestal binnen 24-72 uur, afhankelijk van de dienst.",
    faq3Q: "Welke betaalmethoden accepteren jullie?",
    faq3A: "We accepteren credit-/debitcards, PayPal en cryptocurrency. Alle transacties zijn versleuteld.",
    faq4Q: "Wat is jullie restitutiebeleid?",
    faq4A: "We bieden restituties voor niet-geleverde bestellingen. Neem contact op met hello@tokdadddy.com voor details.",
    privacy: "Privacy", terms: "Voorwaarden", refundPolicy: "Restitutiebeleid", contact: "Contact",
    copyright: "tokdadddy.com Alle rechten voorbehouden. Niet aangesloten bij TikTok of ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe Veilig", crypto: "Crypto", sslEncrypted: "SSL Versleuteld",
  },
  currency: { disclaimer: "In rekening gebracht in USD. Lokale prijs alleen ter referentie." },
  createOrder: {
    followers: "Volgers", likes: "Likes", views: "Weergaven", shares: "Delen", comments: "Reacties",
    selectPostsNextStep: "(selecteer posts in volgende stap)",
    total: "Totaal", discount: "Korting", continue: "Doorgaan",
    contactUs: "Neem contact op bij vragen, we zijn er altijd",
    selected: "geselecteerd", posts: "posts",
  },
  order: {
    totalDueToday: "Totaal vandaag te betalen", totalDue: "Totaal te betalen",
    selectVideos: "Selecteer video's om te promoten",
    selectVideosDesc: "Kies de video's die je wilt boosten",
    noVideosFound: "Geen video's gevonden voor deze gebruiker.",
    continueWithVideos: "Doorgaan met {count} video's",
    selectAtLeastOne: "Selecteer ten minste één video",
    oneTimeOffer: "Eenmalige aanbieding 🔥",
    addFollowers: "voor slechts",
    applied: "Toegepast", apply: "Toepassen",
    paymentFailed: "Betaling mislukt",
    paymentDeclined: "Je betaling is geweigerd. Probeer een van de andere methoden hieronder.",
    tryCard: "💳 Probeer Kaart", tryStripe: "💳 Probeer Stripe", tryCrypto: "₿ Probeer Crypto",
    nameOnCard: "Naam op kaart", cardNumber: "Kaartnummer", expiry: "Vervaldatum", cvv: "CVV",
    processing: "Verwerken...", pay: "Betalen",
    stripeDesc: "Veilige betaling via Stripe Checkout. Ondersteunt kaarten, Apple Pay, Google Pay en meer.",
    payViaStripe: "Betaal via Stripe", redirectingStripe: "Doorverwijzen naar Stripe...",
    stripePowered: "🔒 Powered by Stripe — PCI Level 1",
    cryptoDesc: "Betaal met Bitcoin, USDT, ETH en 20+ cryptocurrencies via Cryptomus.",
    payWithCrypto: "Betalen met Crypto", secureCryptomus: "🔒 Veilige betaling via Cryptomus",
    agreeTerms: "Je gaat akkoord met onze", terms: "Servicevoorwaarden", refundPolicy: "Restitutiebeleid",
    safeCheckout: "Veilige checkout", sslEncrypted: "SSL versleuteld", guaranteed: "Volgbare levering",
    backToVideos: "← Terug naar videoselectie", videos: "video's", bonus: "Bonus", selected: "geselecteerd",
    orderSummary: "Besteloverzicht",
    minimumOrder: "Minimale bestelling is $0.50",
    invalidCardNumber: "Voer een geldig kaartnummer in",
    invalidExpiry: "Ongeldige vervaldatum (MM/JJ)",
    invalidCvv: "Ongeldige CVV",
    cardPaymentFailed: "Kaartbetaling mislukt.",
    cardPaymentDeclined: "Kaartbetaling geweigerd.",
    stripeError: "Stripe-fout",
    stripePaymentFailed: "Stripe-betaling mislukt.",
    paypalFailed: "PayPal mislukt.",
    cryptoPaymentFailed: "Cryptobetaling mislukt.",
    tryPayPal: "🅿️ Probeer PayPal",
    cardLabel: "Kaart", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Crypto",
    stripeDescShort: "Veilig betalen via Stripe — ondersteunt alle grote credit- en debitcards.",
    paypalDesc: "Veilig betalen met je PayPal-account — snelle en eenvoudige checkout.",
    payWithStripe: "Betaal met Stripe",
    payWithPayPal: "Betaal met PayPal",
  },
  thankYou: {
    awaitingPayment: "Wacht op betaling",
    awaitingPaymentDesc: "Je bestelling is aangemaakt. We wachten op de betalingsbevestiging.",
    thankYou: "Bedankt!", orderConfirmed: "Je bestelling is bevestigd en wordt verwerkt.",
    referenceNumber: "Referentienummer", copy: "Kopiëren", copied: "Gekopieerd!",
    payment: "Betaling", delivery: "Levering", services: "Diensten", total: "Totaal",
    whatsNext: "Wat is de volgende stap?",
    step1: "Je bestelling wordt beoordeeld en verwerkt",
    step2: "Resultaten verschijnen geleidelijk in je account",
    step3: "Volledige levering binnen 24-72 uur",
    bookmark: "Voeg deze pagina toe aan je bladwijzers om de status te controleren — geen login nodig.",
    placeAnother: "Plaats nog een bestelling",
    pending: "In afwachting", paid: "Betaald", completed: "Voltooid", processingStatus: "Verwerken", delivered: "Geleverd", failed: "Mislukt",
  },
  dashboard: {
    trackYourOrder: "Volg je bestelling",
    trackDesc: "Voer je referentienummer in of bekijk je recente bestellingen hieronder.",
    searchPlaceholder: "bijv. TD-ABC12345",
    noOrderFound: "Geen bestelling gevonden met dat referentienummer.",
    yourOrderHistory: "Je bestelgeschiedenis", refresh: "Vernieuwen",
    noHistory: "Nog geen bestelgeschiedenis. Plaats een bestelling en het verschijnt hier!",
    placeAnother: "Plaats nog een bestelling",
    bookmark: "Voeg deze pagina toe aan je bladwijzers om de status te controleren — geen login nodig.",
    whatsNext: "Wat is de volgende stap?",
    step1: "Je bestelling wordt beoordeeld en verwerkt",
    step2: "Resultaten verschijnen geleidelijk in je account",
    step3: "Volledige levering binnen 24-72 uur",
  },
  heroByPlatform: {
    tiktok: { title: "Promoot je", highlight: "TikTok-content", subtitle: "Vergroot je bereik op TikTok met marketingdiensten die je video's helpen ontdekken." },
    youtube: { title: "Laat je", highlight: "YouTube-kanaal groeien", subtitle: "Promoot video's en kanalen met marketingdiensten ontworpen om je publiek te vergroten." },
    spotify: { title: "Promoot je", highlight: "muziek op Spotify", subtitle: "Promotionele diensten voor artiesten en labels om tracks bij nieuwe luisteraars te krijgen." },
    instagram: { title: "Versterk je", highlight: "Instagram-aanwezigheid", subtitle: "Promoot profielen, posts en Reels met marketingdiensten die je publiek vergroten." },
    twitch: { title: "Laat je", highlight: "Twitch-streams groeien", subtitle: "Promoot je kanaal en streams met marketingdiensten die je helpen nieuwe kijkers te bereiken." },
    twitter: { title: "Promoot je", highlight: "X-profiel", subtitle: "Vergroot je bereik op X met promotionele diensten voor tweets, profielen en contentcampagnes." },
    facebook: { title: "Boost je", highlight: "Facebook-pagina", subtitle: "Laat Facebook-pagina's, posts en video's groeien met marketingdiensten om een breder publiek te bereiken." },
  },
};

const sv: Translation = {
  ...en,
  nav: { trackOrder: "Spåra beställning", track: "Spåra" },
  hero: {
    title: "Få din",
    titleHighlight: "TikTok-framgång att växa",
    subtitle: "Professionella sociala medier-marknadsföringstjänster för att hjälpa ditt innehåll att nå fler människor.",
    placeholder: "Ange ditt TikTok-användarnamn",
    cta: "Kom igång",
    securePayments: "Säkra betalningar",
    fastProcessing: "Snabb hantering",
    noPassword: "Inget lösenord behövs",
    noUsersFound: "Inga användare hittades",
    tryDifferent: "Prova ett annat användarnamn",
  },
  valueProp: {
    title: "Varför",
    titleHighlight: "TokDadddy",
    subtitle: "Den pålitliga plattformen för att marknadsföra ditt TikTok-innehåll och få din publik att växa.",
    audienceGrowth: "Publiktillväxt",
    audienceGrowthDesc: "Professionella marknadsföringstjänster för att hjälpa ditt innehåll att nå fler tittare.",
    fastProcessing: "Snabb hantering",
    fastProcessingDesc: "Beställningar behandlas direkt. Resultat visas vanligtvis inom 24-72 timmar.",
    privacyFirst: "Integritet först",
    privacyFirstDesc: "Vi ber aldrig om dina inloggningsuppgifter. Säkerheten för ditt konto är vår prioritet.",
    securePayments: "Säkra betalningar",
    securePaymentsDesc: "Flera betalningsalternativ. Alla transaktioner är krypterade och PCI-kompatibla.",
    orderTracking: "Beställningsspårning",
    orderTrackingDesc: "Övervaka din beställningsstatus i realtid.",
    dedicatedSupport: "Dedikerad support",
    dedicatedSupportDesc: "Vårt supportteam är redo att svara på alla dina frågor.",
  },
  howItWorks: {
    title: "Så fungerar det",
    subtitle: "Kom igång på 2 minuter. Aldrig något lösenord behövs.",
    step1Title: "Hitta din profil",
    step1Desc: "Ange ditt TikTok-användarnamn så hittar vi din profil direkt.",
    step2Title: "Välj tjänster",
    step2Desc: "Välj från våra marknadsföringspaket och anpassa din beställning.",
    step3Title: "Säker betalning",
    step3Desc: "Slutför ditt köp med vårt krypterade betalningssystem.",
    step4Title: "Spåra din beställning",
    step4Desc: "Övervaka din beställningsutveckling i realtid.",
    stat247: "24/7", statSSL: "SSL", statNo: "Inga", statDelivery: "24-72h",
    statSupportLabel: "Kundsupport",
    statEncryptedLabel: "Krypterade betalningar",
    statPasswordLabel: "Lösenord krävs",
    statDeliveryLabel: "Leveranstid",
  },
  footer: {
    faqTitle: "Vanliga frågor",
    faq1Q: "Är mitt konto säkert?",
    faq1A: "Absolut. Vi ber aldrig om lösenord eller inloggningsuppgifter. Endast ditt offentliga användarnamn.",
    faq2Q: "Hur lång tid tar hanteringen?",
    faq2A: "Beställningar behandlas direkt. Resultat visas vanligtvis inom 24-72 timmar beroende på tjänst.",
    faq3Q: "Vilka betalningsmetoder accepterar ni?",
    faq3A: "Vi accepterar kredit-/betalkort, PayPal och kryptovaluta. Alla transaktioner är krypterade.",
    faq4Q: "Vad är er återbetalningspolicy?",
    faq4A: "Vi erbjuder återbetalningar för icke-levererade beställningar. Kontakta hello@tokdadddy.com för detaljer.",
    privacy: "Integritet", terms: "Villkor", refundPolicy: "Återbetalningspolicy", contact: "Kontakt",
    copyright: "tokdadddy.com Alla rättigheter förbehållna. Inte ansluten till TikTok eller ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe Säker", crypto: "Krypto", sslEncrypted: "SSL Krypterad",
  },
  currency: { disclaimer: "Debiteras i USD. Lokalt pris endast som referens." },
  createOrder: {
    followers: "Följare", likes: "Gilla-markeringar", views: "Visningar", shares: "Delningar", comments: "Kommentarer",
    selectPostsNextStep: "(välj inlägg i nästa steg)",
    total: "Totalt", discount: "Rabatt", continue: "Fortsätt",
    contactUs: "Kontakta oss om du har frågor, vi finns alltid här",
    selected: "valda", posts: "inlägg",
  },
  order: {
    totalDueToday: "Totalt att betala idag", totalDue: "Totalt att betala",
    selectVideos: "Välj videor att marknadsföra",
    selectVideosDesc: "Välj videorna du vill boosta",
    noVideosFound: "Inga videor hittades för denna användare.",
    continueWithVideos: "Fortsätt med {count} videor",
    selectAtLeastOne: "Välj minst en video",
    oneTimeOffer: "Engångserbjudande 🔥",
    addFollowers: "för bara",
    applied: "Tillämpad", apply: "Tillämpa",
    paymentFailed: "Betalning misslyckades",
    paymentDeclined: "Din betalning nekades. Prova en av de andra metoderna nedan.",
    tryCard: "💳 Prova Kort", tryStripe: "💳 Prova Stripe", tryCrypto: "₿ Prova Krypto",
    nameOnCard: "Namn på kort", cardNumber: "Kortnummer", expiry: "Utgångsdatum", cvv: "CVV",
    processing: "Bearbetar...", pay: "Betala",
    stripeDesc: "Säker betalning via Stripe Checkout. Stöder kort, Apple Pay, Google Pay och mer.",
    payViaStripe: "Betala via Stripe", redirectingStripe: "Omdirigerar till Stripe...",
    stripePowered: "🔒 Drivs av Stripe — PCI Level 1",
    cryptoDesc: "Betala med Bitcoin, USDT, ETH och 20+ kryptovalutor via Cryptomus.",
    payWithCrypto: "Betala med Krypto", secureCryptomus: "🔒 Säker betalning via Cryptomus",
    agreeTerms: "Du godkänner våra", terms: "Användarvillkor", refundPolicy: "Återbetalningspolicy",
    safeCheckout: "Säker kassa", sslEncrypted: "SSL-krypterad", guaranteed: "Spårbar leverans",
    backToVideos: "← Tillbaka till videoval", videos: "videor", bonus: "Bonus", selected: "vald",
    orderSummary: "Beställningsöversikt",
    minimumOrder: "Minsta beställning är $0.50",
    invalidCardNumber: "Ange ett giltigt kortnummer",
    invalidExpiry: "Ogiltigt utgångsdatum (MM/ÅÅ)",
    invalidCvv: "Ogiltig CVV",
    cardPaymentFailed: "Kortbetalning misslyckades.",
    cardPaymentDeclined: "Kortbetalning nekades.",
    stripeError: "Stripe-fel",
    stripePaymentFailed: "Stripe-betalning misslyckades.",
    paypalFailed: "PayPal misslyckades.",
    cryptoPaymentFailed: "Kryptobetalning misslyckades.",
    tryPayPal: "🅿️ Prova PayPal",
    cardLabel: "Kort", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Krypto",
    stripeDescShort: "Betala säkert via Stripe — stöder alla större kredit- och betalkort.",
    paypalDesc: "Betala säkert med ditt PayPal-konto — snabb och enkel kassa.",
    payWithStripe: "Betala med Stripe",
    payWithPayPal: "Betala med PayPal",
  },
  thankYou: {
    awaitingPayment: "Väntar på betalning",
    awaitingPaymentDesc: "Din beställning har skapats. Vi väntar på betalningsbekräftelse.",
    thankYou: "Tack!", orderConfirmed: "Din beställning är bekräftad och bearbetas.",
    referenceNumber: "Referensnummer", copy: "Kopiera", copied: "Kopierad!",
    payment: "Betalning", delivery: "Leverans", services: "Tjänster", total: "Totalt",
    whatsNext: "Vad händer härnäst?",
    step1: "Din beställning granskas och bearbetas",
    step2: "Resultaten visas gradvis på ditt konto",
    step3: "Fullständig leverans inom 24-72 timmar",
    bookmark: "Bokmärk denna sida för att kontrollera status — ingen inloggning behövs.",
    placeAnother: "Lägg en annan beställning",
    pending: "Väntande", paid: "Betald", completed: "Slutförd", processingStatus: "Bearbetar", delivered: "Levererad", failed: "Misslyckades",
  },
  dashboard: {
    trackYourOrder: "Spåra din beställning",
    trackDesc: "Ange ditt referensnummer eller kontrollera dina senaste beställningar nedan.",
    searchPlaceholder: "t.ex. TD-ABC12345",
    noOrderFound: "Ingen beställning hittades med det referensnumret.",
    yourOrderHistory: "Din beställningshistorik", refresh: "Uppdatera",
    noHistory: "Ingen beställningshistorik än. Lägg en beställning så visas den här!",
    placeAnother: "Lägg en annan beställning",
    bookmark: "Bokmärk denna sida för att kontrollera status — ingen inloggning behövs.",
    whatsNext: "Vad händer härnäst?",
    step1: "Din beställning granskas och bearbetas",
    step2: "Resultaten visas gradvis på ditt konto",
    step3: "Fullständig leverans inom 24-72 timmar",
  },
  heroByPlatform: {
    tiktok: { title: "Marknadsför ditt", highlight: "TikTok-innehåll", subtitle: "Utöka din räckvidd på TikTok med marknadsföringstjänster som hjälper dina videor att upptäckas." },
    youtube: { title: "Få din", highlight: "YouTube-kanal att växa", subtitle: "Marknadsför videor och kanaler med marknadsföringstjänster designade för att utöka din publik." },
    spotify: { title: "Marknadsför din", highlight: "musik på Spotify", subtitle: "Marknadsföringstjänster för artister och skivbolag att få låtar till nya lyssnare." },
    instagram: { title: "Stärk din", highlight: "Instagram-närvaro", subtitle: "Marknadsför profiler, inlägg och Reels med marknadsföringstjänster som utökar din publik." },
    twitch: { title: "Få dina", highlight: "Twitch-strömmar att växa", subtitle: "Marknadsför din kanal och dina strömmar med tjänster som hjälper dig nå nya tittare." },
    twitter: { title: "Marknadsför din", highlight: "X-profil", subtitle: "Utöka din räckvidd på X med marknadsföringstjänster för tweets, profiler och innehållskampanjer." },
    facebook: { title: "Boosta din", highlight: "Facebook-sida", subtitle: "Få Facebook-sidor, inlägg och videor att växa med marknadsföringstjänster för att nå en bredare publik." },
  },
};

const fil: Translation = {
  ...en,
  nav: { trackOrder: "I-track ang Order", track: "I-track" },
  hero: {
    title: "Palaguin ang iyong",
    titleHighlight: "TikTok success",
    subtitle: "Mga propesyonal na social media promotion services para tumulong sa iyong content na maabot ang mas maraming tao.",
    placeholder: "Ilagay ang iyong TikTok username",
    cta: "Magsimula",
    securePayments: "Ligtas na bayad",
    fastProcessing: "Mabilis na pagproseso",
    noPassword: "Walang password kailangan",
    noUsersFound: "Walang nahanap na user",
    tryDifferent: "Subukan ang ibang username",
  },
  valueProp: {
    title: "Bakit",
    titleHighlight: "TokDadddy",
    subtitle: "Ang pinagkakatiwalaang platform para i-promote ang iyong TikTok content at palaguin ang audience.",
    audienceGrowth: "Paglago ng Audience",
    audienceGrowthDesc: "Mga propesyonal na promotion services para tumulong sa content mo na maabot ang mas maraming viewer.",
    fastProcessing: "Mabilis na pagproseso",
    fastProcessingDesc: "Agad na pinoproseso ang mga order. Karaniwang lumalabas ang resulta sa loob ng 24-72 oras.",
    privacyFirst: "Privacy Una",
    privacyFirstDesc: "Hindi kami humihingi ng iyong login credentials. Ang seguridad ng account mo ang aming prioridad.",
    securePayments: "Ligtas na bayad",
    securePaymentsDesc: "Maraming payment options. Lahat ng transaksyon ay encrypted at PCI-compliant.",
    orderTracking: "Pag-track ng Order",
    orderTrackingDesc: "I-monitor ang status ng iyong order sa real-time.",
    dedicatedSupport: "Dedikadong Suporta",
    dedicatedSupportDesc: "Ang aming support team ay handa sumagot sa lahat ng tanong mo.",
  },
  howItWorks: {
    title: "Paano ito gumagana",
    subtitle: "Magsimula sa loob ng 2 minuto. Hindi kailangan ng password.",
    step1Title: "Hanapin ang Profile mo",
    step1Desc: "Ilagay ang iyong TikTok username at agad naming hahanapin ang profile mo.",
    step2Title: "Pumili ng Services",
    step2Desc: "Pumili mula sa aming promotion packages at i-customize ang order mo.",
    step3Title: "Ligtas na Bayad",
    step3Desc: "Tapusin ang pagbili gamit ang aming encrypted payment system.",
    step4Title: "I-track ang Order mo",
    step4Desc: "I-monitor ang progress ng order mo sa real-time.",
    stat247: "24/7", statSSL: "SSL", statNo: "Wala", statDelivery: "24-72 oras",
    statSupportLabel: "Customer Support",
    statEncryptedLabel: "Encrypted Payments",
    statPasswordLabel: "Password Required",
    statDeliveryLabel: "Oras ng Delivery",
  },
  footer: {
    faqTitle: "Mga Madalas na Tanong",
    faq1Q: "Ligtas ba ang account ko?",
    faq1A: "Oo naman. Hindi kami humihingi ng password o login. Ang public username mo lang.",
    faq2Q: "Gaano katagal ang pagproseso?",
    faq2A: "Agad na pinoproseso ang mga order. Karaniwang lumalabas ang resulta sa loob ng 24-72 oras depende sa service.",
    faq3Q: "Anong payment methods ang tinatanggap?",
    faq3A: "Tumatanggap kami ng credit/debit cards, PayPal, at cryptocurrency. Lahat encrypted.",
    faq4Q: "Ano ang refund policy?",
    faq4A: "Nag-aalok kami ng refund para sa mga hindi natanggap na order. Mag-contact sa hello@tokdadddy.com.",
    privacy: "Privacy", terms: "Terms", refundPolicy: "Refund Policy", contact: "Contact",
    copyright: "tokdadddy.com Reserved ang lahat ng karapatan. Hindi kaakibat ng TikTok o ByteDance.",
    visaMastercard: "Visa / Mastercard", stripeSecure: "Stripe Ligtas", crypto: "Crypto", sslEncrypted: "SSL Encrypted",
  },
  currency: { disclaimer: "Sinisingil sa USD. Ang lokal na presyo ay para lang reference." },
  createOrder: {
    followers: "Followers", likes: "Likes", views: "Views", shares: "Shares", comments: "Comments",
    selectPostsNextStep: "(piliin ang posts sa susunod na step)",
    total: "Kabuuan", discount: "Diskwento", continue: "Magpatuloy",
    contactUs: "Mag-message kung may tanong, nandito kami palagi",
    selected: "napili", posts: "posts",
  },
  order: {
    totalDueToday: "Kabuuang babayaran ngayon", totalDue: "Kabuuang babayaran",
    selectVideos: "Pumili ng videos para i-promote",
    selectVideosDesc: "Piliin ang videos na gusto mong i-boost",
    noVideosFound: "Walang nahanap na videos para sa user na ito.",
    continueWithVideos: "Magpatuloy sa {count} videos",
    selectAtLeastOne: "Pumili ng kahit isang video",
    oneTimeOffer: "One-time Offer 🔥",
    addFollowers: "sa halagang",
    applied: "Naipasa", apply: "I-apply",
    paymentFailed: "Bigo ang bayad",
    paymentDeclined: "Tinanggihan ang bayad mo. Subukan ang ibang methods sa baba.",
    tryCard: "💳 Subukan Card", tryStripe: "💳 Subukan Stripe", tryCrypto: "₿ Subukan Crypto",
    nameOnCard: "Pangalan sa Card", cardNumber: "Numero ng Card", expiry: "Expiry", cvv: "CVV",
    processing: "Pinoproseso...", pay: "Magbayad",
    stripeDesc: "Ligtas na bayad sa pamamagitan ng Stripe Checkout. Sumusuporta sa cards, Apple Pay, Google Pay at iba pa.",
    payViaStripe: "Magbayad via Stripe", redirectingStripe: "Nire-redirect sa Stripe...",
    stripePowered: "🔒 Pinapagana ng Stripe — PCI Level 1",
    cryptoDesc: "Magbayad ng Bitcoin, USDT, ETH at 20+ cryptocurrencies via Cryptomus.",
    payWithCrypto: "Magbayad gamit Crypto", secureCryptomus: "🔒 Ligtas na bayad via Cryptomus",
    agreeTerms: "Sumasang-ayon ka sa aming", terms: "Terms of Service", refundPolicy: "Refund Policy",
    safeCheckout: "Ligtas na checkout", sslEncrypted: "SSL encrypted", guaranteed: "Trackable na delivery",
    backToVideos: "← Bumalik sa pagpili ng videos", videos: "videos", bonus: "Bonus", selected: "napili",
    orderSummary: "Buod ng Order",
    minimumOrder: "Ang pinakamababang order ay $0.50",
    invalidCardNumber: "Maglagay ng valid na card number",
    invalidExpiry: "Invalid na expiry (MM/YY)",
    invalidCvv: "Invalid na CVV",
    cardPaymentFailed: "Bigo ang card payment.",
    cardPaymentDeclined: "Tinanggihan ang card payment.",
    stripeError: "Stripe error",
    stripePaymentFailed: "Bigo ang Stripe payment.",
    paypalFailed: "Bigo ang PayPal.",
    cryptoPaymentFailed: "Bigo ang crypto payment.",
    tryPayPal: "🅿️ Subukan PayPal",
    cardLabel: "Card", stripeLabel: "Stripe", paypalLabel: "PayPal", cryptoLabel: "Crypto",
    stripeDescShort: "Magbayad nang ligtas via Stripe — sumusuporta sa lahat ng major credit at debit cards.",
    paypalDesc: "Magbayad nang ligtas gamit ang iyong PayPal account — mabilis at madaling checkout.",
    payWithStripe: "Magbayad gamit Stripe",
    payWithPayPal: "Magbayad gamit PayPal",
  },
  thankYou: {
    awaitingPayment: "Naghihintay ng Bayad",
    awaitingPaymentDesc: "Nagawa na ang order mo. Naghihintay kami ng kumpirmasyon ng bayad.",
    thankYou: "Salamat!", orderConfirmed: "Nakumpirma na ang order mo at pinoproseso na.",
    referenceNumber: "Reference Number", copy: "Kopyahin", copied: "Nakopya!",
    payment: "Bayad", delivery: "Delivery", services: "Services", total: "Kabuuan",
    whatsNext: "Ano ang susunod?",
    step1: "Sinusuri at pinoproseso ang order mo",
    step2: "Unti-unting lalabas ang resulta sa account mo",
    step3: "Kumpletong delivery sa loob ng 24-72 oras",
    bookmark: "I-bookmark ang page para makita ang status — walang login.",
    placeAnother: "Mag-order ulit",
    pending: "Naghihintay", paid: "Bayad na", completed: "Kumpleto", processingStatus: "Pinoproseso", delivered: "Naipadala", failed: "Bigo",
  },
  dashboard: {
    trackYourOrder: "I-track ang Order mo",
    trackDesc: "Ilagay ang reference number o tingnan ang mga recent orders sa baba.",
    searchPlaceholder: "hal. TD-ABC12345",
    noOrderFound: "Walang nahanap na order sa reference number na iyon.",
    yourOrderHistory: "Iyong Order History", refresh: "I-refresh",
    noHistory: "Wala pang order history. Mag-order at lalabas dito!",
    placeAnother: "Mag-order ulit",
    bookmark: "I-bookmark ang page para makita ang status — walang login.",
    whatsNext: "Ano ang susunod?",
    step1: "Sinusuri at pinoproseso ang order mo",
    step2: "Unti-unting lalabas ang resulta sa account mo",
    step3: "Kumpletong delivery sa loob ng 24-72 oras",
  },
  heroByPlatform: {
    tiktok: { title: "I-promote ang iyong", highlight: "TikTok content", subtitle: "Palawakin ang reach sa TikTok gamit ang marketing services para ma-discover ang videos mo." },
    youtube: { title: "Palaguin ang iyong", highlight: "YouTube channel", subtitle: "I-promote ang videos at channels gamit ang marketing services para palawakin ang audience." },
    spotify: { title: "I-promote ang iyong", highlight: "musika sa Spotify", subtitle: "Promotion services para sa artists at labels para maipakilala ang tracks sa bagong listeners." },
    instagram: { title: "Palakasin ang iyong", highlight: "Instagram presence", subtitle: "I-promote ang profiles, posts at Reels gamit ang marketing services para palawakin ang audience." },
    twitch: { title: "Palaguin ang iyong", highlight: "Twitch streams", subtitle: "I-promote ang channel at streams gamit ang marketing services para maabot ang bagong viewers." },
    twitter: { title: "I-promote ang iyong", highlight: "X profile", subtitle: "Palawakin ang reach sa X gamit ang promotion services para sa tweets, profiles at content campaigns." },
    facebook: { title: "I-boost ang iyong", highlight: "Facebook page", subtitle: "Palaguin ang Facebook pages, posts at videos gamit ang marketing services para sa mas malawak na audience." },
  },
};

export const translations: Record<Locale, Translation> = {
  en, es, zh, ar, fr, pt, ja, de, th, hi, vi, id, tr, ru, ko, it, pl, nl, sv, fil,
};

export const localeNames: Record<Locale, string> = {
  en: "English",
  es: "Español",
  zh: "中文",
  ar: "العربية",
  fr: "Français",
  pt: "Português",
  ja: "日本語",
  ko: "한국어",
  de: "Deutsch",
  th: "ไทย",
  hi: "हिन्दी",
  vi: "Tiếng Việt",
  id: "Bahasa Indonesia",
  tr: "Türkçe",
  ru: "Русский",
  it: "Italiano",
  pl: "Polski",
  nl: "Nederlands",
  sv: "Svenska",
  fil: "Filipino",
};

export const localeFlags: Record<Locale, string> = {
  en: "🇺🇸",
  es: "🇪🇸",
  zh: "🇨🇳",
  ar: "🇸🇦",
  fr: "🇫🇷",
  pt: "🇧🇷",
  ja: "🇯🇵",
  ko: "🇰🇷",
  de: "🇩🇪",
  th: "🇹🇭",
  hi: "🇮🇳",
  vi: "🇻🇳",
  id: "🇮🇩",
  tr: "🇹🇷",
  ru: "🇷🇺",
  it: "🇮🇹",
  pl: "🇵🇱",
  nl: "🇳🇱",
  sv: "🇸🇪",
  fil: "🇵🇭",
};
